import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { motion, AnimatePresence } from 'framer-motion';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate';
import CurrencyInput from 'react-currency-input-field';
import Tabs from '../../../../ui/canon/Tabs';
import ValuationGauge from './ValuationGauge';
import ValuationDonutChart from './ValuationDonutChart';
import ValuationMetricCard from './ValuationMetricCard';
import { DollarSign, PoundSterling, Percent, BarChart3, Save as SaveIcon, X as XIcon, Info as InfoIcon, TrendingUp as TrendingUpIcon, Equal as EqualIcon, RotateCcw as RotateCcwIcon, Target as TargetIcon, AlertCircle as AlertCircleIcon, RefreshCw as RefreshCwIcon, Sparkles as SparklesIcon, Plus as PlusIcon, Minus as MinusIcon, ChevronDown as ChevronDownIcon, ChevronRight as ChevronRightIcon } from 'lucide-react';
import MORDataTable from './MORDataTable';
import MORLineChart from './MORLineChart';
import DCFSummaryCards from './DCFSummaryCards';
import OperatingExpensesBenchmarkChart from './OperatingExpensesBenchmarkChart';
import HistoricalRevenueChart from './HistoricalRevenueChart';
import DCFProjectionsTable from './DCFProjectionsTable';
import DCFGrowthAssumptions from './DCFGrowthAssumptions';
import { Table as CanonTable, Thead as CanonThead, Th as CanonTh, Tr as CanonTr, Td as CanonTd } from '../../../../ui/canon/DataTable';
import Button from '../../../../ui/canon/Button';
import EBITDASummaryCards from './EBITDASummaryCards';
import Drawer from '@material-ui/core/Drawer';
import Modal from '../../../../ui/canon/Modal';
import MultiLineValuationChatWindow from './AgentSystem/MultiLineValuationChatWindow';
import InfoTooltip from '../common/InfoTooltip';
import { ConfidenceIndicator, ValidationCTA, gatherConfidenceFactors, calculateConfidence } from './ConfidenceRating';
import SavedValuationsPopover from './SavedValuationsPopover';
import { getRevenueLineTerm } from './revenueLineTerminology';
import { useCurrency } from '../../hooks/useCurrency';

const PdfIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" strokeWidth="2"/>
    <path d="M14 3v5h5" stroke="currentColor" strokeWidth="2"/>
    <text x="12" y="16" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor">PDF</text>
  </svg>
);

const CsvIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" strokeWidth="2"/>
    <path d="M14 3v5h5" stroke="currentColor" strokeWidth="2"/>
    <text x="12" y="16" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor">CSV</text>
  </svg>
);

const XlsIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" strokeWidth="2"/>
    <path d="M14 3v5h5" stroke="currentColor" strokeWidth="2"/>
    <text x="12" y="16" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor">XLS</text>
  </svg>
);

const TooltipLabel = ({ label, tooltip }) => (
  <span className="inline-flex items-center gap-2">
    <span>{label}</span>
    {tooltip ? <InfoTooltip content={tooltip} /> : null}
  </span>
);

// extracted ValuationGauge imported from ./ValuationGauge

// (Removed unused DonutChart)

// Polymet-inspired Wealth inputs form (pixel-approx implementation)
const WealthInputsForm = ({ values = {}, onFieldChange = () => {}, onChange = () => {}, terminology = {}, showAum = false, showClientCount = false, t12Note = null }) => {
  const intl = useIntl();
  const { format: _currencyFormat } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const toNumber = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const cleaned = String(v).replace(/[$£,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  const toPercentStr = (v) => `${Math.round(Number(v || 0) * 1000) / 10}%`;
  const toCurrencyStr = (v) => fmtCurrency(v).replace(/\.00$/, '');

  const local = {
    faRetention: toPercentStr(values.faRetentionPercent),
    feeBasedRevenue: toCurrencyStr(values.feeBasedRevenue),
    trailerRevenue: toCurrencyStr(values.trailCommissions),
    transactionalRevenue: toCurrencyStr(values.transactionalCommissions),
    directRevenue: toCurrencyStr(values.otherDirectRevenue),
    historicalY1: toCurrencyStr(values?.history?.y1),
    historicalY2: toCurrencyStr(values?.history?.y2),
    historicalY3: toCurrencyStr(values?.history?.y3),
    historicalY4: toCurrencyStr(values?.history?.y4),
    historicalY5: toCurrencyStr(values?.history?.y5),
    growthRate: toPercentStr(values.growth),
  };

  const handle = (fieldId, raw) => {
    onChange && onChange();
    switch (fieldId) {
      case 'faRetention': onFieldChange('faRetentionPercent', toNumber(raw) / 100); break;
      case 'aum': onFieldChange('aum', toNumber(raw)); break;
      case 'clientCount': onFieldChange('clientCount', Math.round(toNumber(raw))); break;
      case 'feeBasedRevenue': onFieldChange('feeBasedRevenue', toNumber(raw)); break;
      case 'trailerRevenue': onFieldChange('trailCommissions', toNumber(raw)); break;
      case 'transactionalRevenue': onFieldChange('transactionalCommissions', toNumber(raw)); break;
      case 'directRevenue': onFieldChange('otherDirectRevenue', toNumber(raw)); break;
      case 'historicalY1': onFieldChange('history.y1', toNumber(raw)); break;
      case 'historicalY2': onFieldChange('history.y2', toNumber(raw)); break;
      case 'historicalY3': onFieldChange('history.y3', toNumber(raw)); break;
      case 'historicalY4': onFieldChange('history.y4', toNumber(raw)); break;
      case 'historicalY5': onFieldChange('history.y5', toNumber(raw)); break;
      case 'growthRate': onFieldChange('growth', toNumber(raw) / 100); break;
      default: break;
    }
  };

  const rowBase = 'space-y-2';
  const labelBase = 'text-sm text-succession-text-secondary';
  const inputBox = 'basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2';

  const SliderRow = ({ id, label, tooltip, type = 'percentage', min = 0, max = 100, step = 'any', value, placeholder }) => {
    const numeric = toNumber(value);
    const isPct = type === 'percentage';
    const initialInputValue = isPct ? `${Number(numeric || 0).toFixed(1)}%` : fmtCurrency(numeric).replace(/\.00$/, '');
    const [val, setVal] = React.useState(numeric);
    const [inputValue, setInputValue] = React.useState(initialInputValue);
    const inputRef = React.useRef(null);
    const isFocusedRef = React.useRef(false);
    React.useEffect(() => {
      if (!isFocusedRef.current) {
        setVal(numeric);
        const isPct = type === 'percentage';
        setInputValue(isPct ? `${Number(numeric || 0).toFixed(1)}%` : fmtCurrency(numeric).replace(/\.00$/, ''));
      }
    }, [numeric, type]);
    const debounceRef = React.useRef(null);
    const onRange = (e) => {
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      if (!isFocusedRef.current) {
        setInputValue(isPct ? `${Number(numVal || 0).toFixed(1)}%` : fmtCurrency(numVal).replace(/\.00$/, ''));
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handle(id, v), 120);
    };
    const percent = (() => {
      const denom = (max - min) || 1;
      const p = ((val - min) / denom) * 100;
      return Math.min(100, Math.max(0, p));
    })();
    const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${percent}%, var(--succession-bg-tertiary, #eee) ${percent}%, var(--succession-bg-tertiary, #eee) 100%)`;
    const onTextFocus = () => {
      isFocusedRef.current = true;
      const isPct = type === 'percentage';
      // For currency, show raw number for easy editing. For percentage, show formatted value.
      if (isPct) {
        setInputValue(`${Number(val || 0).toFixed(1)}%`);
      } else {
        // Show raw numeric value when focused for currency inputs
        setInputValue(String(val || 0));
      }
    };
    const onTextBlur = (e) => {
      isFocusedRef.current = false;
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      const isPct = type === 'percentage';
      setInputValue(isPct ? `${Number(numVal || 0).toFixed(1)}%` : fmtCurrency(numVal).replace(/\.00$/, ''));
      handle(id, v);
    };
    const onTextChange = (e) => {
      const v = e.target.value;
      // Preserve cursor position and selection
      const cursorPosition = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      
      setInputValue(v);
      
      // Restore cursor position after React's state update
      setTimeout(() => {
        if (inputRef.current && isFocusedRef.current) {
          // Check if input still has focus
          const newPosition = Math.min(cursorPosition, v.length);
          const newEnd = Math.min(selectionEnd, v.length);
          if (document.activeElement === inputRef.current) {
            inputRef.current.setSelectionRange(newPosition, newEnd);
          } else {
            // If focus was lost, restore it
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newPosition, newEnd);
          }
        }
      }, 0);
      
      // Don't update val or call handle during typing to prevent re-renders that cause focus loss
      // Only update on blur - see onTextBlur handler
    };
    return (
      <div className={rowBase}>
        <div className={labelBase}><TooltipLabel label={label} tooltip={tooltip} /></div>
        <div className="flex items-center gap-4 w-full">
          <input
            type="range"
            min={min}
            max={max}
            step="any"
            value={val}
            onInput={onRange}
            onChange={onRange}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
            style={{ accentColor: 'var(--succession-primary)', background: trackBg, scrollBehavior: 'smooth' }}
          />
          <input
            ref={inputRef}
            type="text"
            className={inputBox}
            value={inputValue}
            onChange={onTextChange}
            onFocus={onTextFocus}
            onBlur={onTextBlur}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  };

  const TextCurrency = ({ id, label, value }) => (
    <div className={rowBase}>
      <div className={labelBase}>{label}</div>
      <div className="flex items-center gap-4">
        <div className="flex-1" />
        <input type="text" className={inputBox} defaultValue={value} onBlur={(e) => handle(id, e.target.value)} />
      </div>
    </div>
  );

  // Controlled label + text input (no slider) for informational metrics such as AUM and
  // client households. `type="number"` renders a plain integer; otherwise currency.
  const InfoFieldRow = ({ id, label, tooltip, type = 'currency', value, placeholder }) => {
    const numeric = toNumber(value);
    const isCount = type === 'number';
    const fmt = (n) => (isCount ? String(Math.round(Number(n || 0))) : fmtCurrency(n).replace(/\.00$/, ''));
    const [inputValue, setInputValue] = React.useState(fmt(numeric));
    const isFocusedRef = React.useRef(false);
    React.useEffect(() => {
      if (!isFocusedRef.current) setInputValue(fmt(numeric));
    }, [numeric, type]);
    const inputId = `mlv-${id}`;
    return (
      <div className={rowBase}>
        <label htmlFor={inputId} className={labelBase}><TooltipLabel label={label} tooltip={tooltip} /></label>
        <div className="flex items-center gap-4 w-full">
          <div className="flex-1" />
          <input
            id={inputId}
            type="text"
            className={inputBox}
            value={inputValue}
            placeholder={placeholder}
            onFocus={() => { isFocusedRef.current = true; setInputValue(String(numeric || 0)); }}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={(e) => { isFocusedRef.current = false; setInputValue(fmt(toNumber(e.target.value))); handle(id, e.target.value); }}
          />
        </div>
      </div>
    );
  };

  const totalProduction = toNumber(local.feeBasedRevenue) + toNumber(local.trailerRevenue) + toNumber(local.transactionalRevenue) + toNumber(local.directRevenue);
  const currentYear = (typeof window !== 'undefined' ? new Date().getFullYear() : (new Date()).getFullYear());
  const histGrowthPct = (() => {
    const vals = [
      toNumber(local.historicalY1),
      toNumber(local.historicalY2),
      toNumber(local.historicalY3),
      toNumber(local.historicalY4),
      toNumber(local.historicalY5),
    ].filter((v) => Number.isFinite(v) && v > 0);
    // Fewer than two years measures nothing: null, so it renders as unavailable
    // rather than a confident 0% (the server reports the same as growthBasis 'unavailable').
    if (vals.length < 2) return null;
    const growths = [];
    for (let i = 0; i < vals.length - 1; i++) {
      const c = vals[i];
      const n = vals[i + 1];
      if (c > 0 && Number.isFinite(n)) growths.push((c - n) / c);
    }
    if (!growths.length) return null;
    // Divide by vals.length (# of data points) to match backend rj_gross_revenue_detail.rb
    const avg = growths.reduce((s, x) => s + x, 0) / vals.length;
    return Math.round((avg * 100) * 10) / 10;
  })();
  

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.wealth_management' })}</h2>
        <p className="text-sm text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.wealth_form_desc' })}
        </p>
      </div>
      <div className="space-y-10">
        <div>
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.business_info' })}</h3>
          <div className="space-y-6">
            <SliderRow
              id="faRetention"
              label={terminology?.wealth?.fa_retention_percent?.label || intl.formatMessage({ id: 'succession.terminology.wealth.fa_retention_percent' })}
              tooltip={getRevenueLineTerm(terminology, 'wealth', 'fa_retention_percent').definition}
              type="percentage"
              min={0}
              max={100}
              step={1}
              value={local.faRetention}
              placeholder="0%"
            />
            {showAum && (
              <InfoFieldRow
                id="aum"
                label={intl.formatMessage({ id: 'succession.multi_line_valuation.aum_label' })}
                tooltip={intl.formatMessage({ id: 'succession.multi_line_valuation.aum_tooltip' })}
                type="currency"
                value={values.aum}
              />
            )}
            {showClientCount && (
              <InfoFieldRow
                id="clientCount"
                label={intl.formatMessage({ id: 'succession.multi_line_valuation.client_households_label' })}
                tooltip={intl.formatMessage({ id: 'succession.multi_line_valuation.client_households_tooltip' })}
                type="number"
                value={values.clientCount}
              />
            )}
          </div>
        </div>

        <div className="border-t border-succession-border pt-10">
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary flex items-center gap-2">
            {intl.formatMessage({ id: 'succession.multi_line_valuation.revenue_information' })}
            {t12Note ? <InfoTooltip content={t12Note} /> : null}
          </h3>
          <div className="space-y-6">
            <SliderRow id="feeBasedRevenue" label={terminology?.wealth?.fee_based_revenue?.label || intl.formatMessage({ id: 'succession.terminology.wealth.fee_based_revenue' })} tooltip={getRevenueLineTerm(terminology, 'wealth', 'fee_based_revenue').definition} type="currency" min={0} max={5000000} step={50000} value={local.feeBasedRevenue} />
            <SliderRow id="trailerRevenue" label={terminology?.wealth?.trailer_revenue?.label || intl.formatMessage({ id: 'succession.terminology.wealth.trailer_revenue' })} tooltip={getRevenueLineTerm(terminology, 'wealth', 'trailer_revenue').definition} type="currency" min={0} max={5000000} step={50000} value={local.trailerRevenue} />
            <SliderRow id="transactionalRevenue" label={terminology?.wealth?.transactional_revenue?.label || intl.formatMessage({ id: 'succession.terminology.wealth.transactional_revenue' })} tooltip={getRevenueLineTerm(terminology, 'wealth', 'transactional_revenue').definition} type="currency" min={0} max={2000000} step={25000} value={local.transactionalRevenue} />
            <SliderRow id="directRevenue" label={terminology?.wealth?.direct_revenue?.label || intl.formatMessage({ id: 'succession.terminology.wealth.direct_revenue' })} tooltip={getRevenueLineTerm(terminology, 'wealth', 'direct_revenue').definition} type="currency" min={0} max={2000000} step={25000} value={local.directRevenue} />
            <div className="pt-4 border-t-2 border-succession-text-primary/80">
              <div className="flex items-center justify-between">
                <div className="text-base font-bold">{terminology?.wealth?.total_production?.label || intl.formatMessage({ id: 'succession.multi_line_valuation.total_production' })}</div>
                <div className="text-2xl font-bold">{fmtCurrency(totalProduction)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-succession-border pt-10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.historical_revenue' })}</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.use_manual_growth_rate' })}</span>
              <input
                type="checkbox"
                checked={!!values.useManualGrowth}
                onChange={(e) => {
                  onChange && onChange();
                  onFieldChange('useManualGrowth', e.target.checked);
                }}
                className="w-4 h-4 rounded border-succession-border text-succession-primary focus:ring-succession-primary focus:ring-offset-0"
              />
            </label>
          </div>
          {!values.useManualGrowth ? (
            <div className="space-y-6">
              <SliderRow id="historicalY1" label={`${currentYear - 1}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY1} />
              <SliderRow id="historicalY2" label={`${currentYear - 2}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY2} />
              <SliderRow id="historicalY3" label={`${currentYear - 3}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY3} />
              <SliderRow id="historicalY4" label={`${currentYear - 4}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY4} />
              <SliderRow id="historicalY5" label={`${currentYear - 5}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY5} />
              <div className="pt-4 border-t-2 border-succession-text-primary/80">
                <div className="flex items-center justify-between">
                  <div className="text-base font-bold">{intl.formatMessage({ id: 'succession.multi_line_valuation.growth_rate' })}</div>
                  <div className="text-2xl font-bold">{histGrowthPct === null ? '—' : `${histGrowthPct}%`}</div>
                </div>
                {histGrowthPct === null && (
                  <div className="text-xs text-succession-text-secondary mt-1">
                    {intl.formatMessage({ id: 'succession.multi_line_valuation.growth_unavailable_history' })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <SliderRow id="growthRate" label={intl.formatMessage({ id: 'succession.multi_line_valuation.growth_percent' })} type="percentage" min={-10} max={20} step={0.5} value={local.growthRate} placeholder="0%" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Life inputs form (mirrors Wealth layout/slider UX)
const LifeInputsForm = ({ values = {}, onFieldChange = () => {}, onChange = () => {}, terminology = {} }) => {
  const intl = useIntl();
  const { format: _currencyFormat } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const toNumber = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const cleaned = String(v).replace(/[$£,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  const toPercentStr = (v) => `${Math.round(Number(v || 0) * 1000) / 10}%`;
  const toCurrencyStr = (v) => fmtCurrency(v).replace(/\.00$/, '');

  const local = {
    policyCount: toNumber(values.policyCount || 0),
    firstYearCommissions: toCurrencyStr(values.firstYearCommissions),
    renewalCommissions: toCurrencyStr(values.renewalCommissions),
    overrideCommissions: toCurrencyStr(values.overrideCommissions),
    otherRevenue: toCurrencyStr(values.otherRevenue),
    historicalY1: toCurrencyStr(values?.history?.y1),
    historicalY2: toCurrencyStr(values?.history?.y2),
    historicalY3: toCurrencyStr(values?.history?.y3),
    historicalY4: toCurrencyStr(values?.history?.y4),
    historicalY5: toCurrencyStr(values?.history?.y5),
    growthRate: toPercentStr(values.growth),
  };

  const handle = (fieldId, raw) => {
    onChange && onChange();
    switch (fieldId) {
      case 'policyCount': onFieldChange('policyCount', toNumber(raw)); break;
      case 'firstYearCommissions': onFieldChange('firstYearCommissions', toNumber(raw)); break;
      case 'renewalCommissions': onFieldChange('renewalCommissions', toNumber(raw)); break;
      case 'overrideCommissions': onFieldChange('overrideCommissions', toNumber(raw)); break;
      case 'otherRevenue': onFieldChange('otherRevenue', toNumber(raw)); break;
      case 'historicalY1': onFieldChange('history.y1', toNumber(raw)); break;
      case 'historicalY2': onFieldChange('history.y2', toNumber(raw)); break;
      case 'historicalY3': onFieldChange('history.y3', toNumber(raw)); break;
      case 'historicalY4': onFieldChange('history.y4', toNumber(raw)); break;
      case 'historicalY5': onFieldChange('history.y5', toNumber(raw)); break;
      case 'growthRate': onFieldChange('growth', toNumber(raw) / 100); break;
      default: break;
    }
  };

  const rowBase = 'space-y-2';
  const labelBase = 'text-sm text-succession-text-secondary';
  const inputBox = 'basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2';

  const SliderRow = ({ id, label, tooltip, type = 'percentage', min = 0, max = 100, step = 'any', value, placeholder }) => {
    const numeric = toNumber(value);
    const isPct = type === 'percentage';
    const isCurrency = type === 'currency';
    const isInteger = type === 'integer' || type === 'number';
    const getInitialInputValue = () => {
      if (isPct) {
        return `${Number(numeric || 0).toFixed(1)}%`;
      } else if (isInteger) {
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(numeric || 0));
      } else {
        return fmtCurrency(numeric).replace(/\.00$/, '');
      }
    };
    const [val, setVal] = React.useState(numeric);
    const [inputValue, setInputValue] = React.useState(getInitialInputValue());
    const inputRef = React.useRef(null);
    const isFocusedRef = React.useRef(false);
    React.useEffect(() => {
      if (!isFocusedRef.current) {
        setVal(numeric);
        setInputValue(getInitialInputValue());
      }
    }, [numeric, type]);
    const rangeStep = isCurrency ? 50000 : step;
    const debounceRef = React.useRef(null);
    const onRange = (e) => {
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      if (!isFocusedRef.current) {
        if (isPct) {
          setInputValue(`${Number(numVal || 0).toFixed(1)}%`);
        } else if (isInteger) {
          setInputValue(new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(numVal || 0)));
        } else {
          setInputValue(fmtCurrency(numVal).replace(/\.00$/, ''));
        }
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handle(id, v), 120);
    };
    const percent = (() => {
      const denom = (max - min) || 1;
      const p = ((val - min) / denom) * 100;
      return Math.min(100, Math.max(0, p));
    })();
    const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${percent}%, var(--succession-bg-tertiary, #eee) ${percent}%, var(--succession-bg-tertiary, #eee) 100%)`;
    const onTextFocus = () => {
      isFocusedRef.current = true;
      if (isPct) {
        setInputValue(`${Number(val || 0).toFixed(1)}%`);
      } else {
        // For currency and integer inputs, show raw number for easy editing
        setInputValue(String(val || 0));
      }
    };
    const onTextBlur = (e) => {
      isFocusedRef.current = false;
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      if (isPct) {
        setInputValue(`${Number(numVal || 0).toFixed(1)}%`);
      } else if (isInteger) {
        setInputValue(new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(numVal || 0)));
      } else {
        setInputValue(fmtCurrency(numVal).replace(/\.00$/, ''));
      }
      handle(id, v);
    };
    const onTextChange = (e) => {
      const v = e.target.value;
      // Preserve cursor position and selection
      const cursorPosition = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      
      setInputValue(v);
      
      // Restore cursor position after React's state update
      setTimeout(() => {
        if (inputRef.current && isFocusedRef.current) {
          // Check if input still has focus
          const newPosition = Math.min(cursorPosition, v.length);
          const newEnd = Math.min(selectionEnd, v.length);
          if (document.activeElement === inputRef.current) {
            inputRef.current.setSelectionRange(newPosition, newEnd);
          } else {
            // If focus was lost, restore it
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newPosition, newEnd);
          }
        }
      }, 0);
      
      // Don't update val or call handle during typing to prevent re-renders that cause focus loss
      // Only update on blur - see onTextBlur handler
    };
    return (
      <div className={rowBase}>
        <div className={labelBase}><TooltipLabel label={label} tooltip={tooltip} /></div>
        <div className="flex items-center gap-4 w-full">
          <input
            type="range"
            min={min}
            max={max}
            step={rangeStep}
            value={val}
            onInput={onRange}
            onChange={onRange}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
            style={{ accentColor: 'var(--succession-primary)', background: trackBg, scrollBehavior: 'smooth' }}
          />
          <input
            ref={inputRef}
            type="text"
            className={inputBox}
            value={inputValue}
            onChange={onTextChange}
            onFocus={onTextFocus}
            onBlur={onTextBlur}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  };

  const currentYear = (typeof window !== 'undefined' ? new Date().getFullYear() : (new Date()).getFullYear());
  const totalProduction = toNumber(local.firstYearCommissions) + toNumber(local.renewalCommissions) + toNumber(local.overrideCommissions) + toNumber(local.otherRevenue);
  const histGrowthPct = (() => {
    const vals = [toNumber(local.historicalY1), toNumber(local.historicalY2), toNumber(local.historicalY3), toNumber(local.historicalY4), toNumber(local.historicalY5)].filter((v) => Number.isFinite(v) && v > 0);
    if (vals.length < 2) return 0;
    const growths = [];
    for (let i = 0; i < vals.length - 1; i++) {
      const c = vals[i];
      const n = vals[i + 1];
      if (c > 0 && Number.isFinite(n)) growths.push((c - n) / c);
    }
    if (!growths.length) return 0;
    // Divide by vals.length (# of data points) to match backend rj_gross_revenue_detail.rb
    const avg = growths.reduce((s, x) => s + x, 0) / vals.length;
    return Math.round((avg * 100) * 10) / 10;
  })();

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.life_insurance' })}</h2>
        <p className="text-sm text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.life_form_desc' })}
        </p>
      </div>
      <div className="space-y-10">
        <div>
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.business_info' })}</h3>
          <div className="space-y-6">
            <SliderRow id="policyCount" label={intl.formatMessage({ id: 'succession.multi_line_valuation.policy_count' })} type="number" min={0} max={3000} step={1} value={local.policyCount} />
          </div>
        </div>

        <div className="border-t border-succession-border pt-10">
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.revenue_information' })}</h3>
          <div className="space-y-6">
            <SliderRow id="firstYearCommissions" label={terminology?.life?.first_year_commissions?.label || intl.formatMessage({ id: 'succession.terminology.life.first_year_commissions' })} tooltip={getRevenueLineTerm(terminology, 'life', 'first_year_commissions').definition} type="currency" min={0} max={5000000} step={50000} value={local.firstYearCommissions} />
            <SliderRow id="renewalCommissions" label={terminology?.life?.renewal_commissions?.label || intl.formatMessage({ id: 'succession.terminology.life.renewal_commissions' })} tooltip={getRevenueLineTerm(terminology, 'life', 'renewal_commissions').definition} type="currency" min={0} max={5000000} step={50000} value={local.renewalCommissions} />
            <SliderRow id="overrideCommissions" label={terminology?.life?.override_commissions?.label || intl.formatMessage({ id: 'succession.terminology.life.override_commissions' })} tooltip={getRevenueLineTerm(terminology, 'life', 'override_commissions').definition} type="currency" min={0} max={2000000} step={25000} value={local.overrideCommissions} />
            <SliderRow id="otherRevenue" label={terminology?.life?.other_revenues?.label || intl.formatMessage({ id: 'succession.terminology.life.other_revenues' })} tooltip={getRevenueLineTerm(terminology, 'life', 'other_revenues').definition} type="currency" min={0} max={2000000} step={25000} value={local.otherRevenue} />
            <div className="pt-4 border-t-2 border-succession-text-primary/80">
              <div className="flex items-center justify-between">
                <div className="text-base font-bold">{terminology?.life?.total_production?.label || intl.formatMessage({ id: 'succession.multi_line_valuation.total_production' })}</div>
                <div className="text-2xl font-bold">{fmtCurrency(totalProduction)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-succession-border pt-10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.historical_revenue' })}</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.use_manual_growth_rate' })}</span>
              <input
                type="checkbox"
                checked={!!values.useManualGrowth}
                onChange={(e) => {
                  onChange && onChange();
                  onFieldChange('useManualGrowth', e.target.checked);
                }}
                className="w-4 h-4 rounded border-succession-border text-succession-primary focus:ring-succession-primary focus:ring-offset-0"
              />
            </label>
          </div>
          {!values.useManualGrowth ? (
            <div className="space-y-6">
              <SliderRow id="historicalY1" label={`${currentYear - 1}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY1} />
              <SliderRow id="historicalY2" label={`${currentYear - 2}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY2} />
              <SliderRow id="historicalY3" label={`${currentYear - 3}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY3} />
              <SliderRow id="historicalY4" label={`${currentYear - 4}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY4} />
              <SliderRow id="historicalY5" label={`${currentYear - 5}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY5} />
              <div className="pt-4 border-t-2 border-succession-text-primary/80">
                <div className="flex items-center justify-between">
                  <div className="text-base font-bold">{intl.formatMessage({ id: 'succession.multi_line_valuation.growth_rate' })}</div>
                  <div className="text-2xl font-bold">{`${histGrowthPct}%`}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <SliderRow id="growthRate" label={intl.formatMessage({ id: 'succession.multi_line_valuation.growth_percent' })} type="percentage" min={-10} max={20} step={0.5} value={local.growthRate} placeholder="0%" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// P&C inputs form (mirrors Wealth layout/slider UX)
const PCInputsForm = ({ values = {}, onFieldChange = () => {}, onChange = () => {}, terminology = {} }) => {
  const intl = useIntl();
  const { format: _currencyFormat } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const toNumber = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const cleaned = String(v).replace(/[$£,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  const toPercentStr = (v) => `${Math.round(Number(v || 0) * 1000) / 10}%`;
  const toCurrencyStr = (v) => fmtCurrency(v).replace(/\.00$/, '');

  const local = {
    personalPremium: toCurrencyStr(values.personalPremium),
    personalCommRate: toPercentStr(values.personalCommRate),
    commercialPremium: toCurrencyStr(values.commercialPremium),
    commercialCommRate: toPercentStr(values.commercialCommRate),
    retentionPersonal: toPercentStr(values.retentionPersonal),
    retentionCommercial: toPercentStr(values.retentionCommercial),
    historicalY1: toCurrencyStr(values?.history?.y1),
    historicalY2: toCurrencyStr(values?.history?.y2),
    historicalY3: toCurrencyStr(values?.history?.y3),
    historicalY4: toCurrencyStr(values?.history?.y4),
    historicalY5: toCurrencyStr(values?.history?.y5),
    growthRate: toPercentStr(values.growth),
  };

  const handle = (fieldId, raw) => {
    onChange && onChange();
    switch (fieldId) {
      case 'personalPremium': onFieldChange('personalPremium', toNumber(raw)); break;
      case 'personalCommRate': onFieldChange('personalCommRate', toNumber(raw) / 100); break;
      case 'commercialPremium': onFieldChange('commercialPremium', toNumber(raw)); break;
      case 'commercialCommRate': onFieldChange('commercialCommRate', toNumber(raw) / 100); break;
      case 'retentionPersonal': onFieldChange('retentionPersonal', toNumber(raw) / 100); break;
      case 'retentionCommercial': onFieldChange('retentionCommercial', toNumber(raw) / 100); break;
      case 'historicalY1': onFieldChange('history.y1', toNumber(raw)); break;
      case 'historicalY2': onFieldChange('history.y2', toNumber(raw)); break;
      case 'historicalY3': onFieldChange('history.y3', toNumber(raw)); break;
      case 'historicalY4': onFieldChange('history.y4', toNumber(raw)); break;
      case 'historicalY5': onFieldChange('history.y5', toNumber(raw)); break;
      case 'growthRate': onFieldChange('growth', toNumber(raw) / 100); break;
      default: break;
    }
  };

  const rowBase = 'space-y-2';
  const labelBase = 'text-sm text-succession-text-secondary';
  const inputBox = 'basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2';

  const SliderRow = ({ id, label, tooltip, type = 'percentage', min = 0, max = 100, step = 'any', value, placeholder }) => {
    const numeric = toNumber(value);
    const isPct = type === 'percentage';
    const isCurrency = type === 'currency';
    const isInteger = type === 'integer' || type === 'number';
    const getInitialInputValue = () => {
      if (isPct) {
        return `${Number(numeric || 0).toFixed(1)}%`;
      } else if (isInteger) {
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(numeric || 0));
      } else {
        return fmtCurrency(numeric).replace(/\.00$/, '');
      }
    };
    const [val, setVal] = React.useState(numeric);
    const [inputValue, setInputValue] = React.useState(getInitialInputValue());
    const inputRef = React.useRef(null);
    const isFocusedRef = React.useRef(false);
    React.useEffect(() => {
      if (!isFocusedRef.current) {
        setVal(numeric);
        setInputValue(getInitialInputValue());
      }
    }, [numeric, type]);
    const rangeStep = isCurrency ? 50000 : step;
    const debounceRef = React.useRef(null);
    const onRange = (e) => {
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      if (!isFocusedRef.current) {
        if (isPct) {
          setInputValue(`${Number(numVal || 0).toFixed(1)}%`);
        } else if (isInteger) {
          setInputValue(new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(numVal || 0)));
        } else {
          setInputValue(fmtCurrency(numVal).replace(/\.00$/, ''));
        }
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handle(id, v), 120);
    };
    const percent = (() => {
      const denom = (max - min) || 1;
      const p = ((val - min) / denom) * 100;
      return Math.min(100, Math.max(0, p));
    })();
    const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${percent}%, var(--succession-bg-tertiary, #eee) ${percent}%, var(--succession-bg-tertiary, #eee) 100%)`;
    const onTextFocus = () => {
      isFocusedRef.current = true;
      if (isPct) {
        setInputValue(`${Number(val || 0).toFixed(1)}%`);
      } else {
        // For currency and integer inputs, show raw number for easy editing
        setInputValue(String(val || 0));
      }
    };
    const onTextBlur = (e) => {
      isFocusedRef.current = false;
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      if (isPct) {
        setInputValue(`${Number(numVal || 0).toFixed(1)}%`);
      } else if (isInteger) {
        setInputValue(new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(numVal || 0)));
      } else {
        setInputValue(fmtCurrency(numVal).replace(/\.00$/, ''));
      }
      handle(id, v);
    };
    const onTextChange = (e) => {
      const v = e.target.value;
      // Preserve cursor position and selection
      const cursorPosition = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      
      setInputValue(v);
      
      // Restore cursor position after React's state update
      setTimeout(() => {
        if (inputRef.current && isFocusedRef.current) {
          // Check if input still has focus
          const newPosition = Math.min(cursorPosition, v.length);
          const newEnd = Math.min(selectionEnd, v.length);
          if (document.activeElement === inputRef.current) {
            inputRef.current.setSelectionRange(newPosition, newEnd);
          } else {
            // If focus was lost, restore it
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newPosition, newEnd);
          }
        }
      }, 0);
      
      // Don't update val or call handle during typing to prevent re-renders that cause focus loss
      // Only update on blur - see onTextBlur handler
    };
    return (
      <div className={rowBase}>
        <div className={labelBase}><TooltipLabel label={label} tooltip={tooltip} /></div>
        <div className="flex items-center gap-4 w-full">
          <input
            type="range"
            min={min}
            max={max}
            step={rangeStep}
            value={val}
            onInput={onRange}
            onChange={onRange}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
            style={{ accentColor: 'var(--succession-primary)', background: trackBg, scrollBehavior: 'smooth' }}
          />
          <input
            ref={inputRef}
            type="text"
            className={inputBox}
            value={inputValue}
            onChange={onTextChange}
            onFocus={onTextFocus}
            onBlur={onTextBlur}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  };

  const currentYear = (typeof window !== 'undefined' ? new Date().getFullYear() : (new Date()).getFullYear());
  const histGrowthPct = (() => {
    const vals = [
      toNumber(local.historicalY1),
      toNumber(local.historicalY2),
      toNumber(local.historicalY3),
      toNumber(local.historicalY4),
      toNumber(local.historicalY5),
    ].filter((v) => Number.isFinite(v) && v > 0);
    if (vals.length < 2) return 0;
    const growths = [];
    for (let i = 0; i < vals.length - 1; i++) {
      const c = vals[i];
      const n = vals[i + 1];
      if (c > 0 && Number.isFinite(n)) growths.push((c - n) / c);
    }
    if (!growths.length) return 0;
    // Divide by vals.length (# of data points) to match backend rj_gross_revenue_detail.rb
    const avg = growths.reduce((s, x) => s + x, 0) / vals.length;
    return Math.round((avg * 100) * 10) / 10;
  })();

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.property_and_casualty' })}</h2>
        <p className="text-sm text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.pc_form_desc' })}
        </p>
      </div>
      <div className="space-y-10">
        <div>
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.revenue_information' })}</h3>
          <div className="space-y-6">
            <SliderRow id="personalPremium" label={`${terminology?.pc?.premium?.label || intl.formatMessage({ id: 'succession.terminology.pc.premium' })} ${intl.formatMessage({ id: 'succession.multi_line_valuation.personal' })}`} tooltip={getRevenueLineTerm(terminology, 'pc', 'premium').definition} type="currency" min={0} max={5000000} step={50000} value={local.personalPremium} />
            <SliderRow id="personalCommRate" label={`${terminology?.pc?.commission_rate?.label || intl.formatMessage({ id: 'succession.terminology.pc.commission_rate' })} ${intl.formatMessage({ id: 'succession.multi_line_valuation.personal' })} %`} tooltip={getRevenueLineTerm(terminology, 'pc', 'commission_rate').definition} type="percentage" min={0} max={100} step={0.1} value={local.personalCommRate} />
            <SliderRow id="commercialPremium" label={`${terminology?.pc?.premium?.label || intl.formatMessage({ id: 'succession.terminology.pc.premium' })} ${intl.formatMessage({ id: 'succession.multi_line_valuation.commercial' })}`} tooltip={getRevenueLineTerm(terminology, 'pc', 'premium').definition} type="currency" min={0} max={5000000} step={50000} value={local.commercialPremium} />
            <SliderRow id="commercialCommRate" label={`${terminology?.pc?.commission_rate?.label || intl.formatMessage({ id: 'succession.terminology.pc.commission_rate' })} ${intl.formatMessage({ id: 'succession.multi_line_valuation.commercial' })} %`} tooltip={getRevenueLineTerm(terminology, 'pc', 'commission_rate').definition} type="percentage" min={0} max={100} step={0.1} value={local.commercialCommRate} />
            <SliderRow id="retentionPersonal" label={`${terminology?.pc?.retention_rate?.label || intl.formatMessage({ id: 'succession.terminology.pc.retention_rate' })} ${intl.formatMessage({ id: 'succession.multi_line_valuation.personal' })} %`} tooltip={getRevenueLineTerm(terminology, 'pc', 'retention_rate').definition} type="percentage" min={0} max={100} step={0.1} value={local.retentionPersonal} />
            <SliderRow id="retentionCommercial" label={`${terminology?.pc?.retention_rate?.label || intl.formatMessage({ id: 'succession.terminology.pc.retention_rate' })} ${intl.formatMessage({ id: 'succession.multi_line_valuation.commercial' })} %`} tooltip={getRevenueLineTerm(terminology, 'pc', 'retention_rate').definition} type="percentage" min={0} max={100} step={0.1} value={local.retentionCommercial} />
            {(() => {
              const personalPremium = toNumber(local.personalPremium);
              const personalRate = toNumber(local.personalCommRate) / 100;
              const commercialPremium = toNumber(local.commercialPremium);
              const commercialRate = toNumber(local.commercialCommRate) / 100;
              const total = (personalPremium * personalRate) + (commercialPremium * commercialRate);
              return (
                <div className="pt-4 border-t-2 border-succession-text-primary/80">
                  <div className="flex items-center justify-between">
                    <div className="text-base font-bold">{terminology?.pc?.total_production?.label || intl.formatMessage({ id: 'succession.multi_line_valuation.total_production' })}</div>
                    <div className="text-2xl font-bold">{fmtCurrency(total)}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="border-t border-succession-border pt-10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.historical_revenue' })}</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.use_manual_growth_rate' })}</span>
              <input
                type="checkbox"
                checked={!!values.useManualGrowth}
                onChange={(e) => {
                  onChange && onChange();
                  onFieldChange('useManualGrowth', e.target.checked);
                }}
                className="w-4 h-4 rounded border-succession-border text-succession-primary focus:ring-succession-primary focus:ring-offset-0"
              />
            </label>
          </div>
          {!values.useManualGrowth ? (
            <div className="space-y-6">
              <SliderRow id="historicalY1" label={`${currentYear - 1}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY1} />
              <SliderRow id="historicalY2" label={`${currentYear - 2}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY2} />
              <SliderRow id="historicalY3" label={`${currentYear - 3}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY3} />
              <SliderRow id="historicalY4" label={`${currentYear - 4}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY4} />
              <SliderRow id="historicalY5" label={`${currentYear - 5}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY5} />
              <div className="pt-4 border-t-2 border-succession-text-primary/80">
                <div className="flex items-center justify-between">
                  <div className="text-base font-bold">{intl.formatMessage({ id: 'succession.multi_line_valuation.growth_rate' })}</div>
                  <div className="text-2xl font-bold">{`${histGrowthPct}%`}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <SliderRow id="growthRate" label={intl.formatMessage({ id: 'succession.multi_line_valuation.growth_percent' })} type="percentage" min={-10} max={20} step={0.5} value={local.growthRate} placeholder="0%" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Group inputs form (mirrors Wealth layout/slider UX)
const GroupInputsForm = ({ values = {}, onFieldChange = () => {}, onChange = () => {}, terminology = {} }) => {
  const intl = useIntl();
  const { format: _currencyFormat } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const toNumber = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const cleaned = String(v).replace(/[$£,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  const toPercentStr = (v) => `${Math.round(Number(v || 0) * 1000) / 10}%`;
  const toCurrencyStr = (v) => fmtCurrency(v).replace(/\.00$/, '');

  const local = {
    numberOfCases: toNumber(values.numberOfCases || 0),
    avgCaseSize: toCurrencyStr(values.avgCaseSize),
    retentionRate: toPercentStr(values.retentionRate),
    firstYearCommissions: toCurrencyStr(values.firstYearCommissions),
    renewalCommissions: toCurrencyStr(values.renewalCommissions),
    overrideCommissions: toCurrencyStr(values.overrideCommissions),
    otherRevenue: toCurrencyStr(values.otherRevenue),
    historicalY1: toCurrencyStr(values?.history?.y1),
    historicalY2: toCurrencyStr(values?.history?.y2),
    historicalY3: toCurrencyStr(values?.history?.y3),
    historicalY4: toCurrencyStr(values?.history?.y4),
    historicalY5: toCurrencyStr(values?.history?.y5),
    growthRate: toPercentStr(values.growth),
  };

  const handle = (fieldId, raw) => {
    onChange && onChange();
    switch (fieldId) {
      case 'numberOfCases': onFieldChange('numberOfCases', toNumber(raw)); break;
      case 'avgCaseSize': onFieldChange('avgCaseSize', toNumber(raw)); break;
      case 'retentionRate': onFieldChange('retentionRate', toNumber(raw) / 100); break;
      case 'firstYearCommissions': onFieldChange('firstYearCommissions', toNumber(raw)); break;
      case 'renewalCommissions': onFieldChange('renewalCommissions', toNumber(raw)); break;
      case 'overrideCommissions': onFieldChange('overrideCommissions', toNumber(raw)); break;
      case 'otherRevenue': onFieldChange('otherRevenue', toNumber(raw)); break;
      case 'historicalY1': onFieldChange('history.y1', toNumber(raw)); break;
      case 'historicalY2': onFieldChange('history.y2', toNumber(raw)); break;
      case 'historicalY3': onFieldChange('history.y3', toNumber(raw)); break;
      case 'historicalY4': onFieldChange('history.y4', toNumber(raw)); break;
      case 'historicalY5': onFieldChange('history.y5', toNumber(raw)); break;
      case 'growthRate': onFieldChange('growth', toNumber(raw) / 100); break;
      default: break;
    }
  };

  const rowBase = 'space-y-2';
  const labelBase = 'text-sm text-succession-text-secondary';
  const inputBox = 'basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2';

  const SliderRow = ({ id, label, tooltip, type = 'percentage', min = 0, max = 100, step = 'any', value, placeholder }) => {
    const numeric = toNumber(value);
    const [val, setVal] = React.useState(numeric);
    React.useEffect(() => { setVal(numeric); }, [numeric]);
    const isPct = type === 'percentage';
    const isCurrency = type === 'currency';
    const isInteger = type === 'integer' || type === 'number';
    const rangeStep = isCurrency ? 50000 : step;
    const debounceRef = React.useRef(null);
    const onRange = (e) => {
      const v = e.target.value;
      setVal(toNumber(v));
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handle(id, v), 120);
    };
    const percent = (() => {
      const denom = (max - min) || 1;
      const p = ((val - min) / denom) * 100;
      return Math.min(100, Math.max(0, p));
    })();
    const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${percent}%, var(--succession-bg-tertiary, #eee) ${percent}%, var(--succession-bg-tertiary, #eee) 100%)`;
    const onText = (e) => { const v = e.target.value; setVal(toNumber(v)); handle(id, v); };
    const onTextChange = (e) => {
      const v = e.target.value;
      setVal(toNumber(v));
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => handle(id, v), 120);
    };
    return (
      <div className={rowBase}>
        <div className={labelBase}><TooltipLabel label={label} tooltip={tooltip} /></div>
        <div className="flex items-center gap-4 w-full">
          <input
            type="range"
            min={min}
            max={max}
            step={rangeStep}
            value={val}
            onInput={onRange}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
            style={{ accentColor: 'var(--succession-primary)', background: trackBg, scrollBehavior: 'smooth' }}
          />
          <input
            type="text"
            className={inputBox}
            value={
              isPct
                ? `${Number(val || 0).toFixed(1)}%`
                : (isInteger
                    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(val || 0))
                    : fmtCurrency(val).replace(/\.00$/, '')
                  )
            }
            onChange={onTextChange}
            onBlur={onText}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  };

  const currentYear = (typeof window !== 'undefined' ? new Date().getFullYear() : (new Date()).getFullYear());
  const histGrowthPct = (() => {
    const vals = [
      toNumber(local.historicalY1),
      toNumber(local.historicalY2),
      toNumber(local.historicalY3),
      toNumber(local.historicalY4),
      toNumber(local.historicalY5),
    ].filter((v) => Number.isFinite(v) && v > 0);
    if (vals.length < 2) return 0;
    const growths = [];
    for (let i = 0; i < vals.length - 1; i++) {
      const c = vals[i];
      const n = vals[i + 1];
      if (c > 0 && Number.isFinite(n)) growths.push((c - n) / c);
    }
    if (!growths.length) return 0;
    // Divide by vals.length (# of data points) to match backend rj_gross_revenue_detail.rb
    const avg = growths.reduce((s, x) => s + x, 0) / vals.length;
    return Math.round((avg * 100) * 10) / 10;
  })();
  const totalProduction = toNumber(local.firstYearCommissions) + toNumber(local.renewalCommissions) + toNumber(local.overrideCommissions) + toNumber(local.otherRevenue);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.group_benefits' })}</h2>
        <p className="text-sm text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.group_benefits_desc' })}
        </p>
      </div>
      <div className="space-y-10">
        <div>
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.business_info' })}</h3>
          <div className="space-y-6">
            <SliderRow id="numberOfCases" label={intl.formatMessage({ id: 'succession.multi_line_valuation.number_of_cases' })} type="number" min={0} max={3000} step={1} value={local.numberOfCases} />
            <SliderRow id="avgCaseSize" label={intl.formatMessage({ id: 'succession.multi_line_valuation.average_case_size' })} type="currency" min={0} max={5000000} step={50000} value={local.avgCaseSize} />
            <SliderRow id="retentionRate" label={intl.formatMessage({ id: 'succession.multi_line_valuation.retention_rate_percent' })} type="percentage" min={0} max={100} step={0.1} value={local.retentionRate} />
          </div>
        </div>

        <div className="border-t border-succession-border pt-10">
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.revenue_information' })}</h3>
          <div className="space-y-6">
            <SliderRow id="firstYearCommissions" label={terminology?.group?.first_year_commissions?.label || intl.formatMessage({ id: 'succession.terminology.group.first_year_commissions' })} tooltip={getRevenueLineTerm(terminology, 'group', 'first_year_commissions').definition} type="currency" min={0} max={5000000} step={50000} value={local.firstYearCommissions} />
            <SliderRow id="renewalCommissions" label={terminology?.group?.renewal_commissions?.label || intl.formatMessage({ id: 'succession.terminology.group.renewal_commissions' })} tooltip={getRevenueLineTerm(terminology, 'group', 'renewal_commissions').definition} type="currency" min={0} max={5000000} step={50000} value={local.renewalCommissions} />
            <SliderRow id="overrideCommissions" label={terminology?.group?.override_commissions?.label || intl.formatMessage({ id: 'succession.terminology.group.override_commissions' })} tooltip={getRevenueLineTerm(terminology, 'group', 'override_commissions').definition} type="currency" min={0} max={2000000} step={25000} value={local.overrideCommissions} />
            <SliderRow id="otherRevenue" label={terminology?.group?.other_revenues?.label || intl.formatMessage({ id: 'succession.terminology.group.other_revenues' })} tooltip={getRevenueLineTerm(terminology, 'group', 'other_revenues').definition} type="currency" min={0} max={2000000} step={25000} value={local.otherRevenue} />
            <div className="pt-4 border-t-2 border-succession-text-primary/80">
              <div className="flex items-center justify-between">
                <div className="text-base font-bold">{terminology?.group?.total_production?.label || intl.formatMessage({ id: 'succession.multi_line_valuation.total_production' })}</div>
                <div className="text-2xl font-bold">{fmtCurrency(totalProduction)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-succession-border pt-10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.historical_revenue' })}</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.use_manual_growth_rate' })}</span>
              <input
                type="checkbox"
                checked={!!values.useManualGrowth}
                onChange={(e) => {
                  onChange && onChange();
                  onFieldChange('useManualGrowth', e.target.checked);
                }}
                className="w-4 h-4 rounded border-succession-border text-succession-primary focus:ring-succession-primary focus:ring-offset-0"
              />
            </label>
          </div>
          {!values.useManualGrowth ? (
            <div className="space-y-6">
              <SliderRow id="historicalY1" label={`${currentYear - 1}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY1} />
              <SliderRow id="historicalY2" label={`${currentYear - 2}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY2} />
              <SliderRow id="historicalY3" label={`${currentYear - 3}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY3} />
              <SliderRow id="historicalY4" label={`${currentYear - 4}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY4} />
              <SliderRow id="historicalY5" label={`${currentYear - 5}`} type="currency" min={0} max={5000000} step={50000} value={local.historicalY5} />
              <div className="pt-4 border-t-2 border-succession-text-primary/80">
                <div className="flex items-center justify-between">
                  <div className="text-base font-bold">{intl.formatMessage({ id: 'succession.multi_line_valuation.growth_rate' })}</div>
                  <div className="text-2xl font-bold">{`${histGrowthPct}%`}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <SliderRow id="growthRate" label={intl.formatMessage({ id: 'succession.multi_line_valuation.growth_percent' })} type="percentage" min={-10} max={20} step={0.5} value={local.growthRate} placeholder="0%" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Expense Allocation Row Component
const ExpenseAllocationRow = ({ line, lineLabel, percent, allocatedAmount, revenueShare, disabled = false, onChange, className = "" }) => {
  const { format: _currencyFormat } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const toNumber = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const cleaned = String(v).replace(/[$£,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  const numeric = Number(percent || 0);
  const [val, setVal] = React.useState(numeric);
  const [inputValue, setInputValue] = React.useState(() => `${Number(numeric || 0).toFixed(2)}%`);
  const inputRef = React.useRef(null);
  const isFocusedRef = React.useRef(false);
  
  React.useEffect(() => {
    if (!isFocusedRef.current) {
      setVal(numeric);
      setInputValue(`${Number(numeric || 0).toFixed(2)}%`);
    }
  }, [numeric]);

  const percentFill = Math.min(100, Math.max(0, ((val - 0) / (100 - 0)) * 100));
  const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${percentFill}%, var(--succession-bg-tertiary, #eee) ${percentFill}%, var(--succession-bg-tertiary, #eee) 100%)`;
  
  const debounceRef = React.useRef(null);
  const onRange = (e) => {
    const v = e.target.value;
    const numVal = toNumber(v);
    setVal(numVal);
    if (!isFocusedRef.current) {
      setInputValue(`${Number(numVal || 0).toFixed(2)}%`);
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!disabled && onChange) onChange(numVal);
    }, 120);
  };

  const onTextFocus = () => {
    isFocusedRef.current = true;
    setInputValue(String(val || 0));
  };

  const onTextBlur = (e) => {
    isFocusedRef.current = false;
    const v = e.target.value;
    const numValue = parseFloat(v.replace(/[^0-9.]/g, ''));
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      const clamped = Math.min(100, Math.max(0, numValue));
      setVal(clamped);
      setInputValue(`${Number(clamped || 0).toFixed(2)}%`);
      if (!disabled && onChange) onChange(clamped);
    } else if (v === '' || v === '0') {
      setVal(0);
      setInputValue('0.00%');
      if (!disabled && onChange) onChange(0);
    } else {
      setInputValue(`${Number(val || 0).toFixed(2)}%`);
    }
  };

  const onTextChange = (e) => {
    const v = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;
    
    setInputValue(v);
    
    setTimeout(() => {
      if (inputRef.current && isFocusedRef.current) {
        const newPosition = Math.min(cursorPosition, v.length);
        const newEnd = Math.min(selectionEnd, v.length);
        if (document.activeElement === inputRef.current) {
          inputRef.current.setSelectionRange(newPosition, newEnd);
        } else {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newPosition, newEnd);
        }
      }
    }, 0);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <label htmlFor={`allocation-${line}`} className="text-sm font-medium text-succession-text-primary">
          {lineLabel}
          {revenueShare !== undefined && (
            <span className="text-xs text-succession-text-secondary ml-2">
              ({revenueShare.toFixed(1)}% of revenue)
            </span>
          )}
        </label>
        <div className="text-sm text-succession-text-secondary">
          {fmtCurrency(allocatedAmount)}
        </div>
      </div>
      <div className="flex items-center gap-4">
        <input
          type="range"
          min={0}
          max={100}
          step={0.01}
          value={val}
          onInput={onRange}
          onChange={onRange}
          disabled={disabled}
          onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
          onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
          className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
          style={{ accentColor: 'var(--succession-primary)', background: trackBg, scrollBehavior: 'smooth' }}
        />
        <input
          ref={inputRef}
          id={`allocation-${line}`}
          type="text"
          value={inputValue}
          onChange={onTextChange}
          onFocus={onTextFocus}
          onBlur={onTextBlur}
          disabled={disabled}
          onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
          onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
          placeholder="0.00"
          className="basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2 font-medium"
        />
      </div>
    </div>
  );
};

// Normalizing Adjustment Row Component
const NormalizingAdjustmentRow = ({
  adjustment,
  onUpdate,
  onDelete,
  disabled = false,
  className = ""
}) => {
  const intl = useIntl();
  const { format: _currencyFormat } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const toNumber = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const cleaned = String(v).replace(/[$£,]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  
  const getAdjustmentLabel = (type) => {
    switch (type) {
      case 'ownerExcessComp': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_owner_excess_comp' });
      case 'oneTimeExpenses': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_one_time_expenses' });
      case 'nonOperatingIncome': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_non_operating_income' });
      case 'other': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_other_adjustment' });
      default: return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_adjustment' });
    }
  };

  const isTreatmentLocked = adjustment.type !== 'other';
  const defaultTreatment = adjustment.type === 'nonOperatingIncome' ? 'subtract' : 'add';
  const treatment = adjustment.treatment || defaultTreatment;
  const allocateByLine = adjustment.allocations && Object.values(adjustment.allocations || {}).some(v => v > 0);
  
  const handleAmountChange = (amount) => {
    onUpdate({ ...adjustment, amount });
  };
  
  const handleTreatmentChange = (newTreatment) => {
    onUpdate({ ...adjustment, treatment: newTreatment });
  };
  
  const handleNotesChange = (notes) => {
    onUpdate({ ...adjustment, notes });
  };
  
  const handleAllocationToggle = (allocate) => {
    if (allocate) {
      // Initialize with equal allocation
      onUpdate({ 
        ...adjustment, 
        allocations: { wealth: 25, life: 25, pc: 25, group: 25 } 
      });
    } else {
      onUpdate({ ...adjustment, allocations: undefined });
    }
  };
  
  const handleAllocationChange = (line, percent) => {
    const newAllocations = { ...(adjustment.allocations || {}), [line]: percent };
    onUpdate({ ...adjustment, allocations: newAllocations });
  };
  
  const handleAffectsDCFChange = (affectsDCF) => {
    onUpdate({ ...adjustment, affectsDCF });
  };
  
  const amountNum = Number(adjustment.amount || 0);
  const allocations = adjustment.allocations || { wealth: 0, life: 0, pc: 0, group: 0 };
  const allocationSum = Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0);
  
  // Amount input state management
  const [amountVal, setAmountVal] = React.useState(amountNum);
  const [amountInputValue, setAmountInputValue] = React.useState(() => fmtCurrency(amountNum).replace(/\.00$/, ''));
  const amountInputRef = React.useRef(null);
  const amountFocusedRef = React.useRef(false);
  
  React.useEffect(() => {
    if (!amountFocusedRef.current) {
      setAmountVal(amountNum);
      setAmountInputValue(fmtCurrency(amountNum).replace(/\.00$/, ''));
    }
  }, [amountNum]);
  
  const onAmountRangeChange = (e) => {
    const val = toNumber(e.target.value);
    setAmountVal(val);
    if (!amountFocusedRef.current) {
      setAmountInputValue(fmtCurrency(val).replace(/\.00$/, ''));
    }
    handleAmountChange(val);
  };
  
  const onAmountTextFocus = () => {
    amountFocusedRef.current = true;
    setAmountInputValue(String(amountVal || 0));
  };
  
  const onAmountTextBlur = (e) => {
    amountFocusedRef.current = false;
    const val = toNumber(e.target.value);
    setAmountVal(val);
    setAmountInputValue(fmtCurrency(val).replace(/\.00$/, ''));
    handleAmountChange(val);
  };
  
  const onAmountTextChange = (e) => {
    const v = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;
    setAmountInputValue(v);
    setTimeout(() => {
      if (amountInputRef.current && amountFocusedRef.current) {
        const newPosition = Math.min(cursorPosition, v.length);
        const newEnd = Math.min(selectionEnd, v.length);
        if (document.activeElement === amountInputRef.current) {
          amountInputRef.current.setSelectionRange(newPosition, newEnd);
        } else {
          amountInputRef.current.focus();
          amountInputRef.current.setSelectionRange(newPosition, newEnd);
        }
      }
    }, 0);
  };
  
  const amountPercent = Math.min(100, Math.max(0, ((amountVal + 5000000) / 10000000) * 100));
  const amountTrackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${amountPercent}%, var(--succession-bg-tertiary, #eee) ${amountPercent}%, var(--succession-bg-tertiary, #eee) 100%)`;
  
  return (
    <div className={`border border-succession-border rounded-lg p-4 space-y-4 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
            treatment === 'add' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
          }`}>
            {treatment === 'add' ? '+' : '-'}
          </span>
          <span className="text-sm font-medium text-succession-text-primary">
            {getAdjustmentLabel(adjustment.type)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onDelete(adjustment.id)}
          disabled={disabled}
          className="text-succession-text-secondary hover:text-red-600 disabled:opacity-50"
          aria-label="Delete adjustment"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
      
      {/* Amount Input */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.amount' })}
        </label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={-5000000}
            max={5000000}
            step={50000}
            value={amountVal}
            onChange={onAmountRangeChange}
            disabled={disabled}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
            style={{ 
              accentColor: 'var(--succession-primary)', 
              background: amountTrackBg,
              scrollBehavior: 'smooth' 
            }}
          />
          <input
            ref={amountInputRef}
            type="text"
            value={amountInputValue}
            onChange={onAmountTextChange}
            onFocus={onAmountTextFocus}
            onBlur={onAmountTextBlur}
            disabled={disabled}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2 font-medium"
          />
        </div>
      </div>
      
      {/* Treatment Selector */}
      {!isTreatmentLocked && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.multi_line_valuation.treatment' })}
          </label>
          <select
            value={treatment}
            onChange={(e) => handleTreatmentChange(e.target.value)}
            disabled={disabled}
            className="w-full rounded border border-succession-border px-3 py-2 text-sm bg-succession-bg-primary text-succession-text-primary"
          >
            <option value="add">{intl.formatMessage({ id: 'succession.multi_line_valuation.add_back' })}</option>
            <option value="subtract">{intl.formatMessage({ id: 'succession.multi_line_valuation.subtract' })}</option>
          </select>
        </div>
      )}
      
      {/* Notes */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.notes_justification' })} <span className="text-red-500">*</span>
        </label>
        <textarea
          value={adjustment.notes || ''}
          onChange={(e) => handleNotesChange(e.target.value)}
          disabled={disabled}
          placeholder={intl.formatMessage({ id: 'succession.multi_line_valuation.notes_placeholder' })}
          rows={3}
          className="w-full rounded border border-succession-border px-3 py-2 text-sm bg-succession-bg-primary text-succession-text-primary resize-none"
        />
        <p className="text-xs text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.required_for_high_confidence' })}
        </p>
      </div>
    </div>
  );
};

// Normalizing Adjustments Card Component
const NormalizingAdjustmentsCard = ({
  reportedEBITDA = 0,
  adjustments = [],
  onChange = () => {},
  className = ""
}) => {
  const intl = useIntl();
  const { format: _currencyFormat, symbol: _currencyMSymbol } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const formatCurrency = (val) => {
    return val.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Format currency in M format (e.g., $2.85M or £2.85M)
  const formatCurrencyM = (val) => {
    return `${_currencyMSymbol}${(val / 1000000).toFixed(2)}M`;
  };
  
  const [newAdjustmentType, setNewAdjustmentType] = React.useState('ownerExcessComp');
  
  // Calculate total adjustments
  const totalAdjustments = React.useMemo(() => {
    return adjustments.reduce((sum, adj) => {
      const amount = Number(adj.amount || 0);
      const treatment = adj.treatment || (adj.type === 'nonOperatingIncome' ? 'subtract' : 'add');
      return sum + (treatment === 'add' ? amount : -amount);
    }, 0);
  }, [adjustments]);
  
  const adjustedEBITDA = reportedEBITDA + totalAdjustments;
  const impactPercent = reportedEBITDA > 0 ? (totalAdjustments / reportedEBITDA) * 100 : 0;
  
  // Calculate confidence: High when all adjustments have notes
  const confidence = React.useMemo(() => {
    if (adjustments.length === 0) return 'low';
    const allHaveNotes = adjustments.every(adj => adj.notes && adj.notes.trim().length > 0);
    // Also check allocations if any adjustment has allocations
    const hasAllocations = adjustments.some(adj => adj.allocations);
    if (hasAllocations) {
      const allocationsValid = adjustments
        .filter(adj => adj.allocations)
        .every(adj => {
          const sum = Object.values(adj.allocations || {}).reduce((s, v) => s + (Number(v) || 0), 0);
          return sum === 100;
        });
      return allHaveNotes && allocationsValid ? 'high' : 'low';
    }
    return allHaveNotes ? 'high' : 'low';
  }, [adjustments]);
  
  const handleAddAdjustment = () => {
    const newId = `adj-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const defaultTreatment = newAdjustmentType === 'nonOperatingIncome' ? 'subtract' : 'add';
    const newAdjustment = {
      id: newId,
      type: newAdjustmentType,
      amount: 0,
      treatment: defaultTreatment,
      notes: '',
      affectsDCF: newAdjustmentType !== 'other',
    };
    onChange([...adjustments, newAdjustment]);
  };
  
  const handleUpdateAdjustment = (updatedAdjustment) => {
    const newAdjustments = adjustments.map(adj => 
      adj.id === updatedAdjustment.id ? updatedAdjustment : adj
    );
    onChange(newAdjustments);
  };
  
  const handleDeleteAdjustment = (id) => {
    const newAdjustments = adjustments.filter(adj => adj.id !== id);
    onChange(newAdjustments);
  };
  
  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-succession-text-primary">
            {intl.formatMessage({ id: 'succession.multi_line_valuation.normalizing_adjustments' })}
          </h3>
          <InfoIcon className="w-4 h-4 text-succession-text-secondary cursor-help" />
        </div>
      </div>

      <p className="text-sm text-succession-text-secondary">
        {intl.formatMessage({ id: 'succession.multi_line_valuation.normalizing_adjustments_desc' })}
      </p>
      
      {/* Summary Card */}
      <div 
        className="border border-emerald-200 dark:border-emerald-800 rounded-lg p-6"
        style={{
          backgroundColor: '#ecfdf5'
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-xs text-succession-text-secondary mb-1">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.reported_ebitda' })}
            </div>
            <div className="text-2xl font-bold text-succession-text-primary">
              {formatCurrencyM(reportedEBITDA)}
            </div>
          </div>
          <div>
            <div className="text-xs text-succession-text-secondary mb-1">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.total_adjustments' })}
            </div>
            <div className="flex items-baseline gap-2">
              <div 
                className="text-2xl font-bold"
                style={{
                  color: totalAdjustments >= 0 ? '#059669' : '#dc2626'
                }}
              >
                {totalAdjustments >= 0 ? '+' : ''}{formatCurrencyM(totalAdjustments)}
              </div>
              <div 
                className="text-sm font-medium"
                style={{
                  color: totalAdjustments >= 0 ? '#059669' : '#dc2626'
                }}
              >
                ({impactPercent >= 0 ? '+' : ''}{impactPercent.toFixed(1)}%)
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs text-succession-text-secondary mb-1">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.adjusted_ebitda' })}
            </div>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-emerald-600">
                {formatCurrencyM(adjustedEBITDA)}
              </div>
              <span className={`inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-semibold ${
                confidence === 'high'
                  ? 'bg-emerald-600 text-white border border-emerald-700'
                  : 'bg-amber-600 text-white border border-amber-700'
              }`}
              style={{
                backgroundColor: confidence === 'high' ? '#059669' : '#d97706',
                color: '#ffffff'
              }}>
                {confidence === 'high' ? intl.formatMessage({ id: 'succession.multi_line_valuation.high_confidence_short' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.low_confidence_short' })}
              </span>
            </div>
          </div>
        </div>
        
        {confidence === 'low' && adjustments.length > 0 && (
          <div className="mt-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded p-3 flex items-start gap-2">
            <AlertCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 dark:text-amber-200">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.low_confidence_hint' })}
            </p>
          </div>
        )}
      </div>
      
      {/* Active Adjustments */}
      {adjustments.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-succession-text-primary">
            {intl.formatMessage({ id: 'succession.multi_line_valuation.active_adjustments' })}
          </h4>
          {adjustments.map((adj) => (
            <NormalizingAdjustmentRow
              key={adj.id}
              adjustment={adj}
              onUpdate={handleUpdateAdjustment}
              onDelete={handleDeleteAdjustment}
            />
          ))}
        </div>
      )}
      
      {/* Empty State */}
      {adjustments.length === 0 && (
        <div className="bg-succession-bg-secondary border border-dashed border-succession-border rounded-lg p-8 text-center">
          <TrendingUpIcon className="w-12 h-12 text-succession-text-secondary mx-auto mb-3" />
          <h4 className="text-sm font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.multi_line_valuation.no_adjustments_yet' })}</h4>
          <p className="text-sm text-succession-text-secondary mb-4">
            {intl.formatMessage({ id: 'succession.multi_line_valuation.no_adjustments_desc' })}
          </p>
          <p className="text-xs text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.multi_line_valuation.common_adjustments' })}
          </p>
        </div>
      )}
      
      {/* Add New Adjustment */}
      <div className="border-t border-succession-border pt-6">
        <h4 className="text-sm font-semibold text-succession-text-primary mb-4">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.add_new_adjustment' })}
        </h4>
        <div className="flex items-stretch gap-3">
          <select
            value={newAdjustmentType}
            onChange={(e) => setNewAdjustmentType(e.target.value)}
            className="flex-1 rounded border border-succession-border px-3 py-2 text-sm bg-succession-bg-primary text-succession-text-primary min-w-0"
          >
            <option value="ownerExcessComp">{intl.formatMessage({ id: 'succession.multi_line_valuation.adj_owner_excess_comp' })}</option>
            <option value="oneTimeExpenses">{intl.formatMessage({ id: 'succession.multi_line_valuation.adj_one_time_expenses' })}</option>
            <option value="nonOperatingIncome">{intl.formatMessage({ id: 'succession.multi_line_valuation.adj_non_operating_income' })}</option>
            <option value="other">{intl.formatMessage({ id: 'succession.multi_line_valuation.adj_other_adjustment' })}</option>
          </select>
          <Button
            variant="primary"
            size="md"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddAdjustment();
            }}
            className="whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <TrendingUpIcon className="w-4 h-4 mr-2" />
            <span>{intl.formatMessage({ id: 'succession.multi_line_valuation.add_adjustment_btn' })}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

// Expenses Form Component
const ExpensesForm = ({ values = {}, onFieldChange = () => {}, onChange = () => {}, result = null, currentUser = {}, viewerUser = null, terminology = {}, enabledLines = {} }) => {
  const intl = useIntl();
  const { format: _currencyFormat, symbol: _categorySymbol } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });
  const toNumber = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    const cleaned = String(v).replace(/[$£,]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  const formatCurrency = (val) => {
    return val.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const DEFAULT_OPEX_CATEGORIES = [
    {
      key: "compensation",
      label: "Compensation - Admin and Support Staff",
      description: "Salaries, wages, and benefits for administrative and support personnel",
    },
    {
      key: "benefits",
      label: "Cost of Benefits",
      description: "Health insurance, retirement contributions, and other employee benefits",
    },
    {
      key: "occupancy",
      label: "Occupancy Cost (Rent, Utilities, etc.)",
      description: "Office rent, utilities, maintenance, and related costs",
    },
    {
      key: "technology",
      label: "Technology Cost (Software, Hardware, Office Equipment)",
      description: "Software licenses, hardware, IT services, and office equipment",
    },
    {
      key: "professional",
      label: "Professional Services (Accounting, Compliance, Consult, Legal)",
      description: "External professional services and consulting fees",
    },
    {
      key: "marketing",
      label: "Marketing and Business Development",
      description: "Marketing, advertising, and business development expenses",
    },
    {
      key: "travel",
      label: "Travel",
      description: "Business travel, lodging, and related expenses",
    },
    {
      key: "insurance",
      label: "Insurance, Business-related",
      description: "Business insurance, E&O, and other coverage",
    },
    {
      key: "other",
      label: "Other Expenses (Taxes, Licenses, etc.)",
      description: "Miscellaneous operating expenses",
    },
  ];

  // NOTE: We intentionally do NOT prefill per-category benchmark amounts.
  // Benchmark mode uses a single revenue-based benchmark total (see calculateBenchmarkExpenses()).

  const company = viewerUser?.company || currentUser?.company;
  const adminCategories = (company?.multi_line_expense_categories || []).filter((c) => c?.enabled !== false);
  const categoryDefinitions = (adminCategories && adminCategories.length > 0)
    ? adminCategories
        .slice()
        .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
        .map((c) => ({
          key: String(c.key),
          label: c.label || String(c.key),
          description: c.description || "",
          groupName: c.groupName,
        }))
    : DEFAULT_OPEX_CATEGORIES;

  const categories = Array.isArray(values.operatingExpenseCategories) ? values.operatingExpenseCategories : [];

  const getCategory = (key) => {
    const found = categories.find((c) => String(c?.key) === String(key));
    if (found) return found;
    const def = categoryDefinitions.find((d) => d.key === key) || DEFAULT_OPEX_CATEGORIES.find((d) => d.key === key);
    return {
      key,
      label: def?.label || key,
      description: def?.description || "",
      amount: 0,
      adjustments: [],
    };
  };

  const computeCategoryNormalized = (cat) => {
    const base = Number(cat?.amount || 0);
    const adj = (cat?.adjustments || []).reduce((s, a) => s + Number(a?.amount || 0), 0);
    return base + adj;
  };

  const useBenchmark = !!values.useBenchmark;
  const otherAdjustments = values.otherAdjustments || {};
  const nonOperatingIncomeNum = Number(otherAdjustments?.nonOperatingIncome || 0);
  
  // Benchmark operating expenses always come from the backend
  // (MultiLineValuationService#benchmark_operating_expenses). There used to be a
  // client-side copy of the RJA tier ladder here as a fallback, but it had no RJAS
  // branch, so RJAS advisors were charged the RJA percentage instead of
  // rjas_benchmark_expense_percentage. Returns null until the backend has answered —
  // callers must not substitute a guess.
  const calculateBenchmarkExpenses = React.useCallback(() => {
    const backendValue = result?.summary_metrics?.benchmark_operating_expenses;
    return backendValue == null ? null : Number(backendValue);
  }, [result]);

  const manualReportedOpEx = categoryDefinitions.reduce((sum, def) => sum + Number(getCategory(def.key)?.amount || 0), 0);
  const manualNormalizedOpEx = categoryDefinitions.reduce((sum, def) => sum + computeCategoryNormalized(getCategory(def.key)), 0);

  // When benchmark is enabled, the totals should reflect the revenue-based benchmark total and
  // reported/normalized should be identical (no category-level normalizations).
  const benchmarkOperatingExpenses = React.useMemo(() => {
    if (!useBenchmark) return 0;
    const v = Number(values?.operatingExpenses);
    if (Number.isFinite(v) && v !== 0) return v;
    return Number(calculateBenchmarkExpenses() || 0);
  }, [useBenchmark, values?.operatingExpenses, calculateBenchmarkExpenses]);

  const totalReportedOpEx = useBenchmark ? benchmarkOperatingExpenses : manualReportedOpEx;
  const totalNormalizedOpEx = useBenchmark ? benchmarkOperatingExpenses : manualNormalizedOpEx;
  const operatingExpensesNum = totalReportedOpEx;
  const directExpensesValue = values.directExpenses !== null && values.directExpenses !== undefined ? Number(values.directExpenses) : null;
  const expenseGrowthRate = Number(values.expenseGrowthRate || 0.02);

  // Determine if benchmark toggle should be shown
  // Logic: show if (!employee_based_advisors || rjas_benchmark_expenses feature exists)
  const showBenchmarkToggle = React.useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const employeeBasedAdvisors = !!company?.employee_based_advisors;
    const featureTypes = company?.feature_types || [];
    const hasRjasBenchmarkExpenses = Array.isArray(featureTypes) && featureTypes.includes('rjas_benchmark_expenses');
    
    // Show if: not employee_based_advisors OR has rjas_benchmark_expenses feature
    return !employeeBasedAdvisors || hasRjasBenchmarkExpenses;
  }, [currentUser, viewerUser]);

  // Benchmark mode: mirror the backend's benchmark total into the displayed field.
  // Do NOT prefill or mutate category rows.
  React.useEffect(() => {
    if (!showBenchmarkToggle) return;
    if (!useBenchmark) return;
    const nextBenchmarkTotal = calculateBenchmarkExpenses();
    // Backend hasn't answered yet — leave the field alone rather than flashing a zero.
    if (nextBenchmarkTotal == null) return;
    onChange && onChange();
    onFieldChange('operatingExpenses', nextBenchmarkTotal);
  }, [useBenchmark, showBenchmarkToggle, calculateBenchmarkExpenses]);
  
  // Calculate revenue shares from Year 1 DCF projections
  const revenueShares = React.useMemo(() => {
    const shares = { wealth: 0, life: 0, pc: 0, group: 0 };
    const lines = result?.lines || [];
    const totalRevenue = lines.reduce((sum, line) => {
      const rev = line?.methods?.dcf?.projections?.[0]?.revenue || 0;
      return sum + rev;
    }, 0);
    
    if (totalRevenue > 0) {
      lines.forEach((line) => {
        const rev = line?.methods?.dcf?.projections?.[0]?.revenue || 0;
        const share = (rev / totalRevenue) * 100;
        if (line.line === 'wealth') shares.wealth = share;
        else if (line.line === 'life') shares.life = share;
        else if (line.line === 'pc') shares.pc = share;
        else if (line.line === 'group') shares.group = share;
      });
    }
    return shares;
  }, [result]);

  // Calculate direct expenses - use backend-calculated value when available
  const calculateDirectExpenses = React.useCallback(() => {
    const ownersCompEnabled = (viewerUser?.company?.rj_use_owners_comp_direct_expenses != null)
      ? !!viewerUser.company.rj_use_owners_comp_direct_expenses
      : (currentUser?.company?.rj_use_owners_comp_direct_expenses != null ? !!currentUser.company.rj_use_owners_comp_direct_expenses : true);
    
    if (!ownersCompEnabled) return 0;
    
    // Prefer backend-calculated direct expenses from summary_metrics
    if (result?.summary_metrics?.direct_expenses != null) {
      return result.summary_metrics.direct_expenses;
    }
    
    // Also check Year 0 directExpenses from wealth line DCF
    const wealthLine = (result?.lines || []).find((l) => l.line === 'wealth');
    if (wealthLine?.methods?.dcf?.year0?.directExpenses != null) {
      return wealthLine.methods.dcf.year0.directExpenses;
    }
    
    // Fallback: calculate client-side if backend value not available
    const wealthRev = wealthLine?.methods?.dcf?.projections?.[0]?.revenue || 0;
    const payoutLocal = (() => {
      const n = Number(currentUser?.branch_net_payout);
      return Number.isFinite(n) ? (n > 1 ? n / 100 : n) : 0;
    })();
    const branchNetRev = wealthRev * payoutLocal;
    
    const ocpLocal = (bnr) => {
      if (bnr < 250000.0) return 0.401;
      if (bnr < 500000.0) return 0.44;
      if (bnr < 1000000.0) return 0.264;
      if (bnr < 2000000.0) return 0.202;
      if (bnr < 3000000.0) return 0.194;
      if (bnr < 5000000.0) return 0.189;
      if (bnr < 7000000.0) return 0.132;
      if (bnr < 10000000.0) return 0.193;
      if (bnr < 15000000.0) return 0.183;
      return 0.141;
    };
    
    return branchNetRev * ocpLocal(branchNetRev);
  }, [result, currentUser, viewerUser]);

  const directExpensesNum = directExpensesValue !== null ? directExpensesValue : calculateDirectExpenses();
  const showDirectExpenses = (() => {
    // Use the same defensive logic as ownersCompEnabledTable
    const viewerCompany = viewerUser?.company;
    const currentCompany = currentUser?.company;
    
    // If viewerUser has company data, use it (most authoritative)
    if (viewerCompany && viewerCompany.hasOwnProperty('rj_use_owners_comp_direct_expenses')) {
      return !!viewerCompany.rj_use_owners_comp_direct_expenses;
    }
    
    // Otherwise, check currentUser company data
    if (currentCompany && currentCompany.hasOwnProperty('rj_use_owners_comp_direct_expenses')) {
      return !!currentCompany.rj_use_owners_comp_direct_expenses;
    }
    
    // Default to false if neither has the property set (more conservative)
    // This prevents showing Direct Expenses when the toggle is OFF but data hasn't loaded yet
    return false;
  })();

  const allocations = values.allocations || { wealth: 0, life: 0, pc: 0, group: 0 };
  const sumPercent = React.useMemo(() => {
    return Object.values(allocations).reduce((sum, val) => sum + Number(val || 0), 0);
  }, [allocations]);

  const confidence = sumPercent === 100 ? "high" : "low";
  const isFullyAllocated = sumPercent === 100;
  // Allocation sliders should stay editable even before the user enters category amounts.
  // Use a non-zero base for the dollar preview if the user only entered adjustments (normalized != 0).
  const allocationBaseNum = React.useMemo(() => {
    const reported = Number(totalReportedOpEx || 0);
    const normalized = Number(totalNormalizedOpEx || 0);
    if (reported !== 0) return reported;
    if (normalized !== 0) return normalized;
    return 0;
  }, [totalReportedOpEx, totalNormalizedOpEx]);
  const hasAnyOperatingExpenses = allocationBaseNum !== 0;
  const isDisabled = false;
  const [expandedCategories, setExpandedCategories] = React.useState({});

  const handleAllocationChange = (line, percent) => {
    onChange && onChange();
    onFieldChange(`allocations.${line}`, percent);
  };

  const applyRevenueAllocations = React.useCallback(() => {
    onChange && onChange();
    // Normalize to 100% and round to 2 decimals; fix last line for rounding drift
    const raw = {
      wealth: Number(revenueShares.wealth || 0),
      life: Number(revenueShares.life || 0),
      pc: Number(revenueShares.pc || 0),
      group: Number(revenueShares.group || 0),
    };
    const sumRaw = Object.values(raw).reduce((s, v) => s + Number(v || 0), 0);
    const base = sumRaw > 0 ? raw : { wealth: 100, life: 0, pc: 0, group: 0 }; // fallback if no revenue data
    const scale = sumRaw > 0 ? (100 / sumRaw) : 1;
    const next = {
      wealth: base.wealth * scale,
      life: base.life * scale,
      pc: base.pc * scale,
      group: base.group * scale,
    };
    // Round and ensure exact 100
    const wealthPct = parseFloat(next.wealth.toFixed(2));
    const lifePct = parseFloat(next.life.toFixed(2));
    const pcPct = parseFloat(next.pc.toFixed(2));
    const groupPct = parseFloat((100 - (wealthPct + lifePct + pcPct)).toFixed(2));
    onFieldChange('allocations.wealth', wealthPct);
    onFieldChange('allocations.life', lifePct);
    onFieldChange('allocations.pc', pcPct);
    onFieldChange('allocations.group', groupPct);
  }, [onChange, onFieldChange, revenueShares]);

  const handleAutoAllocateByRevenue = () => {
    applyRevenueAllocations();
  };

  // Default allocations to revenue shares once the user has operating expenses, but hasn't set allocations yet.
  // This avoids the "nothing changes until I click auto allocate" footgun.
  const didAutoAllocateRef = React.useRef(false);
  React.useEffect(() => {
    if (didAutoAllocateRef.current) return;
    // Only auto-allocate once we have some operating expenses and the user hasn't allocated anything yet.
    if (allocationBaseNum === 0) return;
    if (sumPercent !== 0) return;
    didAutoAllocateRef.current = true;
    applyRevenueAllocations();
  }, [allocationBaseNum, sumPercent, applyRevenueAllocations]);

  const handleEqualSplit = () => {
    onChange && onChange();
    const equalPercent = 100 / 4;
    const newAllocations = {
      wealth: equalPercent,
      life: equalPercent,
      pc: equalPercent,
      group: 100 - (equalPercent * 3), // Ensure exactly 100%
    };
    Object.entries(newAllocations).forEach(([line, pct]) => {
      onFieldChange(`allocations.${line}`, parseFloat(pct.toFixed(2)));
    });
  };

  const handleReset = () => {
    onChange && onChange();
    onFieldChange('allocations.wealth', 0);
    onFieldChange('allocations.life', 0);
    onFieldChange('allocations.pc', 0);
    onFieldChange('allocations.group', 0);
  };

  const handleNormalize = () => {
    if (sumPercent === 0) return;
    onChange && onChange();
    const scale = 100 / sumPercent;
    const newAllocations = {
      wealth: allocations.wealth * scale,
      life: allocations.life * scale,
      pc: allocations.pc * scale,
      group: 100 - ((allocations.wealth * scale) + (allocations.life * scale) + (allocations.pc * scale)), // Fix last line for rounding
    };
    Object.entries(newAllocations).forEach(([line, pct]) => {
      onFieldChange(`allocations.${line}`, parseFloat(pct.toFixed(2)));
    });
  };

  const handleDirectExpensesChange = (fieldId, raw) => {
    onChange && onChange();
    const numVal = toNumber(raw);
    onFieldChange('directExpenses', numVal);
  };

  const handleCategoryAmountChange = (key, raw) => {
    onChange && onChange();
    const numVal = toNumber(raw);
    const next = categoryDefinitions.map((def) => {
      const c = getCategory(def.key);
      if (def.key !== key) return c;
      return { ...c, key: def.key, label: def.label, description: def.description, amount: numVal };
    });
    onFieldChange('operatingExpenseCategories', next);
    onFieldChange('operatingExpenses', next.reduce((s, c) => s + Number(c?.amount || 0), 0));
  };

  const handleAddAdjustment = (key) => {
    onChange && onChange();
    const next = categoryDefinitions.map((def) => {
      const c = getCategory(def.key);
      if (def.key !== key) return c;
      const adjustments = Array.isArray(c.adjustments) ? c.adjustments : [];
      const newAdj = { id: `adj_${Date.now()}_${Math.random().toString(36).slice(2)}`, label: '', amount: 0 };
      return { ...c, adjustments: [...adjustments, newAdj] };
    });
    onFieldChange('operatingExpenseCategories', next);
  };

  const handleUpdateAdjustment = (key, adjIndex, patch) => {
    onChange && onChange();
    const next = categoryDefinitions.map((def) => {
      const c = getCategory(def.key);
      if (def.key !== key) return c;
      const adjustments = Array.isArray(c.adjustments) ? c.adjustments.slice() : [];
      adjustments[adjIndex] = { ...(adjustments[adjIndex] || {}), ...patch };
      return { ...c, adjustments };
    });
    onFieldChange('operatingExpenseCategories', next);
  };

  const handleDeleteAdjustment = (key, adjIndex) => {
    onChange && onChange();
    const next = categoryDefinitions.map((def) => {
      const c = getCategory(def.key);
      if (def.key !== key) return c;
      const adjustments = (Array.isArray(c.adjustments) ? c.adjustments : []).filter((_, i) => i !== adjIndex);
      return { ...c, adjustments };
    });
    onFieldChange('operatingExpenseCategories', next);
  };

  const handleToggleAdjustmentSign = (key, adjIndex) => {
    const c = getCategory(key);
    const current = (Array.isArray(c.adjustments) ? c.adjustments : [])[adjIndex];
    const amount = Number(current?.amount || 0);
    handleUpdateAdjustment(key, adjIndex, { amount: -amount });
  };

  const handleExpenseGrowthRateChange = (fieldId, raw) => {
    onChange && onChange();
    const numVal = toNumber(raw);
    onFieldChange('expenseGrowthRate', numVal / 100); // Convert percentage to decimal
  };

  const handleBenchmarkToggle = (checked) => {
    onChange && onChange();
    onFieldChange('useBenchmark', checked);
    if (checked) {
      // Show the backend's benchmark total immediately when toggled on. If it hasn't
      // arrived yet the recalculate that follows will fill it in.
      const benchmarkTotal = calculateBenchmarkExpenses();
      if (benchmarkTotal != null) onFieldChange('operatingExpenses', benchmarkTotal);
    } else {
      // If user has not entered any category amounts, keep totals blank (0)
      const cats = Array.isArray(values?.operatingExpenseCategories) ? values.operatingExpenseCategories : [];
      const manualTotal = cats.reduce((s, c) => s + Number(c?.amount || 0), 0);
      if (!manualTotal) onFieldChange('operatingExpenses', 0);
    }
  };

  const handleNonOperatingIncomeChange = (raw) => {
    onChange && onChange();
    const numVal = toNumber(raw);
    onFieldChange('otherAdjustments.nonOperatingIncome', numVal);
  };

  const BUSINESS_LINES = [
    { id: 'wealth', label: 'Wealth' },
    { id: 'life', label: 'Life' },
    { id: 'pc', label: 'P&C' },
    { id: 'group', label: 'Group' },
  ];

  // Revenue lines this tenant has configured (from the enabledLines map).
  // When only one is configured (e.g. RJA = Wealth only), a per-line operating-
  // expense allocation is meaningless: the single line gets 100%; the
  // "Allocate Operating Expenses by Line" section is hidden.
  const effectiveLineIds = (() => {
    const ids = BUSINESS_LINES.map((l) => l.id).filter((id) => enabledLines && enabledLines[id]);
    return ids.length > 0 ? ids : ['wealth'];
  })();
  const isSingleLine = effectiveLineIds.length === 1;

  React.useEffect(() => {
    if (!isSingleLine) return;
    const only = effectiveLineIds[0];
    const a = values.allocations || {};
    const others = ['wealth', 'life', 'pc', 'group'].filter((l) => l !== only);
    if (Number(a[only]) === 100 && others.every((l) => !Number(a[l]))) return;
    onFieldChange(`allocations.${only}`, 100);
    others.forEach((l) => { if (Number(a[l])) onFieldChange(`allocations.${l}`, 0); });
  }, [isSingleLine, values.allocations]);

  const rowBase = 'space-y-2';
  const labelBase = 'text-sm text-succession-text-secondary';
  const inputBox = 'basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2';

  // SliderRow for percentage inputs (Expense Growth Rate)
  const SliderRowPercent = ({ id, label, min = -10, max = 20, step = 0.5, value }) => {
    const toPercentStr = (v) => `${Math.round(Number(v || 0) * 1000) / 10}%`;
    const toNumber = (v) => {
      if (v === undefined || v === null || v === '') return 0;
      const cleaned = String(v).replace(/[$£,%]/g, '').replace(/,/g, '');
      const num = parseFloat(cleaned);
      return Number.isFinite(num) ? num : 0;
    };
    const numeric = Number(value || 0) * 100; // Convert decimal to percentage
    const [val, setVal] = React.useState(numeric);
    const [inputValue, setInputValue] = React.useState(() => toPercentStr(value));
    const inputRef = React.useRef(null);
    const isFocusedRef = React.useRef(false);
    
    React.useEffect(() => {
      if (!isFocusedRef.current) {
        const newNumeric = Number(value || 0) * 100;
        setVal(newNumeric);
        setInputValue(toPercentStr(value));
      }
    }, [value]);
    
    const percentFill = Math.min(100, Math.max(0, ((val - min) / (max - min)) * 100));
    const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${percentFill}%, var(--succession-bg-tertiary, #eee) ${percentFill}%, var(--succession-bg-tertiary, #eee) 100%)`;
    
    const debounceRef = React.useRef(null);
    const onRange = (e) => {
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      if (!isFocusedRef.current) {
        setInputValue(`${Number(numVal || 0).toFixed(1)}%`);
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        handleExpenseGrowthRateChange(id, v);
      }, 120);
    };

    const onTextFocus = () => {
      isFocusedRef.current = true;
      setInputValue(`${Number(val || 0).toFixed(1)}%`);
    };

    const onTextBlur = (e) => {
      isFocusedRef.current = false;
      const v = e.target.value;
      const numVal = toNumber(v);
      const clamped = Math.min(max, Math.max(min, numVal));
      setVal(clamped);
      setInputValue(`${Number(clamped || 0).toFixed(1)}%`);
      handleExpenseGrowthRateChange(id, clamped);
    };

    const onTextChange = (e) => {
      const v = e.target.value;
      const cursorPosition = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      setInputValue(v);
      setTimeout(() => {
        if (inputRef.current && isFocusedRef.current) {
          const newPosition = Math.min(cursorPosition, v.length);
          const newEnd = Math.min(selectionEnd, v.length);
          if (document.activeElement === inputRef.current) {
            inputRef.current.setSelectionRange(newPosition, newEnd);
          } else {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newPosition, newEnd);
          }
        }
      }, 0);
    };

    return (
      <div className={rowBase}>
        <div className={labelBase}>{label}</div>
        <div className="flex items-center gap-4 w-full">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={val}
            onInput={onRange}
            onChange={onRange}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
            style={{ accentColor: 'var(--succession-primary)', background: trackBg, scrollBehavior: 'smooth' }}
          />
          <input
            ref={inputRef}
            type="text"
            className={inputBox}
            value={inputValue}
            onChange={onTextChange}
            onFocus={onTextFocus}
            onBlur={onTextBlur}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            placeholder="0%"
          />
        </div>
      </div>
    );
  };

  // Reuse SliderRow for direct and operating expenses inputs
  const SliderRow = ({ id, label, tooltip, type = 'currency', min = 0, max = 10000000, step = 50000, value, disabled = false }) => {
    const numeric = toNumber(value);
    const isCurrency = type === 'currency';
    const getInitialInputValue = () => {
      return fmtCurrency(numeric).replace(/\.00$/, '');
    };
    const [val, setVal] = React.useState(numeric);
    const [inputValue, setInputValue] = React.useState(getInitialInputValue());
    const inputRef = React.useRef(null);
    const isFocusedRef = React.useRef(false);
    React.useEffect(() => {
      if (!isFocusedRef.current) {
        setVal(numeric);
        setInputValue(getInitialInputValue());
      }
    }, [numeric]);
    const rangeStep = isCurrency ? 50000 : step;
    const debounceRef = React.useRef(null);
    const onRange = (e) => {
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      if (!isFocusedRef.current) {
        setInputValue(fmtCurrency(numVal).replace(/\.00$/, ''));
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (id === 'directExpenses') handleDirectExpensesChange(id, v);
      }, 120);
    };
    const percent = (() => {
      const denom = (max - min) || 1;
      const p = ((val - min) / denom) * 100;
      return Math.min(100, Math.max(0, p));
    })();
    const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${percent}%, var(--succession-bg-tertiary, #eee) ${percent}%, var(--succession-bg-tertiary, #eee) 100%)`;
    const onTextFocus = () => {
      isFocusedRef.current = true;
      setInputValue(String(val || 0));
    };
    const onTextBlur = (e) => {
      isFocusedRef.current = false;
      const v = e.target.value;
      const numVal = toNumber(v);
      setVal(numVal);
      setInputValue(fmtCurrency(numVal).replace(/\.00$/, ''));
      if (id === 'directExpenses') handleDirectExpensesChange(id, v);
    };
    const onTextChange = (e) => {
      const v = e.target.value;
      const cursorPosition = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      setInputValue(v);
      setTimeout(() => {
        if (inputRef.current && isFocusedRef.current) {
          const newPosition = Math.min(cursorPosition, v.length);
          const newEnd = Math.min(selectionEnd, v.length);
          if (document.activeElement === inputRef.current) {
            inputRef.current.setSelectionRange(newPosition, newEnd);
          } else {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newPosition, newEnd);
          }
        }
      }, 0);
    };
    return (
      <div className={rowBase}>
        {label && <div className={labelBase}><TooltipLabel label={label} tooltip={tooltip} /></div>}
        <div className="flex items-center gap-4 w-full">
          <input
            type="range"
            min={min}
            max={max}
            step={rangeStep}
            value={val}
            onInput={onRange}
            onChange={onRange}
            disabled={disabled}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
            style={{ accentColor: 'var(--succession-primary)', background: trackBg, scrollBehavior: 'smooth' }}
          />
          <input
            ref={inputRef}
            type="text"
            className={inputBox}
            value={inputValue}
            onChange={onTextChange}
            onFocus={onTextFocus}
            onBlur={onTextBlur}
            disabled={disabled}
            onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            placeholder="0"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="text-xl font-semibold mb-2 text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.expenses' })}</h2>
        <p className="text-sm text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.expenses_desc' })}
        </p>
      </motion.div>

      <div className="space-y-10">
        {/* Direct Expenses Section */}
        <div>
          <h3 className="text-base font-semibold mb-6 text-succession-text-primary">
            {terminology?.expenses?.direct_expenses?.label || intl.formatMessage({ id: 'succession.multi_line_valuation.direct_expenses_title' })}
          </h3>
          <div className="space-y-6">
            {showDirectExpenses && (
              <SliderRow
                id="directExpenses"
                label={terminology?.expenses?.direct_expenses?.label || intl.formatMessage({ id: 'succession.multi_line_valuation.direct_expenses_label' })}
                tooltip={getRevenueLineTerm(terminology, 'expenses', 'direct_expenses').definition}
                type="currency"
                min={0}
                max={5000000}
                step={50000}
                value={directExpensesNum}
              />
              )}
            <SliderRowPercent
              id="expenseGrowthRate"
              label={intl.formatMessage({ id: 'succession.multi_line_valuation.expense_growth_rate_percent' })}
              min={-10}
              max={20}
              step={0.5}
              value={expenseGrowthRate}
            />
          </div>
        </div>

        {/* Totals Panel */}
        <div className="bg-succession-bg-secondary border border-succession-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4 gap-3">
            <h3 className="text-base font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.operating_expenses_summary' })}
            </h3>
            {showBenchmarkToggle && (
              <div className="flex items-center gap-2">
                <label htmlFor="useBenchmark" className="text-xs font-normal text-succession-text-secondary cursor-pointer">
                  {intl.formatMessage({ id: 'succession.multi_line_valuation.use_benchmark' })}
                </label>
                {(() => {
                  const benchmarkSourceName = terminology?.expenses?.benchmark_source?.label || '';
                  const benchmarkSourceNote = getRevenueLineTerm(terminology, 'expenses', 'benchmark_source').definition
                    || (benchmarkSourceName ? `Benchmarks are based on ${benchmarkSourceName} for firms of similar size and revenue profile.` : '');
                  return benchmarkSourceNote ? <InfoTooltip content={benchmarkSourceNote} /> : null;
                })()}
                <button
                  type="button"
                  role="switch"
                  aria-checked={useBenchmark}
                  id="useBenchmark"
                  onClick={() => handleBenchmarkToggle(!useBenchmark)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    useBenchmark ? 'bg-succession-primary' : 'bg-succession-bg-tertiary'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useBenchmark ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4">
              <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.multi_line_valuation.reported_total' })}</div>
              <div className="text-2xl font-bold text-succession-text-primary">{fmtCurrency(totalReportedOpEx)}</div>
            </div>
            <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4">
              <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.multi_line_valuation.normalized_total' })}</div>
              <div className="text-2xl font-bold text-succession-text-primary">{fmtCurrency(totalNormalizedOpEx)}</div>
            </div>
          </div>
        </div>

        {/* Category-based Operating Expenses */}
        <div id="operating-expense-categories-section" className="border-t border-succession-border pt-10">
          <div className="flex items-center justify-between mb-6 gap-3">
            <h3 className="text-base font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.operating_expenses_by_category' })}
            </h3>
          </div>

          {useBenchmark && (
            <div className="bg-succession-bg-secondary border border-succession-border rounded-lg p-4 text-sm text-succession-text-secondary mb-6">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.benchmark_enabled_readonly' })}
            </div>
          )}

          <div className={`space-y-4 ${useBenchmark ? 'opacity-60 pointer-events-none' : ''}`}>
            {(() => {
              const renderCategoryRow = (def) => {
                  const cat = getCategory(def.key);
                  const normalized = computeCategoryNormalized(cat);
                  const delta = normalized - Number(cat?.amount || 0);
                  const hasAdj = Array.isArray(cat.adjustments) && cat.adjustments.length > 0;
                  const expanded = !!expandedCategories[def.key];

                  return (
                    <div key={def.key} className="border border-succession-border rounded-lg bg-succession-bg-secondary">
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => setExpandedCategories((prev) => ({ ...(prev || {}), [def.key]: !expanded }))}
                            className="h-8 w-8 p-0 shrink-0 mt-0.5 rounded hover:bg-succession-bg-tertiary flex items-center justify-center"
                            aria-label={expanded ? "Collapse category" : "Expand category"}
                          >
                            {expanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-succession-text-primary">{def.label}</div>
                            {def.description && (
                              <div className="text-xs text-succession-text-secondary mt-0.5">{def.description}</div>
                            )}
                            {hasAdj && (
                              <div className="mt-2 flex items-center gap-2 text-xs">
                                <span className="text-succession-text-secondary">
                                  {cat.adjustments.length} adjustment{cat.adjustments.length !== 1 ? "s" : ""}
                                </span>
                                <span className={`font-medium ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-600" : "text-succession-text-secondary"}`}>
                                  {delta > 0 ? "+" : ""}{fmtCurrency(delta)}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="w-40 shrink-0">
                            <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.multi_line_valuation.reported_amount' })}</div>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-succession-text-secondary">{_categorySymbol}</span>
                              <input
                                type="text"
                                value={Number(cat.amount || 0).toLocaleString()}
                                onChange={(e) => handleCategoryAmountChange(def.key, e.target.value)}
                                placeholder="0"
                                disabled={useBenchmark}
                                className="h-9 w-full text-sm pl-6 pr-3 text-right rounded border border-succession-border bg-succession-bg-primary text-succession-text-primary"
                              />
                            </div>
                          </div>

                          <div className="w-40 shrink-0">
                            <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.multi_line_valuation.normalized_amount' })}</div>
                            <div
                              className={`h-9 px-3 rounded border border-succession-border bg-succession-bg-primary flex items-center justify-end text-sm font-medium ${
                                delta !== 0 ? (delta > 0 ? "text-emerald-600" : "text-red-600") : "text-succession-text-primary"
                              }`}
                            >
                              {fmtCurrency(normalized)}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              handleAddAdjustment(def.key);
                              setExpandedCategories((prev) => ({ ...(prev || {}), [def.key]: true }));
                            }}
                            disabled={useBenchmark}
                            className="h-9 shrink-0 px-3 rounded border border-succession-border text-xs text-succession-text-primary hover:bg-succession-bg-tertiary inline-flex items-center"
                          >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            {intl.formatMessage({ id: 'succession.multi_line_valuation.adjust_btn' })}
                          </button>
                        </div>
                      </div>

                      {expanded && (
                        <div className="border-t border-succession-border bg-succession-bg-primary p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.adjustments' })}</h4>
                              {!hasAdj && <span className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.no_adjustments_yet_short' })}</span>}
                            </div>

                            {hasAdj ? (
                              <div className="space-y-2">
                                {(cat.adjustments || []).map((adj, idx) => {
                                  const isPositive = Number(adj?.amount || 0) >= 0;
                                  return (
                                    <div key={adj.id || idx} className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleToggleAdjustmentSign(def.key, idx)}
                                        disabled={useBenchmark}
                                        className={`h-8 w-8 p-0 shrink-0 rounded border ${
                                          isPositive
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100"
                                            : "bg-red-50 border-red-200 text-red-600 hover:bg-red-100"
                                        } flex items-center justify-center`}
                                        aria-label="Toggle adjustment sign"
                                      >
                                        {isPositive ? <PlusIcon className="w-3.5 h-3.5" /> : <MinusIcon className="w-3.5 h-3.5" />}
                                      </button>

                                      <input
                                        type="text"
                                        value={adj.label || ""}
                                        onChange={(e) => handleUpdateAdjustment(def.key, idx, { label: e.target.value })}
                                        placeholder="Adjustment description"
                                        disabled={useBenchmark}
                                        className="flex-1 h-8 text-sm rounded border border-succession-border bg-succession-bg-secondary text-succession-text-primary px-3"
                                      />

                                      <div className="relative w-32 shrink-0">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-succession-text-secondary">{_categorySymbol}</span>
                                        <input
                                          type="text"
                                          value={Math.abs(Number(adj.amount || 0)).toLocaleString()}
                                          onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9.-]/g, "");
                                            const numValue = value === "" || value === "-" ? 0 : parseFloat(value);
                                            const abs = Number.isNaN(numValue) ? 0 : numValue;
                                            const signed = (Number(adj.amount || 0) >= 0) ? abs : -abs;
                                            handleUpdateAdjustment(def.key, idx, { amount: signed });
                                          }}
                                          placeholder="0"
                                          disabled={useBenchmark}
                                          className="h-8 w-full text-sm pl-6 pr-3 text-right rounded border border-succession-border bg-succession-bg-secondary text-succession-text-primary"
                                        />
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => handleDeleteAdjustment(def.key, idx)}
                                        disabled={useBenchmark}
                                        className="h-8 w-8 p-0 shrink-0 rounded hover:bg-succession-bg-tertiary text-succession-text-secondary hover:text-red-600 flex items-center justify-center"
                                        aria-label="Delete adjustment"
                                      >
                                        <XIcon className="w-4 h-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-sm text-succession-text-secondary">
                                {intl.formatMessage({ id: 'succession.multi_line_valuation.click_adjust_hint' })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
              };

              const groups = new Map();
              categoryDefinitions.forEach(def => {
                const g = def.groupName || 'Other';
                if (!groups.has(g)) groups.set(g, []);
                groups.get(g).push(def);
              });
              
              return Array.from(groups.entries()).map(([groupName, defs]) => (
                <div key={groupName} className="space-y-4">
                  {(groupName !== 'Other' || groups.size > 1) && (
                     <h4 className="font-semibold text-succession-text-primary mt-6 mb-2">{groupName === 'Other' && groups.size > 1 ? 'Other Expenses' : groupName}</h4>
                  )}
                  {defs.map(renderCategoryRow)}
                </div>
              ));
            })()}
          </div>
        </div>

        {/* Other Adjustments (hidden for now; keep state+payload for future) */}
        {false && (
          <div className="border-t border-succession-border pt-10">
            <h3 className="text-base font-semibold mb-2 text-succession-text-primary">Other Adjustments</h3>
            <p className="text-sm text-succession-text-secondary mb-6">
              These adjustments are separate from category-based expense normalizations.
            </p>
            <div className="bg-succession-bg-secondary border border-succession-border rounded-lg p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-succession-text-primary">Non-Operating Income</div>
                  <div className="text-xs text-succession-text-secondary">
                    Income not related to normal operations (Option A). Enter annual amount.
                  </div>
                </div>
                <div className="relative w-48 shrink-0">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-succession-text-secondary">{_categorySymbol}</span>
                  <input
                    type="text"
                    value={Math.abs(Number(nonOperatingIncomeNum || 0)).toLocaleString()}
                    onChange={(e) => handleNonOperatingIncomeChange(e.target.value)}
                    placeholder="0"
                    className="h-9 w-full text-sm pl-6 pr-3 text-right rounded border border-succession-border bg-succession-bg-primary text-succession-text-primary"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Allocation Section (collapsible) — hidden when only one revenue line is
            configured; that line is auto-set to 100% above. */}
        {!isSingleLine && (
        <div className="border-t border-succession-border pt-10">
          <button
            type="button"
            className="w-full flex items-center justify-between bg-succession-bg-secondary border border-succession-border rounded-lg p-4 hover:bg-succession-bg-tertiary"
            onClick={() => onFieldChange('allocationsOpen', !values.allocationsOpen)}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-succession-text-primary">Allocate Operating Expenses by Line</h3>
              <InfoIcon className="w-4 h-4 text-succession-text-secondary cursor-help" title="Allocate your total operating expenses across lines. Per-line EBITDA uses these estimates. If the total ≠ 100%, we'll show Low Confidence and hide line-level EBITDA." />
            </div>
            {values.allocationsOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
          </button>

          {values.allocationsOpen && (
            <div className="mt-4">
          <p className="text-sm text-succession-text-secondary mb-6">
            Distribute your total operating expenses across lines. These are estimates for analysis—not GAAP allocations.
          </p>

          {/* Summary Info */}
          <div className="bg-succession-bg-secondary border border-succession-border rounded-lg p-4 mb-6 space-y-2">
            <div className="flex items-center justify-between text-sm">
                  <span className="text-succession-text-secondary">Total Operating Expenses:</span>
                  <span className="font-semibold text-succession-text-primary">{fmtCurrency(allocationBaseNum)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-succession-text-secondary">Allocation Sum:</span>
              <span className={`font-semibold ${isFullyAllocated ? "text-emerald-600" : "text-amber-600"}`}>
                {sumPercent.toFixed(2)}% of 100%
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-succession-text-secondary">Confidence:</span>
              <span
                className={`inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                  confidence === "high"
                    ? "bg-emerald-600 text-white border border-emerald-700"
                    : "bg-amber-600 text-white border border-amber-700"
                }`}
                    style={{ backgroundColor: confidence === "high" ? "#059669" : "#d97706", color: "#ffffff" }}
              >
                    {confidence === "high" ? intl.formatMessage({ id: 'succession.multi_line_valuation.high_confidence' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.low_confidence' })}
              </span>
            </div>
          </div>

          {!isFullyAllocated && (
            <div 
              className="rounded-lg p-4 mb-6 flex items-start gap-3"
                  style={{ backgroundColor: '#fef3c7', border: '2px solid #fbbf24', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
            >
              <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#92400e' }} />
              <p className="text-sm font-semibold" style={{ color: '#78350f' }}>
                Operating expenses will be hidden until total equals 100%.
              </p>
            </div>
          )}

              {!hasAnyOperatingExpenses && (
            <div className="bg-succession-bg-secondary border border-succession-border rounded-lg p-6 text-center mb-6">
              <p className="text-sm text-succession-text-secondary">
                    No operating expenses entered yet. You can set the allocation % now; dollar amounts will populate once you add expenses above.
              </p>
            </div>
          )}

          {!isDisabled && (
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                type="button"
                onClick={handleAutoAllocateByRevenue}
                className="px-3 py-1.5 rounded border border-succession-border text-xs text-succession-text-primary hover:bg-succession-bg-tertiary inline-flex items-center"
              >
                <TrendingUpIcon className="w-3 h-3 mr-1.5" />
                {intl.formatMessage({ id: 'succession.multi_line_valuation.auto_allocate_by_revenue' })}
              </button>
              <button
                type="button"
                onClick={handleEqualSplit}
                className="px-3 py-1.5 rounded border border-succession-border text-xs text-succession-text-primary hover:bg-succession-bg-tertiary inline-flex items-center"
              >
                <EqualIcon className="w-3 h-3 mr-1.5" />
                {intl.formatMessage({ id: 'succession.multi_line_valuation.equal_split' })}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-3 py-1.5 rounded border border-succession-border text-xs text-succession-text-primary hover:bg-succession-bg-tertiary inline-flex items-center"
              >
                <RotateCcwIcon className="w-3 h-3 mr-1.5" />
                {intl.formatMessage({ id: 'succession.multi_line_valuation.reset_to_zero' })}
              </button>
              {sumPercent > 0 && sumPercent !== 100 && (
                <button
                  type="button"
                  onClick={handleNormalize}
                  className="px-3 py-1.5 rounded border border-succession-border text-xs text-succession-text-primary hover:bg-succession-bg-tertiary inline-flex items-center"
                >
                  <TargetIcon className="w-3 h-3 mr-1.5" />
                  {intl.formatMessage({ id: 'succession.multi_line_valuation.normalize_to_100' })}
                </button>
              )}
            </div>
          )}

          <div className="space-y-6">
            {BUSINESS_LINES.filter((line) => effectiveLineIds.includes(line.id)).map((line) => (
              <ExpenseAllocationRow
                key={line.id}
                line={line.id}
                lineLabel={line.label}
                percent={allocations[line.id] || 0}
                    allocatedAmount={allocationBaseNum * ((allocations[line.id] || 0) / 100)}
                revenueShare={revenueShares[line.id]}
                disabled={isDisabled}
                onChange={(percent) => handleAllocationChange(line.id, percent)}
              />
            ))}
          </div>
        </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

// EBITDA Bridge Card Component
const EBITDABridgeCard = ({
  reportedEBITDA = 0,
  adjustedEBITDA = 0,
  adjustments = [],
  className = ""
}) => {
  const intl = useIntl();
  const { format: _currencyFormat } = useCurrency();
  const fmtCurrency = (n) => _currencyFormat(Number(n || 0), { maximumFractionDigits: 0 });

  const getAdjustmentLabel = (type) => {
    switch (type) {
      case 'ownerExcessComp': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_owner_excess_comp' });
      case 'oneTimeExpenses': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_one_time_expenses' });
      case 'nonOperatingIncome': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_non_operating_income' });
      case 'other': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_other_adjustment' });
      default: return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_adjustment' });
    }
  };
  
  const impact = adjustedEBITDA - reportedEBITDA;
  const impactPercent = reportedEBITDA > 0 ? (impact / reportedEBITDA) * 100 : 0;
  
  return (
    <div className={`bg-succession-bg-secondary border border-succession-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-succession-text-primary">
          {intl.formatMessage({ id: 'succession.multi_line_valuation.ebitda_bridge_title' })}
        </h3>
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          impact >= 0 ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {impact >= 0 ? '+' : ''}{impactPercent.toFixed(1)}% Impact
        </span>
      </div>
      
      <div className="space-y-4">
        {/* Reported EBITDA */}
        <div className="flex items-center justify-between pb-4 border-b border-succession-border">
          <span className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.reported_ebitda' })}</span>
          <span className="text-lg font-semibold text-succession-text-primary">{fmtCurrency(reportedEBITDA)}</span>
        </div>
        
        {/* Adjustments */}
        {adjustments.map((adj, idx) => {
          const amount = Number(adj.amount || 0);
          const treatment = adj.treatment || (adj.type === 'nonOperatingIncome' ? 'subtract' : 'add');
          const isAdd = treatment === 'add';
          const sign = isAdd ? '+' : '-';
          
          return (
            <div key={adj.id || idx} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                  isAdd ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
                }`}>
                  {sign}
                </span>
                <span className="text-sm text-succession-text-primary">{adj.label || getAdjustmentLabel(adj.type)}</span>
              </div>
              <span className={`text-sm font-medium ${isAdd ? 'text-emerald-600' : 'text-red-600'}`}>
                {sign}{fmtCurrency(amount)}
              </span>
            </div>
          );
        })}
        
        {/* Net Adjustment */}
        {adjustments.length > 0 && (
          <div className="pt-2 border-t border-succession-border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.net_adjustment' })}</span>
              <span className={`text-sm font-semibold ${impact >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {impact >= 0 ? '+' : ''}{fmtCurrency(impact)} ({impact >= 0 ? '+' : ''}{impactPercent.toFixed(1)}%)
              </span>
            </div>
          </div>
        )}
        
        {/* Adjusted EBITDA */}
        <div className="flex items-center justify-between pt-4 border-t-2 border-succession-text-primary/80">
          <span className="text-base font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.adjusted_ebitda' })}</span>
          <span className="text-2xl font-bold text-succession-text-primary">{fmtCurrency(adjustedEBITDA)}</span>
        </div>
      </div>
    </div>
  );
};

const MultiLineValuation = ({ loading, error, result, scenarios = [], initialInputs = { wealth: {}, life: {}, pc: {}, group: {} }, initialExpenses = null, allowManualGrowth = false, currentUser = {}, viewerIsPrivileged = false, viewerUser = null, terminology = null, validationData = null, loadedScenarioId = null, loadedScenarioName = null, saving = false, updating = false, readOnly = false, exportUserId = null, onRecalculate, onSave, onUpdate, onLoadScenario, onClearScenario, quickbooksConnected = false, quickbooksConnectorEnabled = false, quickbooksConnectUrl = null, quickbooksSyncUrl = null }) => {
  const intl = useIntl();
  if (loading) return <div>{intl.formatMessage({ id: 'succession.multi_line_valuation.loading' })}</div>;
  if (error) return <div>{intl.formatMessage({ id: 'succession.multi_line_valuation.error_loading' })}</div>;

  const combined = result?.combined || {};
  const lines = result?.lines || [];
  const weights = result?.weights || {};
  const features = result?.features || {};
  const methodEnabled = useMemo(() => {
    const fallback = {
      mor: Number(weights.mor || 0) > 0,
      dcf: Number(weights.dcf || 0) > 0,
      ebitda: Number(weights.ebitda || 0) > 0,
      asset: Number(weights.asset || 0) > 0,
    };
    return {
      mor: typeof features.mor === 'boolean' ? features.mor : fallback.mor,
      dcf: typeof features.dcf === 'boolean' ? features.dcf : fallback.dcf,
      ebitda: typeof features.ebitda === 'boolean' ? features.ebitda : fallback.ebitda,
      asset: typeof features.asset === 'boolean' ? features.asset : fallback.asset,
    };
  }, [features, weights]);
  const enabledLabels = useMemo(() => {
    const labels = [];
    if (methodEnabled.mor) labels.push(intl.formatMessage({ id: 'succession.multi_line_valuation.method_mor' }));
    if (methodEnabled.dcf) labels.push(intl.formatMessage({ id: 'succession.multi_line_valuation.method_dcf' }));
    if (methodEnabled.ebitda) labels.push(intl.formatMessage({ id: 'succession.multi_line_valuation.method_ebitda' }));
    if (methodEnabled.asset) labels.push(intl.formatMessage({ id: 'succession.multi_line_valuation.method_asset_based' }));
    return labels;
  }, [methodEnabled, intl]);

  // Per-line enabled map driven by feature flags. Used to hide rows for
  // disabled lines of business in DCFProjectionsTable (and similar tables).
  const enabledLinesMap = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    const has = (key) => Array.isArray(featureTypes) && featureTypes.includes(key);
    const map = {
      wealth: has('multi_line_valuation_wealth_management'),
      life: has('multi_line_valuation_life_insurance'),
      pc: has('multi_line_valuation_p_and_c'),
      group: has('multi_line_valuation_group'),
    };
    if (!map.wealth && !map.life && !map.pc && !map.group) {
      map.wealth = true; // mirror enabledLineTabs fallback
    }
    return map;
  }, [currentUser, viewerUser]);

  // Optional Wealth Business Info fields (AUM, client households), each gated by its own feature flag.
  const wealthExtraFields = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    const has = (key) => Array.isArray(featureTypes) && featureTypes.includes(key);
    return {
      showAum: has('multi_line_valuation_aum'),
      showClientCount: has('multi_line_valuation_client_count'),
    };
  }, [currentUser, viewerUser]);

  // Determine which revenue line tabs to show based on feature flags
  const enabledLineTabs = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    const lineFeatureMap = {
      wealth: 'multi_line_valuation_wealth_management',
      life: 'multi_line_valuation_life_insurance',
      pc: 'multi_line_valuation_p_and_c',
      group: 'multi_line_valuation_group',
    };
    const lineTabs = [
      { id: 'wealth', label: intl.formatMessage({ id: 'succession.multi_line_valuation.tab_wealth' }) },
      { id: 'life', label: intl.formatMessage({ id: 'succession.multi_line_valuation.tab_life' }) },
      { id: 'pc', label: intl.formatMessage({ id: 'succession.multi_line_valuation.tab_pc' }) },
      { id: 'group', label: intl.formatMessage({ id: 'succession.multi_line_valuation.tab_group' }) },
    ];
    const enabledLines = lineTabs.filter((tab) =>
      Array.isArray(featureTypes) && featureTypes.includes(lineFeatureMap[tab.id])
    );
    // If no lines are enabled, default to wealth only
    const finalLines = enabledLines.length > 0 ? enabledLines : [{ id: 'wealth', label: intl.formatMessage({ id: 'succession.multi_line_valuation.tab_wealth' }) }];
    // Always include expenses tab at the end
    return [...finalLines, { id: 'expenses', label: intl.formatMessage({ id: 'succession.multi_line_valuation.tab_expenses' }) }];
  }, [currentUser, viewerUser]);

  // If the benchmark toggle is available for this tenant/user, we default it ON in the Expenses tab.
  // (We still allow scenarios to override via initialExpenses.useBenchmark.)
  const benchmarkToggleAvailable = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const employeeBasedAdvisors = !!company?.employee_based_advisors;
    const featureTypes = company?.feature_types || [];
    const hasRjasBenchmarkExpenses = Array.isArray(featureTypes) && featureTypes.includes('rjas_benchmark_expenses');
    return !employeeBasedAdvisors || hasRjasBenchmarkExpenses;
  }, [currentUser, viewerUser]);

  const joinWithAnd = (arr) => {
    if (arr.length <= 1) return arr.join('');
    if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
    return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
  };

  const [tab, setTab] = useState(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    const hasSummaryFeature = Array.isArray(featureTypes) && featureTypes.includes('multi_line_valuation_summary');
    if (hasSummaryFeature) return 'summary';
    const defaultMethodTab = ['mor', 'dcf', 'ebitda', 'asset'].find((method) => Number(weights?.[method] || 0) > 0);
    return defaultMethodTab || 'mor';
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(() => {
    // Initialize to first enabled line tab
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    const lineFeatureMap = {
      wealth: 'multi_line_valuation_wealth_management',
      life: 'multi_line_valuation_life_insurance',
      pc: 'multi_line_valuation_p_and_c',
      group: 'multi_line_valuation_group',
    };
    const lineOrder = ['wealth', 'life', 'pc', 'group'];
    const firstEnabled = lineOrder.find((line) => 
      Array.isArray(featureTypes) && featureTypes.includes(lineFeatureMap[line])
    );
    return firstEnabled || 'wealth';
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [rateBreakdown, setRateBreakdown] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [chatConversationId, setChatConversationId] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const canViewDiscountRateBreakdown = Boolean(
    viewerIsPrivileged ||
    currentUser?.is_admin ||
    currentUser?.admin ||
    currentUser?.customer ||
    currentUser?.staff ||
    currentUser?.is_customer ||
    currentUser?.is_staff
  );

  // Check if multi-line valuation chat agent feature is enabled
  const hasChatFeature = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    return Array.isArray(featureTypes) && featureTypes.includes('multi_line_valuation_chat_agent');
  }, [currentUser, viewerUser]);

  // Check if industry benchmark feature is enabled
  const hasIndustryBenchmarkFeature = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    return Array.isArray(featureTypes) && featureTypes.includes('multi_line_valuation_industry_benchmark');
  }, [currentUser, viewerUser]);

  // Check if graphs tab feature is enabled
  const hasGraphsFeature = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    return Array.isArray(featureTypes) && featureTypes.includes('multi_line_valuation_graphs');
  }, [currentUser, viewerUser]);

  // Check if confidence ratings feature is enabled
  const hasConfidenceFeature = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    return Array.isArray(featureTypes) && featureTypes.includes('multi_line_valuation_confidence_ratings');
  }, [currentUser, viewerUser]);

  // Check if summary tab feature is enabled
  const hasSummaryTabFeature = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    return Array.isArray(featureTypes) && featureTypes.includes('multi_line_valuation_summary');
  }, [currentUser, viewerUser]);

  // DCF valuation range: present the DCF as a floor→ceiling range (1× trailing
  // 12-month gross production up to the DCF value) instead of a point estimate
  // with a ±10% band. Reintroduces the legacy range for RJ Modernization.
  const hasDcfRangeFeature = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    return Array.isArray(featureTypes) && featureTypes.includes('multi_line_valuation_dcf_range');
  }, [currentUser, viewerUser]);

  // 1× trailing 12-month gross production — the low end of the DCF range.
  //
  // Taken from the DCF's own revenue base rather than the stored `gross_production`.
  // The two agree on an untouched record (the stored field is the sum of the stored
  // revenue inputs) but diverge the moment revenue is edited in the Valuation Inputs
  // panel, which the stored field never sees. Reading the stored field paired a DCF
  // computed from the edited inputs against a floor from the old ones — an advisor
  // whose entered T12 was $137,121 saw a range of $443.5K – $23.3M, the ceiling being
  // their untouched stored revenue. Mirrors MultiLineValuationService#dcf_range_snapshot.
  const grossProductionFloor = useMemo(() => {
    const fromResult = (result?.lines || []).reduce(
      (sum, l) => sum + Number(l?.methods?.dcf?.baseRevenue || 0),
      0
    );
    if (fromResult > 0) return fromResult;
    return Number(viewerUser?.gross_production ?? currentUser?.gross_production ?? 0);
  }, [result, currentUser, viewerUser]);

  // Clarify that T12 revenue is a fiscal year-end snapshot, not month-end
  // (feed-driven tenants: RJA/ICD).
  const hasT12YearEndNote = useMemo(() => {
    const company = viewerUser?.company || currentUser?.company;
    const featureTypes = company?.feature_types || [];
    return Array.isArray(featureTypes) && featureTypes.includes('valuation_t12_year_end_note');
  }, [currentUser, viewerUser]);
  const t12YearEndNote = hasT12YearEndNote
    ? intl.formatMessage({ id: 'succession.multi_line_valuation.t12_year_end_note' })
    : null;

  const enabledMethodTabIds = useMemo(() => {
    const tabs = [];
    if (methodEnabled.mor) tabs.push('mor');
    if (methodEnabled.dcf) tabs.push('dcf');
    if (methodEnabled.ebitda) tabs.push('ebitda');
    if (methodEnabled.asset) tabs.push('asset');
    if (hasGraphsFeature) tabs.push('graphs');
    return tabs;
  }, [methodEnabled, hasGraphsFeature]);

  React.useEffect(() => {
    const availableTabs = hasSummaryTabFeature ? ['summary', ...enabledMethodTabIds] : enabledMethodTabIds;
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0] || 'mor');
    }
  }, [hasSummaryTabFeature, enabledMethodTabIds, tab]);

  // Get industry benchmark data from result
  const industryBenchmark = useMemo(() => {
    if (!hasIndustryBenchmarkFeature) return null;
    return result?.summary_metrics?.industry_benchmark || null;
  }, [hasIndustryBenchmarkFeature, result?.summary_metrics?.industry_benchmark]);
  const [ebitdaViewMode, setEbitdaViewMode] = useState('adjusted'); // 'reported' | 'adjusted'
  const [dcfViewMode, setDcfViewMode] = useState('adjusted'); // 'reported' | 'adjusted'
  const [summaryViewMode, setSummaryViewMode] = useState('adjusted'); // 'reported' | 'adjusted'
  // Controls whether we hydrate local inputs from incoming initialInputs
  const hydrateFromInitialRef = React.useRef(true);
  const drawerScrollRef = React.useRef(null);
  const lastDrawerScrollTopRef = React.useRef(0);
  const rememberDrawerScroll = React.useCallback(() => {
    const el = drawerScrollRef.current;
    if (el) lastDrawerScrollTopRef.current = el.scrollTop;
  }, []);
  React.useLayoutEffect(() => {
    const el = drawerScrollRef.current;
    if (el) el.scrollTop = lastDrawerScrollTopRef.current;
  }, [lineInputs, expensesInputs]);

  // Ensure drawerTab is always set to an enabled tab
  React.useEffect(() => {
    const enabledIds = enabledLineTabs.map((t) => t.id);
    if (!enabledIds.includes(drawerTab)) {
      setDrawerTab(enabledIds[0] || 'expenses');
    }
  }, [enabledLineTabs, drawerTab]);

  const [expensesInputs, setExpensesInputs] = useState(() => {
    const expenseGrowthRate = Number.isFinite(currentUser?.expense_growth_rate) 
      ? (currentUser.expense_growth_rate > 1 ? currentUser.expense_growth_rate / 100 : currentUser.expense_growth_rate)
      : 0.02;
    // Do not seed meaningful defaults for first-time valuation visits.
    // Only hydrate operatingExpenses from scenario-provided inputs (initialExpenses), or benchmark mode (handled in ExpensesForm).
    const legacyOperatingExpenses = initialExpenses?.operatingExpenses !== undefined ? initialExpenses.operatingExpenses : 0;
    const seededCategories = (() => {
      const incoming = initialExpenses?.operatingExpenseCategories || initialExpenses?.operating_expense_categories || [];
      if (Array.isArray(incoming) && incoming.length) return incoming;
      const amt = Number(legacyOperatingExpenses || 0);
      if (!amt) return [];
      // Back-compat: if we only have a legacy total, put it into "other" until user breaks it out by category.
      return [{ key: 'other', label: 'Other Expenses (Taxes, Licenses, etc.)', description: 'Miscellaneous operating expenses', amount: amt, adjustments: [] }];
    })();
    return {
      directExpenses: initialExpenses?.directExpenses !== undefined ? initialExpenses.directExpenses : null,
      operatingExpenses: legacyOperatingExpenses,
      operatingExpenseCategories: seededCategories,
      otherAdjustments: initialExpenses?.otherAdjustments || initialExpenses?.other_adjustments || { nonOperatingIncome: initialExpenses?.nonOperatingIncome || 0 },
      expenseGrowthRate: initialExpenses?.expenseGrowthRate !== undefined ? initialExpenses.expenseGrowthRate : expenseGrowthRate,
      // Mirror legacy RjEbocEstimate#overhead_expenses: benchmark mode is used only
      // when the per-user use_benchmark_expense_percentage flag is set AND the tenant's
      // feature config allows it (benchmarkToggleAvailable == !employee_based_advisors
      // || rjas_benchmark_expenses). Defaulting to `true` whenever the toggle was
      // available forced benchmark on for users whose legacy config had it off (drift:
      // legacy showed gross_expenses, multi-line showed the benchmark total). Saved
      // scenarios still override via initialExpenses.useBenchmark.
      useBenchmark: initialExpenses?.useBenchmark !== undefined
        ? initialExpenses.useBenchmark
        : (benchmarkToggleAvailable && !!currentUser?.use_benchmark_expense_percentage),
      allocations: initialExpenses?.allocations || {
        wealth: 0,
        life: 0,
        pc: 0,
        group: 0,
      },
      allocationsOpen: initialExpenses?.allocationsOpen || false,
      normalizingAdjustments: initialExpenses?.normalizingAdjustments || [],
    };
  });
  
  React.useEffect(() => {
    // Hydrate expenses from initialExpenses when explicitly allowed (scenario load)
    // Also hydrate when initialExpenses changes from null to a value (scenario loaded)
    if (!initialExpenses) return;
    if (!hydrateFromInitialRef.current && initialExpenses) {
      // If initialExpenses was just set (scenario loaded), allow hydration
      hydrateFromInitialRef.current = true;
    }
    if (!hydrateFromInitialRef.current) return;
    setExpensesInputs((prev) => ({
      ...prev,
      directExpenses: initialExpenses.directExpenses !== undefined ? initialExpenses.directExpenses : prev.directExpenses,
      operatingExpenses: initialExpenses.operatingExpenses !== undefined ? initialExpenses.operatingExpenses : prev.operatingExpenses,
      operatingExpenseCategories: (() => {
        const incoming = initialExpenses.operatingExpenseCategories || initialExpenses.operating_expense_categories;
        if (Array.isArray(incoming) && incoming.length) return incoming;
        const legacy = initialExpenses.operatingExpenses !== undefined ? Number(initialExpenses.operatingExpenses || 0) : Number(prev.operatingExpenses || 0);
        if (!legacy) return prev.operatingExpenseCategories || [];
        return [{ key: 'other', label: 'Other Expenses (Taxes, Licenses, etc.)', description: 'Miscellaneous operating expenses', amount: legacy, adjustments: [] }];
      })(),
      otherAdjustments: initialExpenses.otherAdjustments || initialExpenses.other_adjustments || prev.otherAdjustments || { nonOperatingIncome: initialExpenses?.nonOperatingIncome || 0 },
      expenseGrowthRate: initialExpenses.expenseGrowthRate !== undefined ? initialExpenses.expenseGrowthRate : prev.expenseGrowthRate,
      useBenchmark: initialExpenses.useBenchmark !== undefined ? initialExpenses.useBenchmark : prev.useBenchmark,
      allocations: initialExpenses.allocations || prev.allocations,
      allocationsOpen: initialExpenses.allocationsOpen !== undefined ? initialExpenses.allocationsOpen : prev.allocationsOpen,
      normalizingAdjustments: initialExpenses.normalizingAdjustments || prev.normalizingAdjustments,
    }));
  }, [initialExpenses]);
  const [lineInputs, setLineInputs] = useState({
    wealth: initialInputs.wealth || {},
    life: initialInputs.life || {},
    pc: initialInputs.pc || {},
    group: initialInputs.group || {},
  });
  React.useEffect(() => {
    // Hydrate from external inputs only when explicitly allowed (initial mount or scenario load)
    if (!hydrateFromInitialRef.current) return;
    setLineInputs({
      wealth: initialInputs.wealth || {},
      life: initialInputs.life || {},
      pc: initialInputs.pc || {},
      group: initialInputs.group || {},
    });
  }, [initialInputs]);

  React.useEffect(() => {
    // When the drawer is opened, only hydrate from initial inputs if local state is empty
    if (!drawerOpen || hasChanges) return;
    if (!hydrateFromInitialRef.current) return;
    const hasAnyLocal = ['wealth', 'life', 'pc', 'group'].some((id) => {
      const obj = (lineInputs && lineInputs[id]) || {};
      return Object.keys(obj).length > 0;
    });
    if (hasAnyLocal) return;
    setLineInputs({
      wealth: initialInputs.wealth || {},
      life: initialInputs.life || {},
      pc: initialInputs.pc || {},
      group: initialInputs.group || {},
    });
  }, [drawerOpen, initialInputs, hasChanges, lineInputs]);
  // (Removed unused stable* snapshots)

  const { code: _valCurrencyCode, locale: _valCurrencyLocale, symbol: _valCurrencySymbol } = useCurrency();
  const ValCurrencyIcon = _valCurrencyCode === 'GBP' ? PoundSterling : DollarSign;
  const fmtCurrency0 = useMemo(() => new Intl.NumberFormat(_valCurrencyLocale, {
    style: 'currency',
    currency: _valCurrencyCode,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }), [_valCurrencyCode, _valCurrencyLocale]);
  const fmt = useCallback((v) => fmtCurrency0.format(Number(v || 0)), [fmtCurrency0]);
  const fmtShort = useCallback((v) => {
    const n = Number(v || 0);
    const abs = Math.abs(n);
    const prefix = n < 0 ? '-' : '';
    const formatWithUnit = (value, unitDiv, unitLabel) => {
      const base = abs / unitDiv;
      const rounded = Math.round(base * 10) / 10; // one decimal
      const asStr = Number.isInteger(rounded) ? String(rounded.toFixed(0)) : String(rounded.toFixed(1));
      return `${prefix}${_valCurrencySymbol}${asStr} ${unitLabel}`;
    };
    if (abs >= 1e9) return formatWithUnit(n, 1e9, 'B');
    if (abs >= 1e6) return formatWithUnit(n, 1e6, 'M');
    if (abs >= 1e3) return formatWithUnit(n, 1e3, 'K');
    return fmt(n);
  }, [fmt, _valCurrencySymbol]);
  // (Removed unused fmtSmall)

  // Build clientState for chat agent
  const chatClientState = useMemo(() => ({
    wealth: lineInputs.wealth || {},
    life: lineInputs.life || {},
    pc: lineInputs.pc || {},
    group: lineInputs.group || {},
    expenses: expensesInputs,
    valuationResult: result
  }), [lineInputs, expensesInputs, result]);

  // Calculate confidence rating based on data quality
  const confidenceRating = useMemo(() => {
    if (!hasConfidenceFeature) return null;
    const normalizingAdjustments = expensesInputs?.normalizingAdjustments || [];
    const factors = gatherConfidenceFactors(lineInputs, expensesInputs, normalizingAdjustments);
    
    // Get custom weights from company settings (defaults to balanced weighting of 1 for each)
    const company = viewerUser?.company || currentUser?.company;
    const customWeights = {
      revenue: company?.confidence_revenue_weight ?? 1,
      expenses: company?.confidence_expenses_weight ?? 1,
      normalization: company?.confidence_normalization_weight ?? 1,
    };
    
    const rating = calculateConfidence(factors, customWeights);
    
    // Add expert validation if available AND the user is viewing the validated scenario
    // The badge should only show when viewing the specific scenario that was validated
    const validatedScenarioId = validationData?.validated_scenario_id;
    const isViewingValidatedScenario = validatedScenarioId && 
      String(loadedScenarioId) === String(validatedScenarioId);
    
    if (validationData?.is_validated && isViewingValidatedScenario) {
      rating.expertValidation = {
        isValidated: true,
        isCurrentVersion: validationData.is_current_version !== false, // Default true if not specified
        validatedBy: {
          coachName: validationData.coach_profile?.name,
          credentials: validationData.coach_profile?.credentials?.join(', '),
          coachAvatar: validationData.coach_profile?.avatar_url,
        },
        validatedAt: validationData.validated_at,
        programName: validationData.program_name,
        validationNotes: validationData.validation_notes,
      };
    }
    
    return rating;
  }, [hasConfidenceFeature, lineInputs, expensesInputs, validationData, viewerUser, currentUser, loadedScenarioId]);

  // Check if user is viewing a saved valuation scenario
  // The CTA should only show when viewing a saved scenario, not just when there's revenue data
  const isViewingSavedValuation = Boolean(loadedScenarioId);

  // DCF Series Data - Direct passthrough from backend (server-side calculations)
  // This replaces the previous ~150 lines of client-side recalculation
  const { series: dcfSeriesData, years: dcfYears } = useMemo(() => {
    const empty = { series: {}, years: [] };
    if (!result?.lines) return empty;
    
    const dcfLines = result.lines
      .map((l) => ({ id: l.line, dcf: l?.methods?.dcf || {} }))
      .filter((x) => Array.isArray(x.dcf.projections) && x.dcf.projections.length > 0);
    if (dcfLines.length === 0) return empty;

    const maxYears = Math.max(...dcfLines.map((x) => (x.dcf.projections?.length || 0)));
    const years = Array.from({ length: maxYears + 1 }, (_, i) => i); // Include Year 0

    const series = dcfLines.reduce((acc, { id, dcf }) => {
      const year0 = dcf.year0 || {};
      const proj = dcf.projections || [];
      
      // Build arrays directly from backend data (Year 0 + projections)
      const revenue = [year0.revenue || 0, ...proj.map(p => p.revenue || 0)].slice(0, years.length);
      const netRevenue = [year0.netRevenue || 0, ...proj.map(p => p.netRevenue || 0)].slice(0, years.length);
      const directArray = [year0.directExpenses || 0, ...proj.map(p => p.directExpenses || 0)].slice(0, years.length);
      const opexArray = [year0.operatingExpenses || 0, ...proj.map(p => p.operatingExpenses || 0)].slice(0, years.length);
      const ebit = [year0.ebit || 0, ...proj.map(p => p.ebit || 0)].slice(0, years.length);
      const tax = [year0.tax || 0, ...proj.map(p => p.tax || 0)].slice(0, years.length);
      const nopat = [year0.nopat || 0, ...proj.map(p => p.nopat || 0)].slice(0, years.length);
      const fcf = [year0.fcf || 0, ...proj.map(p => p.fcf || 0)].slice(0, years.length);
      const discount = [1, ...proj.map(p => p.discountFactor || 0)].slice(0, years.length);
      const pv = [year0.fcf || 0, ...proj.map(p => p.pvFcf || 0)].slice(0, years.length);

      // Use backend-calculated values for terminal value and enterprise value
      const wacc = Number(dcf.wacc || 0);
      const termG = Number.isFinite(dcf.terminalGrowthRate) ? dcf.terminalGrowthRate : 0.02;
      const terminalValue = dcf.terminalValue || 0;
      const pvTerminal = dcf.pvTerminal || 0;
      const pvSum = proj.reduce((s, p) => s + (p.pvFcf || 0), 0);
      const enterpriseValue = dcf.mid || (pvSum + pvTerminal);

      acc[id] = {
        revenue,
        netRevenue,
        direct: directArray,
        opex: opexArray,
        ebit,
        tax,
        nopat,
        fcf,
        discount,
        pv,
        growthRate: dcf.growthRate || 0,
        // 'historical' | 'manual' | 'input' | 'unavailable' (wealth only). Absent on
        // saved scenarios from before it existed, which then render as before.
        growthBasis: dcf.growthBasis || null,
        terminalGrowthRate: termG,
        wacc,
        waccComponents: dcf.waccComponents || null,
        terminalValue,
        pvTerminal,
        enterpriseValue,
        pvSum
      };
      return acc;
    }, {});

    return { series, years };
  }, [result]);

  // Track if state update came from chat (needs auto-recalculate)
  const chatUpdatePendingRef = useRef(false);

  // Handle state updates from chat agent
  const handleChatStateUpdate = useCallback((newState) => {
    let hasUpdates = false;
    if (newState.wealth) {
      setLineInputs(prev => ({ ...prev, wealth: { ...prev.wealth, ...newState.wealth } }));
      hasUpdates = true;
    }
    if (newState.life) {
      setLineInputs(prev => ({ ...prev, life: { ...prev.life, ...newState.life } }));
      hasUpdates = true;
    }
    if (newState.pc) {
      setLineInputs(prev => ({ ...prev, pc: { ...prev.pc, ...newState.pc } }));
      hasUpdates = true;
    }
    if (newState.group) {
      setLineInputs(prev => ({ ...prev, group: { ...prev.group, ...newState.group } }));
      hasUpdates = true;
    }
    if (newState.expenses) {
      setExpensesInputs(prev => ({ ...prev, ...newState.expenses }));
      hasUpdates = true;
    }
    if (hasUpdates) {
      setHasChanges(true);
      chatUpdatePendingRef.current = true;
    }
  }, []);

  // Auto-recalculate when chat updates inputs
  useEffect(() => {
    if (chatUpdatePendingRef.current && onRecalculate) {
      chatUpdatePendingRef.current = false;
      // Use setTimeout to ensure state updates have propagated
      const timer = setTimeout(() => {
        hydrateFromInitialRef.current = false;
        onRecalculate(buildLinesPayload());
        setHasChanges(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [lineInputs, expensesInputs, onRecalculate]);

  // Track previous expense values for change detection
  const prevExpensesRef = useRef(null);
  
  // Debounced auto-recalculate when expense inputs change
  // This ensures UI always shows fresh backend-calculated data
  useEffect(() => {
    // Skip during initial hydration
    if (hydrateFromInitialRef.current || !onRecalculate) return;
    // Skip if no result yet (initial load)
    if (!result?.lines?.length) return;
    // Skip if chat update is pending (handled by chat effect)
    if (chatUpdatePendingRef.current) return;
    
    // Stringify for deep comparison
    const currentExpensesStr = JSON.stringify(expensesInputs);
    const prevExpensesStr = prevExpensesRef.current;
    
    // Store current value
    prevExpensesRef.current = currentExpensesStr;
    
    // Skip if expenses haven't changed or this is the first render
    if (prevExpensesStr === null || currentExpensesStr === prevExpensesStr) return;
    
    // Debounce recalculate to avoid excessive API calls
    const timer = setTimeout(() => {
      onRecalculate(buildFullPayload());
    }, 500);
    
    return () => clearTimeout(timer);
  }, [expensesInputs, onRecalculate, result]);


  const handleSaveAs = async () => {
    const name = prompt('Scenario name?');
    if (name === null) return; // User cancelled
    if (!onSave) return;
    
    setSaveSuccess(false);
    const result = await onSave(name || `Valuation ${new Date().toLocaleDateString()}`, buildFullPayload());
    if (result?.success) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } else if (result?.error) {
      alert(`Error saving scenario: ${result.error}`);
    }
  };

  const handleSave = async () => {
    if (!onUpdate || !loadedScenarioId) return;
    
    setUpdateSuccess(false);
    const result = await onUpdate(buildFullPayload());
    if (result?.success) {
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    } else if (result?.error) {
      alert(`Error updating scenario: ${result.error}`);
    }
  };

  // Export handlers - UI and exports now use the same backend calculations
  // Recalculate is called as a safety measure to ensure session cache is fresh
  // (auto-recalculate on expense changes should already keep data in sync)
  const [exporting, setExporting] = React.useState(null); // 'pdf' | 'csv' | 'xlsx' | null
  
  const handleExport = async (format) => {
    if (exporting) return; // Prevent double-click
    
    setExporting(format);
    try {
      // Sync current inputs to session cache before export
      if (onRecalculate) {
        await onRecalculate(buildFullPayload());
      }
      // Brief delay to ensure session is saved
      await new Promise(resolve => setTimeout(resolve, 100));
      // Build the export URL. exportUserId names the advisor whose valuation is on
      // screen when an admin is viewing someone else; without it the server exports
      // the signed-in user, which is right for an advisor exporting their own.
      const params = [];
      if (exportUserId) params.push(`user_id=${encodeURIComponent(exportUserId)}`);
      if (loadedScenarioId) params.push(`scenario_id=${encodeURIComponent(loadedScenarioId)}`);
      let exportUrl = `/succession/valuation/multi/export.${format}`;
      if (params.length) {
        exportUrl += `?${params.join('&')}`;
      }
      // Open export URL in new tab
      window.open(exportUrl, '_blank');
    } catch (e) {
      console.error(`Export ${format} failed:`, e);
    } finally {
      setExporting(null);
    }
  };

  const actions = (
    <div className="flex items-center gap-2" data-tutorial-id="save-export">
      {/* Save button - only shows when a scenario is loaded */}
      {loadedScenarioId && (
        <button
          onClick={handleSave}
          disabled={updating}
          className={`px-3 py-2 rounded text-white transition-all ${
            updateSuccess 
              ? 'bg-green-600' 
              : updating 
                ? 'bg-succession-primary opacity-50 cursor-not-allowed' 
                : 'bg-succession-primary hover:opacity-90'
          }`}
          title={loadedScenarioName ? `Save changes to "${loadedScenarioName}"` : 'Save changes'}
        >
          {updating ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {intl.formatMessage({ id: 'succession.multi_line_valuation.saving' })}
            </span>
          ) : updateSuccess ? (
            <span className="flex items-center gap-2">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {intl.formatMessage({ id: 'succession.multi_line_valuation.saved' })}
            </span>
          ) : (
            intl.formatMessage({ id: 'succession.multi_line_valuation.save' })
          )}
        </button>
      )}
      
      {/* Save As button - always visible */}
      <button
        onClick={handleSaveAs}
        disabled={saving}
        className={`px-3 py-2 rounded text-white transition-all ${
          saveSuccess 
            ? 'bg-green-600' 
            : saving 
              ? 'bg-succession-accent opacity-50 cursor-not-allowed' 
              : 'bg-succession-accent hover:opacity-90'
        }`}
      >
        {saving ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {intl.formatMessage({ id: 'succession.multi_line_valuation.saving' })}
          </span>
        ) : saveSuccess ? (
          <span className="flex items-center gap-2">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            {intl.formatMessage({ id: 'succession.multi_line_valuation.saved' })}
          </span>
        ) : (
          intl.formatMessage({ id: 'succession.multi_line_valuation.save_as' })
        )}
      </button>
      <button
        onClick={() => handleExport('pdf')}
        disabled={!!exporting}
        aria-label="Export PDF"
        className={`inline-flex items-center justify-center px-3 py-2 rounded border border-succession-border text-succession-text-primary hover:bg-succession-bg-tertiary ${exporting ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {exporting === 'pdf' ? (
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <PdfIcon className="h-5 w-5" />
        )}
      </button>
      <button
        onClick={() => handleExport('csv')}
        disabled={!!exporting}
        aria-label="Export CSV"
        className={`inline-flex items-center justify-center px-3 py-2 rounded border border-succession-border text-succession-text-primary hover:bg-succession-bg-tertiary ${exporting ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {exporting === 'csv' ? (
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <CsvIcon className="h-5 w-5" />
        )}
      </button>
      <button
        onClick={() => handleExport('xlsx')}
        disabled={!!exporting}
        aria-label="Export XLSX"
        className={`inline-flex items-center justify-center px-3 py-2 rounded border border-succession-border text-succession-text-primary hover:bg-succession-bg-tertiary ${exporting ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {exporting === 'xlsx' ? (
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <XlsIcon className="h-5 w-5" />
        )}
      </button>
      <div className="ml-4">
        <SavedValuationsPopover
          scenarios={scenarios}
          currentUserId={currentUser?.id}
          currentUserAvatar={viewerUser?.profile_picture}
          loadedScenarioId={loadedScenarioId}
          loadedScenarioName={loadedScenarioName}
          validationData={validationData}
          onLoadScenario={(id) => {
            onLoadScenario && onLoadScenario(id);
            hydrateFromInitialRef.current = true;
            setHasChanges(false);
          }}
          onClearScenario={() => {
            onClearScenario && onClearScenario();
            setHasChanges(false);
            hydrateFromInitialRef.current = true;
          }}
        />
      </div>
      {hasChatFeature && (
        <Button
          onClick={() => setShowChat(true)}
          variant="primary"
          size="md"
          title={intl.formatMessage({ id: 'succession.multi_line_valuation.chat_button_title' })}
        >
          <SparklesIcon className="h-4 w-4 mr-2" />
          {intl.formatMessage({ id: 'succession.multi_line_valuation.ask_ai' })}
        </Button>
      )}
    </div>
  );

  const buildLinesPayload = () => {
    const ids = ['wealth', 'life', 'pc', 'group'];
    const totalOperatingExpenses = (() => {
      // Benchmark mode uses the single firm-level operatingExpenses (revenue-based).
      if (expensesInputs?.useBenchmark) return Number(expensesInputs?.operatingExpenses || 0);
      // Category-based OpEx: allocate based on the *reported* category totals (before normalizations).
      // Fall back to legacy `operatingExpenses` if categories haven't been entered.
      const cats = Array.isArray(expensesInputs?.operatingExpenseCategories) ? expensesInputs.operatingExpenseCategories : [];
      const fromCats = cats.reduce((sum, c) => sum + Number(c?.amount || 0), 0);
      if (fromCats) return fromCats;
      return Number(expensesInputs?.operatingExpenses || 0);
    })();
    const allocations = expensesInputs.allocations || { wealth: 0, life: 0, pc: 0, group: 0 };
    
    return ids.map((id) => {
      const inputs = { ...(lineInputs[id] || {}) };
      
      // Remove ebitdaAdjustments from line inputs (now firm-level only)
      if (inputs.ebitdaAdjustments) {
        delete inputs.ebitdaAdjustments;
      }
      
      // Calculate per-line operating expenses from allocation
      const allocatedOpEx = totalOperatingExpenses * ((allocations[id] || 0) / 100);
      inputs.operatingExpenses = allocatedOpEx;
      
      // Apply direct expenses override if set (for Wealth line only)
      if (id === 'wealth' && expensesInputs.directExpenses !== null && expensesInputs.directExpenses !== undefined) {
        inputs.directExpenses = expensesInputs.directExpenses;
      }
      
      if (id === 'pc') {
        // Map UI field names to backend override keys expected by PcRevenueDetail
        if (Object.prototype.hasOwnProperty.call(inputs, 'personalPremium')) {
          inputs.personalLinesPremium = inputs.personalPremium;
        }
        if (Object.prototype.hasOwnProperty.call(inputs, 'personalCommRate')) {
          inputs.personalLinesCommRate = inputs.personalCommRate;
        }
        if (Object.prototype.hasOwnProperty.call(inputs, 'commercialPremium')) {
          inputs.commercialLinesPremium = inputs.commercialPremium;
        }
        if (Object.prototype.hasOwnProperty.call(inputs, 'commercialCommRate')) {
          inputs.commercialLinesCommRate = inputs.commercialCommRate;
        }
      }
      return { name: id, inputs };
    });
  };

  const buildFullPayload = () => {
    const computedOperatingExpenses = (() => {
      if (expensesInputs?.useBenchmark) return Number(expensesInputs?.operatingExpenses || 0);
      const cats = Array.isArray(expensesInputs?.operatingExpenseCategories) ? expensesInputs.operatingExpenseCategories : [];
      const fromCats = cats.reduce((sum, c) => sum + Number(c?.amount || 0), 0);
      // Ensure we use the category sum if available when benchmark is OFF
      return fromCats; 
    })();
    return {
      lines: buildLinesPayload(),
      expenses: {
        directExpenses: expensesInputs.directExpenses,
        operatingExpenses: computedOperatingExpenses,
        operatingExpenseCategories: expensesInputs.operatingExpenseCategories || [],
        otherAdjustments: expensesInputs.otherAdjustments || {},
        expenseGrowthRate: expensesInputs.expenseGrowthRate,
        useBenchmark: expensesInputs.useBenchmark,
        allocations: expensesInputs.allocations,
        allocationsOpen: expensesInputs.allocationsOpen,
        normalizingAdjustments: expensesInputs.normalizingAdjustments || [],
      }
    };
  };

  // Reconcile the first server result with the inputs actually on screen.
  //
  // MultiLineValuationContainer issues the initial query with bare `defaultLines`
  // -- no inputs, no expenses -- while the Valuation Inputs panel renders from
  // values derived client-side. The gauge and the panel therefore described two
  // different valuations, and nothing reconciled them: the debounced effect above
  // deliberately skips the first render, and `hydrateFromInitialRef` stays true on
  // a plain page load. The first thing to trigger a recalculate won the argument --
  // which is why pressing Export made the headline figure visibly jump.
  //
  // Firing once here makes the invariant hold: what is displayed always came from
  // the same buildFullPayload() the exports send, so the page, the PDF, the CSV and
  // the XLSX cannot disagree. Guarded by a ref rather than by `result`, since
  // recalculating replaces `result` and would otherwise loop.
  const initialReconcileRef = useRef(false);
  useEffect(() => {
    if (initialReconcileRef.current) return;
    if (!onRecalculate) return;
    // Wait for the first server result, by which point the hydration effects above
    // have populated the panel state that buildFullPayload() reads.
    if (!result?.lines?.length) return;

    initialReconcileRef.current = true;
    onRecalculate(buildFullPayload());
  }, [result, onRecalculate]);

  const lineRows = useMemo(() => {
    const map = {};
    (result?.lines || []).forEach((l) => { map[l.line] = l; });
    return map;
  }, [result]);

  const fmtPct = useCallback((v) => `${Number(v || 0).toFixed(0)}%`, []);

  // Helper function to format line labels for display
  const formatLineLabel = useCallback((lineId) => {
    if (!lineId) return '';
    const id = String(lineId).toLowerCase();
    if (id === 'pc') return 'P&C';
    return id.charAt(0).toUpperCase() + id.slice(1);
  }, []);

  const getAdjustmentLabel = useCallback((type) => {
    switch (type) {
      case 'ownerExcessComp': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_owner_excess_comp' });
      case 'oneTimeExpenses': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_one_time_expenses' });
      case 'nonOperatingIncome': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_non_operating_income' });
      case 'other': return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_other_adjustment' });
      default: return intl.formatMessage({ id: 'succession.multi_line_valuation.adj_adjustment' });
    }
  }, [intl]);

  // Memoized calculation for owners compensation/direct expenses flag
  // This ensures consistent evaluation on refresh and when user/company data changes
  // Defensively checks for data availability before evaluating
  const ownersCompEnabledTable = useMemo(() => {
    // Wait for company data to be available
    const viewerCompany = viewerUser?.company;
    const currentCompany = currentUser?.company;
    
    // If viewerUser has company data, use it (most authoritative)
    if (viewerCompany && viewerCompany.hasOwnProperty('rj_use_owners_comp_direct_expenses')) {
      return !!viewerCompany.rj_use_owners_comp_direct_expenses;
    }
    
    // Otherwise, check currentUser company data
    if (currentCompany && currentCompany.hasOwnProperty('rj_use_owners_comp_direct_expenses')) {
      return !!currentCompany.rj_use_owners_comp_direct_expenses;
    }
    
    // Default to false if neither has the property set (more conservative)
    // This prevents showing Direct Expenses when the toggle is OFF but data hasn't loaded yet
    return false;
  }, [
    viewerUser?.company?.rj_use_owners_comp_direct_expenses,
    currentUser?.company?.rj_use_owners_comp_direct_expenses,
    viewerUser?.company,
    currentUser?.company
  ]);

  const hasAdjustments = useMemo(() => {
    const legacy = (expensesInputs.normalizingAdjustments || []).length > 0;
    const cats = Array.isArray(expensesInputs.operatingExpenseCategories) ? expensesInputs.operatingExpenseCategories : [];
    const hasCatAdj = cats.some((c) => Array.isArray(c?.adjustments) && c.adjustments.some((a) => Number(a?.amount || 0) !== 0 || String(a?.label || '').trim() !== ''));
    const nonOp = Number(expensesInputs?.otherAdjustments?.nonOperatingIncome || 0);
    const hasNonOp = Number.isFinite(nonOp) && nonOp !== 0;
    return legacy || hasCatAdj || hasNonOp;
  }, [expensesInputs.normalizingAdjustments, expensesInputs.operatingExpenseCategories, expensesInputs.otherAdjustments]);

  // Set default view mode based on adjustments existence
  React.useEffect(() => {
    if (hasAdjustments) {
      setEbitdaViewMode('adjusted');
      setDcfViewMode('adjusted');
      setSummaryViewMode('adjusted');
    }
  }, [hasAdjustments]);

  const renderMethodBreakdown = (lineId) => {
    const m = lineRows[lineId]?.methods || {};
    const items = [
      { key: 'mor', label: `MOR (${fmtPct((weights.mor || 0) * 100)})`, value: m.mor?.mid },
      { key: 'dcf', label: `DCF (${fmtPct((weights.dcf || 0) * 100)})`, value: m.dcf?.mid },
      { key: 'ebitda', label: `EBITDA (${fmtPct((weights.ebitda || 0) * 100)})`, value: m.ebitda?.mid },
      { key: 'asset', label: `Asset (${fmtPct((weights.asset || 0) * 100)})`, value: m.asset?.mid },
    ];
    const activeItems = items.filter((it) => methodEnabled[it.key]);
    return (
      <div className="rounded-md border border-succession-border p-3">
        <div className="text-sm font-medium mb-2">Method breakdown (mid)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {activeItems.map((it) => (
            <div key={it.key}>
              <div className="text-succession-text-secondary">{it.label}</div>
              <div className="font-medium">{fmt(it.value)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAppliedFactors = (lineId) => {
    const list = lineRows[lineId]?.factors || [];
    return (
      <div className="rounded-md border border-succession-border p-3">
        <div className="text-sm font-medium mb-2">Applied factors</div>
        {list.length === 0 ? (
          <div className="text-xs text-succession-text-secondary">No factors applied.</div>
        ) : (
          <ul className="text-sm space-y-1">
            {list.map((f) => (
              <li key={f.id} className="flex items-center justify-between">
                <span className="text-succession-text-secondary">{f.name}</span>
                <span className={f.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}>{fmt(f.delta)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderInputPreview = (lineId) => {
    const inputs = lineInputs[lineId] || {};
    const rows = [];
    const pushRow = (k, v) => rows.push({ k, v });
    Object.keys(inputs).forEach((k) => {
      // Skip ebitdaAdjustments as they're now firm-level
      if (k === 'ebitdaAdjustments') return;
      pushRow(k, inputs[k]);
    });
    const isPercentKey = (name) => /rate|percent|retention|growth/i.test(name);
    const formatVal = (name, val) => {
      const num = Number(val || 0);
      if (!Number.isFinite(num)) return String(val || '');
      return isPercentKey(name) ? `${(num * 100).toFixed(1)}%` : fmt(num);
    };
    return (
      <div className="rounded-md border border-succession-border p-3">
        <div className="text-sm font-medium mb-2">Input preview</div>
        {rows.length === 0 ? (
          <div className="text-xs text-succession-text-secondary">No overrides applied.</div>
        ) : (
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {rows.map((r) => (
              <div key={r.k} className="flex items-center justify-between">
                <span className="text-succession-text-secondary truncate pr-2">{r.k}</span>
                <span className="font-medium">{formatVal(r.k, r.v)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLineCallout = (lineId, label) => {
    const ln = lineRows[lineId];
    const isZero = !ln || !ln.adjusted || ([ln.adjusted.low, ln.adjusted.mid, ln.adjusted.high].every((v) => Number(v || 0) === 0));
    if (!isZero) return null;
    return (
      <div className="mt-3 rounded-md border border-succession-border bg-succession-bg-tertiary text-succession-text-primary p-3">
        <div className="text-sm">{label} is enabled but has insufficient inputs. Add inputs to include it in the valuation.</div>
      </div>
    );
  };

  const updateLine = (lineId, field, value) => {
    setLineInputs((prev) => ({ ...prev, [lineId]: { ...(prev[lineId] || {}), [field]: value } }));
  };

  const renderLinePanel = (lineId, label) => (
    <section className="mt-4 space-y-3">
      <h3 className="text-lg font-semibold">{label}</h3>
      {renderLineCallout(lineId, label)}
      {renderInlineInputs(lineId)}
      <div className="rounded-md border border-succession-border overflow-x-auto">
        <table className="min-w-full table-auto text-sm">
          <thead className="bg-succession-bg-tertiary">
            <tr>
              <th className="px-3 py-2 text-left text-succession-text-secondary font-medium">Low</th>
              <th className="px-3 py-2 text-left text-succession-text-secondary font-medium">Mid</th>
              <th className="px-3 py-2 text-left text-succession-text-secondary font-medium">High</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2">{fmt(lineRows[lineId]?.adjusted?.low)}</td>
              <td className="px-3 py-2">{fmt(lineRows[lineId]?.adjusted?.mid)}</td>
              <td className="px-3 py-2">{fmt(lineRows[lineId]?.adjusted?.high)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {renderMethodBreakdown(lineId)}
        {renderAppliedFactors(lineId)}
        {renderInputPreview(lineId)}
      </div>
    </section>
  );

  const renderInlineInputs = (lineId) => {
    const values = lineInputs[lineId] || {};
    const currentYear = (typeof window !== 'undefined' ? new Date().getFullYear() : (new Date()).getFullYear());
    const setVal = (key, raw) => {
      const num = (v) => {
        const n = typeof v === 'string' ? parseFloat(v) : v;
        return Number.isFinite(n) ? n : 0;
      };
      const pct = (v) => {
        const n = num(v);
        return n / 100;
      };
      const setByPath = (obj, path, val) => {
        const parts = path.split('.');
        const head = parts[0];
        const tail = parts.slice(1);
        if (tail.length === 0) {
          obj[head] = val;
          return obj;
        }
        const next = { ...(obj[head] || {}) };
        obj[head] = setByPath(next, tail.join('.'), val);
        return obj;
      };
      return (eOrValue) => {
        // Support both DOM event and raw value (from programmatic calls)
        const v = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
        const keyLower = key.toLowerCase();
        const isPercent = /rate|growth|percent|retention/.test(keyLower);
        const converted = isPercent ? pct(v) : num(v);
        rememberDrawerScroll();
        setLineInputs((prev) => {
          const current = { ...(prev[lineId] || {}) };
          const next = setByPath(current, key, converted);
          if (!allowManualGrowth && keyLower.startsWith('history.')) {
            // recompute growth from history values if available
            const h = next.history || {};
            const arr = [h.y1, h.y2, h.y3, h.y4, h.y5].map((x) => Number(x || 0)).filter((x) => x);
            if (arr.length >= 2) {
              const growths = [];
              for (let i = 0; i < arr.length - 1; i++) {
                const c = arr[i];
                const n2 = arr[i + 1];
                if (c) growths.push((c - n2) / c);
              }
              if (growths.length) {
                // Divide by arr.length (# of data points) to match backend rj_gross_revenue_detail.rb
                const avg = growths.reduce((s, x) => s + x, 0) / arr.length;
                next.growth = avg;
              }
            }
          }
          return { ...prev, [lineId]: next };
        });
        setHasChanges(true);
      };
    };

    const setCurrencyVal = (key) => {
      const num = (v) => {
        if (v === undefined || v === null || v === '') return 0;
        const cleaned = typeof v === 'string' ? v.replace(/,/g, '') : v;
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : 0;
      };
      const setByPath = (obj, path, val) => {
        const parts = path.split('.');
        const head = parts[0];
        const tail = parts.slice(1);
        if (tail.length === 0) {
          obj[head] = val;
          return obj;
        }
        const next = { ...(obj[head] || {}) };
        obj[head] = setByPath(next, tail.join('.'), val);
        return obj;
      };
      return (value) => {
        const keyLower = key.toLowerCase();
        const converted = num(value);
        rememberDrawerScroll();
        setLineInputs((prev) => {
          const current = { ...(prev[lineId] || {}) };
          const next = setByPath(current, key, converted);
          if (!allowManualGrowth && keyLower.startsWith('history.')) {
            const h = next.history || {};
            const arr = [h.y1, h.y2, h.y3, h.y4, h.y5].map((x) => Number(x || 0)).filter((x) => x);
            if (arr.length >= 2) {
              const growths = [];
              for (let i = 0; i < arr.length - 1; i++) {
                const c = arr[i];
                const n2 = arr[i + 1];
                if (c) growths.push((c - n2) / c);
              }
              if (growths.length) {
                // Divide by arr.length (# of data points) to match backend rj_gross_revenue_detail.rb
                const avg = growths.reduce((s, x) => s + x, 0) / arr.length;
                next.growth = avg;
              }
            }
          }
          return { ...prev, [lineId]: next };
        });
        setHasChanges(true);
      };
    };

    const onSaveUpdate = () => {
      // Recalculate should include firm-level expenses (category-based OpEx + other adjustments)
      onRecalculate && onRecalculate(buildFullPayload());
      setHasChanges(false);
    };

    const SectionBlock = ({ title, children, top = false }) => (
      <div className={top ? "border-t border-succession-border pt-10" : ""}>
        <div className="text-base font-semibold mb-6 text-succession-text-primary">{title}</div>
        <div className="space-y-6">{children}</div>
      </div>
    );

    const Field = ({ name, label, type = 'number', step = '0.01', value }) => {
      const toNum = (x) => { const n = Number(x || 0); return Number.isFinite(n) ? n : 0; };
      const isPercent = /%/.test(label) || /rate|growth|percent|retention/i.test(name);
      const isInteger = /count|number|cases|policy/i.test(name);
      const isPolicyCount = /policy/i.test(name);
      const getInitialInputValue = (numVal) => {
        if (isPercent) {
          return `${Number(numVal || 0).toFixed(1)}%`;
        } else if (isInteger) {
          return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numVal);
        } else {
          return fmtCurrency0.format(Number(numVal || 0)).replace(/\.00$/, '');
        }
      };
      const initialNumVal = toNum(value);
      const [val, setLocal] = React.useState(initialNumVal);
      const [inputValue, setInputValue] = React.useState(getInitialInputValue(initialNumVal));
      const inputRef = React.useRef(null);
      const isFocusedRef = React.useRef(false);
      React.useEffect(() => {
        if (!isFocusedRef.current) {
          const numVal = toNum(value);
          setLocal(numVal);
          setInputValue(getInitialInputValue(numVal));
        }
      }, [value, isPercent, isInteger]);
      const min = 0;
      const max = isPercent ? 100 : (isPolicyCount ? 3000 : (isInteger ? 100000 : 5000000));
      const denom = Math.max(1, max - min);
      const pct = Math.min(100, Math.max(0, ((val - min) / denom) * 100));
      const trackBg = `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${pct}%, var(--succession-bg-tertiary, #eee) ${pct}%, var(--succession-bg-tertiary, #eee) 100%)`;
      const debRef = React.useRef(null);
      const push = (raw) => {
        if (debRef.current) clearTimeout(debRef.current);
        debRef.current = setTimeout(() => setVal(name)(raw), 120);
      };
      const onTextFocus = () => {
        isFocusedRef.current = true;
        if (isPercent) {
          setInputValue(`${Number(val || 0).toFixed(1)}%`);
        } else {
          // For currency and integer inputs, show raw number for easy editing
          setInputValue(String(val || 0));
        }
      };
      const onTextBlur = (e) => {
        isFocusedRef.current = false;
        const nv = toNum(e.target.value);
        setLocal(nv);
        if (isPercent) {
          setInputValue(`${Number(nv || 0).toFixed(1)}%`);
        } else if (isInteger) {
          setInputValue(new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(nv));
        } else {
          setInputValue(fmtCurrency0.format(Number(nv || 0)).replace(/\.00$/, ''));
        }
        push(nv);
      };
      const onTextChange = (e) => {
        const v = e.target.value;
        // Preserve cursor position and selection
        const cursorPosition = e.target.selectionStart;
        const selectionEnd = e.target.selectionEnd;
        
        setInputValue(v);
        
        // Restore cursor position after React's state update
        setTimeout(() => {
          if (inputRef.current && isFocusedRef.current) {
            const newPosition = Math.min(cursorPosition, v.length);
            const newEnd = Math.min(selectionEnd, v.length);
            if (document.activeElement === inputRef.current) {
              inputRef.current.setSelectionRange(newPosition, newEnd);
            } else {
              inputRef.current.focus();
              inputRef.current.setSelectionRange(newPosition, newEnd);
            }
          }
        }, 0);
        
        // Don't update val or call push during typing to prevent re-renders that cause focus loss
        // Only update on blur - see onTextBlur handler
      };
      return (
        <label className="text-sm block">
          <span className="text-succession-text-secondary">{label}</span>
          <div className="flex items-center gap-4 w-full mt-1">
            <input
              type="range"
              min={min}
              max={max}
              step={isInteger ? 1 : 'any'}
              value={val}
              onInput={(e)=>{ const nv = toNum(e.target.value); setLocal(nv); push(nv); }}
              onChange={(e)=>{ const nv = toNum(e.target.value); setLocal(nv); push(nv); }}
              onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
              onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
              className="mlv-range flex-1 min-w-0 h-2 rounded-lg bg-transparent"
              style={{ accentColor: 'var(--succession-primary)', background: trackBg }}
            />
            <input
              ref={inputRef}
              type="text"
              className="basis-1/4 max-w-xs text-right rounded border border-succession-border px-3 py-2"
              value={inputValue}
              onChange={onTextChange}
              onFocus={onTextFocus}
              onBlur={onTextBlur}
              onMouseDown={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
              onTouchStart={(e)=> { try { e.currentTarget.focus({ preventScroll: true }); } catch (_) { e.currentTarget.focus(); } }}
            />
          </div>
        </label>
      );
    };

    return (
      <div className="space-y-3">
        {lineId === 'life' && (
          <SectionBlock title="Business Information">
            <Field name="policyCount" label="Policy Count" value={values.policyCount} />
          </SectionBlock>
        )}
        <SectionBlock title="Revenue Information" top={lineId === 'life'}>
          {lineId === 'wealth' && (
            <>
              <Field name="feeBasedRevenue" label="Fee-based Revenue" value={values.feeBasedRevenue} />
              <Field name="trailCommissions" label="Trail Commissions" value={values.trailCommissions} />
              <Field name="transactionalCommissions" label="Transactional Commissions" value={values.transactionalCommissions} />
              <Field name="otherDirectRevenue" label="Other Direct Revenue" value={values.otherDirectRevenue} />
              <Field name="faRetentionPercent" label="FA Retention %" step="0.1" value={values.faRetentionPercent ? values.faRetentionPercent * 100 : ''} />
            </>
          )}
          {lineId === 'life' && (
            <>
              <Field name="firstYearCommissions" label="First-Year Commissions" value={values.firstYearCommissions} />
              <Field name="renewalCommissions" label="Renewal Commissions" value={values.renewalCommissions} />
              <Field name="overrideCommissions" label="Override Commissions" value={values.overrideCommissions} />
              <Field name="otherRevenue" label="Other Revenue" value={values.otherRevenue} />
              {(() => {
                const total = Number(values.firstYearCommissions || 0) + Number(values.renewalCommissions || 0) + Number(values.overrideCommissions || 0) + Number(values.otherRevenue || 0);
                return (
                  <div className="pt-4 border-t-2 border-succession-text-primary/80">
                    <div className="flex items-center justify-between">
                      <div className="text-base font-bold">Total Production</div>
                      <div className="text-2xl font-bold">{fmt(total)}</div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
          {lineId === 'pc' && (
            <>
              <Field name="personalPremium" label="Personal Lines Premium" value={values.personalPremium} />
              <Field name="personalCommRate" label="Personal Commission Rate %" step="0.1" value={values.personalCommRate ? values.personalCommRate * 100 : ''} />
              <Field name="commercialPremium" label="Commercial Lines Premium" value={values.commercialPremium} />
              <Field name="commercialCommRate" label="Commercial Commission Rate %" step="0.1" value={values.commercialCommRate ? values.commercialCommRate * 100 : ''} />
              <Field name="retentionPersonal" label="Retention - Personal %" step="0.1" value={values.retentionPersonal ? values.retentionPersonal * 100 : ''} />
              <Field name="retentionCommercial" label="Retention - Commercial %" step="0.1" value={values.retentionCommercial ? values.retentionCommercial * 100 : ''} />
            </>
          )}
          {lineId === 'group' && (
            <>
              <Field name="firstYearCommissions" label="First-Year Commissions" value={values.firstYearCommissions} />
              <Field name="renewalCommissions" label="Renewal Commissions" value={values.renewalCommissions} />
              <Field name="overrideCommissions" label="Override Commissions" value={values.overrideCommissions} />
              <Field name="otherRevenue" label="Other Revenue" value={values.otherRevenue} />
              <Field name="numberOfCases" label="Number of Cases" step="1" value={values.numberOfCases} />
              <Field name="avgCaseSize" label="Average Case Size" value={values.avgCaseSize} />
              <Field name="retentionRate" label="Retention Rate %" step="0.1" value={values.retentionRate ? values.retentionRate * 100 : ''} />
            </>
          )}
        </SectionBlock>

        <SectionBlock title="Historical Revenue" top>
          <div className="text-sm text-succession-text-secondary">Historical Revenue (Last 5 Years)</div>
          <Field name="history.y1" label={`${currentYear - 1}`} value={values.history?.y1} />
          <Field name="history.y2" label={`${currentYear - 2}`} value={values.history?.y2} />
          <Field name="history.y3" label={`${currentYear - 3}`} value={values.history?.y3} />
          <Field name="history.y4" label={`${currentYear - 4}`} value={values.history?.y4} />
          <Field name="history.y5" label={`${currentYear - 5}`} value={values.history?.y5} />
          {(() => {
            const h = values.history || {};
            const arr = [h.y1, h.y2, h.y3, h.y4, h.y5].map((x) => Number(x || 0)).filter((x) => x);
            // null = fewer than two years: unavailable, not a measured 0%.
            let pct = null;
            if (arr.length >= 2) {
              const growths = [];
              for (let i = 0; i < arr.length - 1; i++) {
                const c = arr[i];
                const n2 = arr[i + 1];
                if (c) growths.push((c - n2) / c);
              }
              // Divide by arr.length (# of data points) to match backend rj_gross_revenue_detail.rb
              if (growths.length) pct = Math.round(((growths.reduce((s, x) => s + x, 0) / arr.length) * 100) * 10) / 10;
            }
            return (
              <div className="pt-4 border-t-2 border-succession-text-primary/80">
                <div className="flex items-center justify-between">
                  <div className="text-base font-bold">Growth Rate</div>
                  <div className="text-2xl font-bold">{pct === null ? '—' : `${pct}%`}</div>
                </div>
                {pct === null && (
                  <div className="text-xs text-succession-text-secondary mt-1">
                    {intl.formatMessage({ id: 'succession.multi_line_valuation.growth_unavailable_history' })}
                  </div>
                )}
              </div>
            );
          })()}
        </SectionBlock>


        <div className="pt-2">
          <button className="px-3 py-2 rounded bg-succession-primary text-white hover:opacity-90" onClick={onSaveUpdate}>Save & Update</button>
        </div>
      </div>
    );
  };

  // Helper function to set nested state values
  const setExpensesField = (path, value) => {
    setExpensesInputs((prev) => {
      const parts = path.split('.');
      if (parts.length === 1) {
        return { ...prev, [parts[0]]: value };
      }
      const newObj = { ...prev };
      let current = newObj;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...(current[parts[i]] || {}) };
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return newObj;
    });
    setHasChanges(true);
  };

  // On first load (no scenario), if benchmark is default-on, we need to:
  // - compute the revenue-based benchmark OpEx
  // - auto-allocate by revenue
  // - trigger a one-time recalc so DCF/EBITDA include OpEx without requiring a manual click.
  const didAutoBenchmarkRecalcRef = useRef(false);
  const prevBenchmarkRef = useRef(null); // Start as null to detect first real toggle
  const benchmarkInitializedRef = useRef(false);
  
  // Track when benchmark is toggled OFF and trigger recalculate to refresh data (including tax rates)
  // Toggle ON is handled by the existing useEffect below which calculates benchmark expenses
  useEffect(() => {
    const isBenchmark = !!expensesInputs?.useBenchmark;
    
    // Skip the very first render - just initialize the ref
    if (!benchmarkInitializedRef.current) {
      prevBenchmarkRef.current = isBenchmark;
      benchmarkInitializedRef.current = true;
      return;
    }
    
    const wasBenchmark = prevBenchmarkRef.current;
    prevBenchmarkRef.current = isBenchmark;
    
    // Only trigger recalculate when toggling OFF (toggle ON is handled by the other useEffect)
    if (wasBenchmark === true && isBenchmark === false && onRecalculate && result && (result?.lines || []).length > 0) {
      // Use buildFullPayload to ensure correct expense calculations (same as manual recalculate)
      // Note: buildFullPayload reads from state/props in closure, so it's safe to call here
      onRecalculate(buildFullPayload());
    }
  }, [expensesInputs?.useBenchmark, onRecalculate, result]);
  
  useEffect(() => {
    if (!benchmarkToggleAvailable) return;
    if (!expensesInputs?.useBenchmark) { didAutoBenchmarkRecalcRef.current = false; return; }
    if (initialExpenses) return; // don't override scenario-provided expenses
    if (!result || !(result?.lines || []).length) return;
    if (didAutoBenchmarkRecalcRef.current) return;

    const currentOpEx = Number(expensesInputs?.operatingExpenses || 0);
    if (currentOpEx > 0) {
      // Already initialized (e.g., user toggled or prior flow); don't spam recalc
      didAutoBenchmarkRecalcRef.current = true;
      return;
    }

    // The benchmark total is the backend's to compute — it is the only place that
    // knows about the RJAS override. This used to recompute it from a hardcoded RJA
    // tier ladder, which is what charged RJAS advisors 41.3% instead of 19%.
    const benchOpEx = Number(result?.summary_metrics?.benchmark_operating_expenses || 0);

    // Revenue shares from Year 1 DCF projections
    const shares = (() => {
      const lines = result?.lines || [];
      const totalRevenue = lines.reduce((sum, line) => sum + Number(line?.methods?.dcf?.projections?.[0]?.revenue || 0), 0);
      const out = { wealth: 0, life: 0, pc: 0, group: 0 };
      if (totalRevenue > 0) {
        lines.forEach((line) => {
          const rev = Number(line?.methods?.dcf?.projections?.[0]?.revenue || 0);
          const share = (rev / totalRevenue) * 100;
          if (line.line === 'wealth') out.wealth = share;
          else if (line.line === 'life') out.life = share;
          else if (line.line === 'pc') out.pc = share;
          else if (line.line === 'group') out.group = share;
        });
      }
      return out;
    })();

    const sumShares = (shares.wealth || 0) + (shares.life || 0) + (shares.pc || 0) + (shares.group || 0);
    const base = sumShares > 0 ? shares : { wealth: 100, life: 0, pc: 0, group: 0 };
    const scale = sumShares > 0 ? (100 / sumShares) : 1;
    const w = parseFloat((base.wealth * scale).toFixed(2));
    const l = parseFloat((base.life * scale).toFixed(2));
    const p = parseFloat((base.pc * scale).toFixed(2));
    const g = parseFloat((100 - (w + l + p)).toFixed(2));
    const nextAllocations = { wealth: w, life: l, pc: p, group: g };

    // Update local state without marking as user "changes"
    setExpensesInputs((prev) => ({
      ...prev,
      operatingExpenses: benchOpEx,
      allocations: nextAllocations,
    }));

    // Trigger a one-time recalc so backend projections reflect benchmark OpEx
    if (onRecalculate) {
      const directExpensesOverride = expensesInputs?.directExpenses;
      const ids = ['wealth', 'life', 'pc', 'group'];
      const lines = ids.map((id) => {
        const inputs = { ...(lineInputs[id] || {}) };
        if (inputs.ebitdaAdjustments) delete inputs.ebitdaAdjustments;
        const allocatedOpEx = benchOpEx * ((nextAllocations[id] || 0) / 100);
        inputs.operatingExpenses = allocatedOpEx;
        if (id === 'wealth' && directExpensesOverride !== null && directExpensesOverride !== undefined) {
          inputs.directExpenses = directExpensesOverride;
        }
        if (id === 'pc') {
          if (Object.prototype.hasOwnProperty.call(inputs, 'personalPremium')) {
            inputs.personalLinesPremium = inputs.personalPremium;
          }
          if (Object.prototype.hasOwnProperty.call(inputs, 'personalCommRate')) {
            inputs.personalLinesCommRate = inputs.personalCommRate;
          }
          if (Object.prototype.hasOwnProperty.call(inputs, 'commercialPremium')) {
            inputs.commercialLinesPremium = inputs.commercialPremium;
          }
          if (Object.prototype.hasOwnProperty.call(inputs, 'commercialCommRate')) {
            inputs.commercialLinesCommRate = inputs.commercialCommRate;
          }
        }
        return { name: id, inputs };
      });
      hydrateFromInitialRef.current = false;
      onRecalculate({
        lines,
        expenses: {
          directExpenses: expensesInputs.directExpenses,
          operatingExpenses: benchOpEx,
          operatingExpenseCategories: expensesInputs.operatingExpenseCategories || [],
          otherAdjustments: expensesInputs.otherAdjustments || {},
          expenseGrowthRate: expensesInputs.expenseGrowthRate,
          useBenchmark: true,
          allocations: nextAllocations,
          allocationsOpen: expensesInputs.allocationsOpen,
          normalizingAdjustments: expensesInputs.normalizingAdjustments || [],
        },
      });
      setHasChanges(false);
    }

    didAutoBenchmarkRecalcRef.current = true;
  }, [benchmarkToggleAvailable, expensesInputs?.useBenchmark, expensesInputs?.operatingExpenses, initialExpenses, result, onRecalculate, currentUser, viewerUser, lineInputs]);

  const methodTotals = useMemo(() => {
    const totals = { mor: 0, dcf: 0, ebitda: 0, asset: 0 };
    (result?.lines || []).forEach((l) => {
      const m = l?.methods || {};
      if (m.mor && typeof m.mor.mid === 'number') totals.mor += m.mor.mid * (weights.mor || 0);
      if (m.dcf && typeof m.dcf.mid === 'number') totals.dcf += m.dcf.mid * (weights.dcf || 0);
      if (m.ebitda && typeof m.ebitda.mid === 'number') totals.ebitda += m.ebitda.mid * (weights.ebitda || 0);
      if (m.asset && typeof m.asset.mid === 'number') totals.asset += m.asset.mid * (weights.asset || 0);
    });
    return totals;
  }, [result, weights]);

  const methodTabs = [];
  if (methodEnabled.mor) methodTabs.push({ id: 'mor', label: intl.formatMessage({ id: 'succession.multi_line_valuation.method_mor' }) });
  if (methodEnabled.dcf) methodTabs.push({ id: 'dcf', label: intl.formatMessage({ id: 'succession.multi_line_valuation.method_dcf' }) });
  if (methodEnabled.ebitda) methodTabs.push({ id: 'ebitda', label: intl.formatMessage({ id: 'succession.multi_line_valuation.method_ebitda' }) });
  if (methodEnabled.asset) methodTabs.push({ id: 'asset', label: intl.formatMessage({ id: 'succession.multi_line_valuation.method_asset' }) });
  if (hasGraphsFeature) methodTabs.push({ id: 'graphs', label: intl.formatMessage({ id: 'succession.multi_line_valuation.method_graphs' }) });

  return (
    <>
    <style>{`
      .mlv-range { -webkit-appearance: none; appearance: none; height: 8px; width: 100%; border-radius: 9999px; outline: none; background: var(--succession-bg-tertiary, #eee); }
      .mlv-range::-webkit-slider-runnable-track { height: 8px; border-radius: 9999px; background: transparent; }
      .mlv-range::-moz-range-track { height: 8px; border-radius: 9999px; background: transparent; }
      .mlv-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%; background: var(--succession-primary); border: 2px solid #fff; margin-top: -4px; cursor: pointer; }
      .mlv-range::-moz-range-thumb { width: 16px; height: 16px; border-radius: 50%; background: var(--succession-primary); border: 2px solid #fff; cursor: pointer; }
    `}</style>
    <SingleColumnTemplate
      title={readOnly ? intl.formatMessage({ id: 'succession.multi_line_valuation.valuation_read_only' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.valuation_studio' })}
      actions={readOnly ? null : actions}
      variant="wide"
    >
      <div data-tutorial-id="methodology-tabs">
        <Tabs
          tabs={hasSummaryTabFeature
            ? [{ id: 'summary', label: intl.formatMessage({ id: 'succession.multi_line_valuation.tab_summary' }) }, ...methodTabs]
            : methodTabs}
          value={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'summary' && (() => {
        // Compute combined summary client-side so the EBITDA contribution uses the
        // locally-derived EBITDA (consistent with the EBITDA tab cards/gauge).
        const getDisplayCombined = () => {
          const linesArr = result?.lines || [];
          if (!linesArr.length) return { low: 0, mid: 0, high: 0 };

          const w = weights;
          const mTotals = {
            mor:    { low: 0, mid: 0, high: 0 },
            dcf:    { low: 0, mid: 0, high: 0 },
            ebitda: { low: 0, mid: 0, high: 0 },
            asset:  { low: 0, mid: 0, high: 0 },
          };

          linesArr.forEach((l) => {
            const m = l?.methods || {};
            if (m.mor) {
              mTotals.mor.low  += Number(m.mor.low || 0);
              mTotals.mor.mid  += Number(m.mor.mid || 0);
              mTotals.mor.high += Number(m.mor.high || 0);
            }
            if (m.dcf) {
              const dcfMid = Number(m.dcf.mid || 0);
              mTotals.dcf.low  += Number(m.dcf.low ?? dcfMid * 0.9);
              mTotals.dcf.mid  += dcfMid;
              mTotals.dcf.high += Number(m.dcf.high ?? dcfMid * 1.1);
            }
            if (m.ebitda && typeof m.ebitda.mid === 'number') {
              const beEbitda = Number(m.ebitda.ebitda || 0);
              const series = dcfSeriesData[l.line];
              let localEBITDA = beEbitda;
              if (series) {
                localEBITDA = (series.netRevenue?.[1] || 0) - (series.direct?.[1] || 0) - (series.opex?.[1] || 0);
              }
              if (beEbitda > 0) {
                const lowM  = Number(m.ebitda.low || 0) / beEbitda;
                const midM  = Number(m.ebitda.mid || 0) / beEbitda;
                const highM = Number(m.ebitda.high || 0) / beEbitda;
                mTotals.ebitda.low  += Math.max(0, localEBITDA * lowM);
                mTotals.ebitda.mid  += Math.max(0, localEBITDA * midM);
                mTotals.ebitda.high += Math.max(0, localEBITDA * highM);
              }
            }
            if (m.asset) {
              const assetMid = Number(m.asset.mid || 0);
              mTotals.asset.low  += Number(m.asset.low ?? assetMid * 0.85);
              mTotals.asset.mid  += assetMid;
              mTotals.asset.high += Number(m.asset.high ?? assetMid * 1.15);
            }
          });

          const wLow  = { mor: mTotals.mor.low  * (w.mor||0), dcf: mTotals.dcf.low  * (w.dcf||0), ebitda: mTotals.ebitda.low  * (w.ebitda||0), asset: mTotals.asset.low  * (w.asset||0) };
          const wMid  = { mor: mTotals.mor.mid  * (w.mor||0), dcf: mTotals.dcf.mid  * (w.dcf||0), ebitda: mTotals.ebitda.mid  * (w.ebitda||0), asset: mTotals.asset.mid  * (w.asset||0) };
          const wHigh = { mor: mTotals.mor.high * (w.mor||0), dcf: mTotals.dcf.high * (w.dcf||0), ebitda: mTotals.ebitda.high * (w.ebitda||0), asset: mTotals.asset.high * (w.asset||0) };

          if (summaryViewMode === 'adjusted' && hasAdjustments) {
            const dcfImpact    = Number(result?.adjustment_impact?.dcf || 0);
            const ebitdaImpact = Number(result?.adjustment_impact?.ebitda || 0);
            const dcfR    = mTotals.dcf.mid > 0 && dcfImpact !== 0       ? 1 + dcfImpact / mTotals.dcf.mid       : 1;
            const ebitdaR = mTotals.ebitda.mid > 0 && ebitdaImpact !== 0 ? 1 + ebitdaImpact / mTotals.ebitda.mid : 1;
            wLow.dcf  *= dcfR;  wLow.ebitda  *= ebitdaR;
            wMid.dcf  *= dcfR;  wMid.ebitda  *= ebitdaR;
            wHigh.dcf *= dcfR;  wHigh.ebitda *= ebitdaR;
          }

          const baseLow  = Object.values(wLow).reduce((s, v) => s + v, 0);
          const baseMid  = Object.values(wMid).reduce((s, v) => s + v, 0);
          const baseHigh = Object.values(wHigh).reduce((s, v) => s + v, 0);

          let low = baseLow, mid = baseMid, high = baseHigh;
          if (low > mid || mid > high) {
            if (mid > 0) {
              const lowSpread  = baseMid > 0 && baseLow < baseMid  ? (baseMid - baseLow) / baseMid  : 0.1;
              const highSpread = baseMid > 0 && baseHigh > baseMid ? (baseHigh - baseMid) / baseMid : 0.1;
              low  = mid * (1 - lowSpread);
              high = mid * (1 + highSpread);
            } else {
              low = 0; mid = 0; high = 0;
            }
          }
          return { low: Math.max(0, low), mid: Math.max(0, mid), high: Math.max(0, high) };
        };
          
          return (
            <section className="space-y-3">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.multi_line_valuation.valuation_summary_title' })}
                </h1>
                <p className="text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.multi_line_valuation.weighted_combination' }, { methods: joinWithAnd(enabledLabels) })}
                </p>
              </div>

              {/* QuickBooks connection status banner */}
              {quickbooksConnectorEnabled && (
                <div className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border mb-4 ${quickbooksConnected ? 'border-green-200 bg-green-50' : 'border-succession-border bg-succession-bg-secondary'}`}>
                  <div className="flex items-center gap-3">
                    <img
                      src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
                      alt="QuickBooks"
                      className="w-6 h-6 object-contain flex-shrink-0"
                    />
                    <div>
                      <p className="text-sm font-semibold text-succession-text-primary leading-tight">QuickBooks Online</p>
                      <p className="text-xs text-succession-text-muted">
                        {quickbooksConnected
                          ? 'Revenue data is imported from QuickBooks'
                          : 'Connect QuickBooks to automatically import your P&L data'}
                      </p>
                    </div>
                  </div>
                  {quickbooksConnected ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex-shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Connected
                    </span>
                  ) : (
                    <a
                      href={quickbooksConnectUrl}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-succession-accent text-white hover:opacity-90 transition-opacity flex-shrink-0"
                    >
                      Connect QuickBooks
                    </a>
                  )}
                </div>
              )}

              {/* Toggle and Badge */}
              {hasAdjustments && (
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-succession-bg-secondary border border-succession-border rounded-lg p-1">
                      <button
                        type="button"
                        onClick={() => setSummaryViewMode('reported')}
                        className={`px-3 py-1.5 text-xs rounded transition-colors font-medium ${
                          summaryViewMode === 'reported'
                            ? 'bg-succession-primary text-white'
                            : 'bg-transparent text-succession-text-primary border border-succession-border hover:bg-succession-bg-tertiary'
                        }`}
                      >
                        {intl.formatMessage({ id: 'succession.multi_line_valuation.reported' })}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSummaryViewMode('adjusted')}
                        className={`px-3 py-1.5 text-xs rounded transition-colors font-medium ${
                          summaryViewMode === 'adjusted'
                            ? 'bg-succession-success text-white'
                            : 'bg-transparent text-succession-text-primary border border-succession-border hover:bg-succession-bg-tertiary'
                        }`}
                      >
                        {intl.formatMessage({ id: 'succession.multi_line_valuation.adjusted' })}
                      </button>
                    </div>
                    {summaryViewMode === 'adjusted' && (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-succession-success text-white border border-succession-border">
                        {intl.formatMessage({ id: 'succession.multi_line_valuation.normalized_badge' })}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Gauge visualization */}
              <div data-tutorial-id="valuation-gauge" className="bg-succession-bg-primary border border-succession-border rounded-xl p-8 shadow-sm">
                {/* Header with Confidence Indicator */}
                {hasConfidenceFeature && confidenceRating && (
                  <div className="flex justify-end mb-4">
                    <ConfidenceIndicator rating={confidenceRating} />
                  </div>
                )}
                {(() => {
                  const displayCombined = getDisplayCombined();
                  return (
                    <ValuationGauge
                      title={intl.formatMessage({ id: 'succession.multi_line_valuation.overall_indication' })}
                      lowValue={displayCombined.low}
                      highValue={displayCombined.high}
                      currentValue={displayCombined.mid}
                    />
                  );
                })()}
                {/* Validation CTA - Show only when viewing a saved scenario AND (not validated OR validated but modified) */}
                {hasConfidenceFeature && confidenceRating && isViewingSavedValuation && (
                  !confidenceRating.expertValidation?.isValidated ||
                  (confidenceRating.expertValidation?.isValidated && !confidenceRating.expertValidation?.isCurrentVersion)
                ) && (
                  <div className="mt-6">
                    <ValidationCTA variant="subtle" />
                  </div>
                )}
              </div>

              {/* Metrics row */}
              {(() => {
                const fmt0 = (v) => fmtCurrency0.format(Number(v || 0));
                
                // Use backend-provided values
                const displayCombinedMid = (() => {
                  const displayCombined = getDisplayCombined();
                  return displayCombined.mid || Number(combined?.mid || 0);
                })();
                
                // Use backend implied multiples
                const impliedRevenueMultiple = result?.implied_multiples?.revenue || 0;
                const impliedEbitdaMultiple = summaryViewMode === 'adjusted' 
                  ? (result?.implied_multiples?.ebitda_adjusted || 0)
                  : (result?.implied_multiples?.ebitda_reported || 0);
                
                // Use backend summary metrics for revenue
                const totalRevenue = result?.summary_metrics?.total_revenue || 0;

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <ValuationMetricCard 
                      title={<span className="flex items-center gap-2">{intl.formatMessage({ id: 'succession.multi_line_valuation.annual_revenue_label' })} <InfoTooltip content="Total annual revenue across all business lines." /></span>} 
                      value={fmt0(totalRevenue)}
                      icon={ValCurrencyIcon}
                      index={0}
                    />
                    <ValuationMetricCard 
                      title={<span className="flex items-center gap-2">Implied Revenue Multiple <InfoTooltip content="The weighted valuation divided by the total annual revenue." /></span>} 
                      value={`${impliedRevenueMultiple.toFixed(1)}x`} 
                      icon={BarChart3} 
                      index={1} 
                    />
                    <ValuationMetricCard 
                      title={<span className="flex items-center gap-2">Implied EBITDA Multiple <InfoTooltip content="The weighted valuation divided by the total EBITDA." /></span>} 
                      value={`${impliedEbitdaMultiple.toFixed(1)}x`} 
                      icon={XIcon} 
                      index={2} 
                    />
                  </div>
                );
              })()}

              {/* Donut charts row */}
              {(() => {
                // Business Line Revenue (annual revenue by line) — use current year base revenue before growth is applied
                const colorPalette = {
                  wealth: 'var(--succession-primary)',
                  life: 'var(--succession-secondary)',
                  pc: 'var(--succession-accent)',
                  group: 'var(--succession-neutral)',
                  other: 'var(--succession-neutral)',
                };
                const linesArr = (result?.lines || []);
                const revenueSegments = linesArr.map((l) => {
                  const baseRev = l?.methods?.dcf?.baseRevenue || 0;
                  const label = formatLineLabel(l.line);
                  return {
                    label,
                    value: baseRev,
                    color: colorPalette[l.line] || colorPalette.other,
                    formatted: fmt(baseRev),
                  };
                });
                const totalRevenue = revenueSegments.reduce((s, seg) => s + (Number(seg.value) || 0), 0);

                // Valuation by Methodology — reuse calculateAdjustedCombined to get weighted contributions
                const methodColors = {
                  dcf: 'var(--succession-primary)',
                  mor: 'var(--succession-secondary)',
                  ebitda: 'var(--succession-accent)',
                  asset: 'var(--succession-neutral)'
                };
                
                // Calculate method totals (same as calculateAdjustedCombined)
                const methodTotals = {
                  mor: { mid: 0 },
                  dcf: { mid: 0 },
                  ebitda: { mid: 0 },
                  asset: { mid: 0 },
                };
                linesArr.forEach((l) => {
                  const m = l?.methods || {};
                  if (m.mor && typeof m.mor.mid === 'number') methodTotals.mor.mid += m.mor.mid;
                  if (m.dcf && typeof m.dcf.mid === 'number') methodTotals.dcf.mid += m.dcf.mid;
                  if (m.ebitda && typeof m.ebitda.mid === 'number') {
                    const beEbitda = Number(m.ebitda.ebitda || 0);
                    const series = dcfSeriesData[l.line];
                    let localEBITDA = beEbitda;
                    if (series) {
                      localEBITDA = (series.netRevenue?.[1] || 0) - (series.direct?.[1] || 0) - (series.opex?.[1] || 0);
                    }
                    const midMultiple = beEbitda > 0 ? m.ebitda.mid / beEbitda : 0;
                    methodTotals.ebitda.mid += Math.max(0, localEBITDA * midMultiple);
                  }
                  if (m.asset && typeof m.asset.mid === 'number') methodTotals.asset.mid += m.asset.mid;
                });
                
                // Apply weights
                const weightedContrib = {
                  mor: methodTotals.mor.mid * (weights.mor || 0),
                  dcf: methodTotals.dcf.mid * (weights.dcf || 0),
                  ebitda: methodTotals.ebitda.mid * (weights.ebitda || 0),
                  asset: methodTotals.asset.mid * (weights.asset || 0),
                };
                
                // Apply adjustments if in adjusted mode - use backend adjustment_impact
                if (summaryViewMode === 'adjusted' && hasAdjustments) {
                  const dcfAdjustmentImpact = result?.adjustment_impact?.dcf || 0;
                  const ebitdaAdjustmentImpact = result?.adjustment_impact?.ebitda || 0;
                  
                  const reportedDCFMid = methodTotals.dcf.mid;
                  const reportedEBITDAMid = methodTotals.ebitda.mid;
                  
                  const dcfRatio = reportedDCFMid > 0 && dcfAdjustmentImpact !== 0
                    ? 1 + (dcfAdjustmentImpact / reportedDCFMid)
                    : 1;
                  
                  const ebitdaRatio = reportedEBITDAMid > 0 && ebitdaAdjustmentImpact !== 0
                    ? 1 + (ebitdaAdjustmentImpact / reportedEBITDAMid)
                    : 1;
                  
                  weightedContrib.dcf = weightedContrib.dcf * dcfRatio;
                  weightedContrib.ebitda = weightedContrib.ebitda * ebitdaRatio;
                }
                
                const order = ['mor', 'dcf', 'ebitda', 'asset'].filter((m) => methodEnabled[m]);
                const contribSegments = order.map((m) => ({
                  key: m,
                  label: m.toUpperCase(),
                  value: weightedContrib[m],
                  color: methodColors[m],
                  formatted: fmt(weightedContrib[m]),
                }));
                const weightedMidTotal = Object.values(weightedContrib).reduce((sum, val) => sum + Number(val || 0), 0);

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ValuationDonutChart
                      title={<span className="flex items-center gap-2">{intl.formatMessage({ id: 'succession.multi_line_valuation.revenue_breakdown_title' })} <InfoTooltip content="Distribution of annual revenue across different business lines." /></span>}
                      centerLabel={intl.formatMessage({ id: 'succession.multi_line_valuation.annual_revenue_label' })}
                      centerValue={fmtShort(totalRevenue)}
                      data={revenueSegments.map((s) => ({ name: s.label, value: s.value, color: s.color }))}
                    />
                    <ValuationDonutChart
                      title={<span className="flex items-center gap-2">{intl.formatMessage({ id: 'succession.multi_line_valuation.valuation_by_methodology' })} <InfoTooltip content="Contribution of each valuation method to the final weighted valuation." /></span>}
                      centerLabel={intl.formatMessage({ id: 'succession.multi_line_valuation.weighted_mid' })}
                      centerValue={fmtShort(weightedMidTotal)}
                      data={contribSegments.map((s) => ({ key: s.key, name: s.label, value: s.value, color: s.color }))}
                      tooltips={{
                        mor: 'Multiple of Revenue (MOR) applies industry-specific revenue multiples to estimate practice value based on recurring income.',
                        dcf: 'Discounted Cash Flow (DCF) projects future cash flows and discounts them to present value, reflecting the time value of money.',
                        ebitda: 'EBITDA valuation uses Earnings Before Interest, Taxes, Depreciation, and Amortization multiplied by industry-standard multiples.',
                        asset: 'Asset-Based valuation estimates value from the net tangible and intangible assets of the practice.',
                      }}
                      onLegendClick={(methodKey) => {
                        if (methodEnabled[methodKey]) {
                          setTab(methodKey);
                          const tabsEl = document.querySelector('[data-tutorial-id="methodology-tabs"]');
                          if (tabsEl) tabsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                    />
                  </div>
                );
              })()}

              {/* Removed Combined low/mid/high cards and method contributions per request */}

              {/* Learn More CTA Section - Hero Banner Style */}
              <div className="mt-8 rounded-2xl overflow-hidden shadow-succession-lg border border-succession-border bg-succession-secondary relative group">
                {/* Background Icon */}
                <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-15 transition-opacity">
                  <ValCurrencyIcon className="w-32 h-32 text-white" strokeWidth={1.5} />
                </div>
                <div className="px-4 py-3 md:px-6 md:py-5 lg:px-8 lg:py-6 relative z-10">
                  <div className="text-white">
                    <h2 className="m-0 p-0 text-2xl md:text-3xl font-bold">{intl.formatMessage({ id: 'succession.multi_line_valuation.learn_more_title' })}</h2>
                    <p className="mt-3 text-base md:text-lg leading-relaxed max-w-prose text-white/85">
                      {intl.formatMessage({ id: 'succession.multi_line_valuation.learn_more_body' })}
                    </p>
                    <div className="mt-5">
                      <a
                        href="/succession/education?category=valuation"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-succession-primary text-white font-medium hover:opacity-90 transition-opacity"
                      >
                        {intl.formatMessage({ id: 'succession.multi_line_valuation.explore_resources' })}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })()}

      {(tab === 'mor' || tab === 'ebitda' || tab === 'asset' || tab === 'dcf') && (
      <section className="space-y-3">
        {tab !== 'dcf' ? (
          tab === 'mor' ? (
            (() => {
              const rows = (result?.lines || []).map((l) => {
                const row = l?.methods?.mor || {};
                const w = Number(weights?.mor || 0);
                return {
                  line: l.line,
                  low: Number(row.low || 0),
                  mid: Number(row.mid || 0),
                  high: Number(row.high || 0),
                  weighted: Number(row.mid || 0) * w,
                };
              });
              if (typeof window !== 'undefined') {
                // Expose for quick debugging in browser console
                window.morRows = rows;
              }
              // Filter out empty lines to avoid zero-only chart rendering issues
              const nonEmpty = rows.filter((r) => (Number(r.low) || Number(r.mid) || Number(r.high) || Number(r.weighted)));
              const totals = rows.reduce((acc, r) => ({
                low: acc.low + r.low,
                mid: acc.mid + r.mid,
                high: acc.high + r.high,
              }), { low: 0, mid: 0, high: 0 });
              return (
                <>
                  {/* Page Header */}
                  <div className="mb-6">
                    <h1 className="text-2xl font-bold text-succession-text-primary mb-2">
                      {intl.formatMessage({ id: 'succession.multi_line_valuation.mor_title' })}
                    </h1>
                    <p className="text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession.multi_line_valuation.mor_subtitle' })}
                    </p>
                  </div>
                  
                  <div className="bg-succession-bg-primary border border-succession-border rounded-xl p-8 shadow-sm">
                    <ValuationGauge title={intl.formatMessage({ id: 'succession.multi_line_valuation.mor_indication' })} lowValue={totals.low} highValue={totals.high} currentValue={totals.mid} />
                  </div>
                  <MORDataTable data={rows} showTotals className="mt-4" />
                  <MORLineChart data={nonEmpty} className="mt-3" />
                </>
              );
            })()
          ) : (
            <>
              {tab === 'ebitda' && (() => {
                // Calculate adjustment impact - use backend value when available; otherwise fall back to
                // local category adjustments so "Adjusted" view reflects normalized OpEx immediately.
                const backendImpact = Number(result?.adjustment_impact?.ebitda || 0);
                const localImpact = (() => {
                  let impact = 0;
                  const cats = Array.isArray(expensesInputs.operatingExpenseCategories) ? expensesInputs.operatingExpenseCategories : [];
                  cats.forEach((cat) => {
                    const adjs = Array.isArray(cat?.adjustments) ? cat.adjustments : [];
                    adjs.forEach((adj) => {
                      const expenseDelta = Number(adj?.amount || 0); // positive increases expenses
                      impact += -expenseDelta; // EBITDA/cash flow impact
                    });
                  });
                  const nonOp = Number(expensesInputs?.otherAdjustments?.nonOperatingIncome || 0);
                  if (Number.isFinite(nonOp) && nonOp !== 0) impact += -nonOp;
                  (expensesInputs.normalizingAdjustments || []).forEach((adj) => {
                    const amount = Number(adj?.amount || 0);
                    const treatment = adj?.treatment || (adj?.type === 'nonOperatingIncome' ? 'subtract' : 'add');
                    impact += (treatment === 'add') ? amount : -amount;
                  });
                  return impact;
                })();
                const adjustmentImpact = backendImpact !== 0 ? backendImpact : localImpact;
                
                // Compute EBITDA valuation using locally-derived EBITDA (from DCF
                // projections, same data the summary cards display) and the EBITDA
                // multiples implied by the backend result. This keeps the gauge
                // consistent with the cards when expenses change the sign of EBITDA.
                const displayTotals = (result?.lines || []).reduce((acc, l) => {
                  const ebitdaMethod = l?.methods?.ebitda || {};
                  const backendEbitda = Number(ebitdaMethod.ebitda || 0);
                  const series = dcfSeriesData[l.line];

                  let localEBITDA;
                  if (series) {
                    const netRev = series.netRevenue?.[1] || 0;
                    const direct = series.direct?.[1] || 0;
                    let opex = series.opex?.[1] || 0;
                    if (ebitdaViewMode === 'adjusted' && hasAdjustments && adjustmentImpact !== 0) {
                      const totalOpExBase = Object.values(dcfSeriesData).reduce((s, sLine) => s + (sLine.opex?.[1] || 0), 0);
                      if (totalOpExBase > 0) {
                        opex -= adjustmentImpact * (opex / totalOpExBase);
                      }
                    }
                    localEBITDA = netRev - direct - opex;
                  } else {
                    localEBITDA = backendEbitda;
                  }

                  if (backendEbitda > 0) {
                    const lowM = Number(ebitdaMethod.low || 0) / backendEbitda;
                    const midM = Number(ebitdaMethod.mid || 0) / backendEbitda;
                    const highM = Number(ebitdaMethod.high || 0) / backendEbitda;
                    acc.low += Math.max(0, localEBITDA * lowM);
                    acc.mid += Math.max(0, localEBITDA * midM);
                    acc.high += Math.max(0, localEBITDA * highM);
                  }
                  return acc;
                }, { low: 0, mid: 0, high: 0 });
                
                // Calculate totals for cards using the same DCF series data as the table
                // This ensures consistency between card values and table values
                const totalsForCards = (result?.lines || []).reduce((acc, l) => {
                  const series = dcfSeriesData[l.line];
                  if (!series) return acc;
                  
                  // Use Year 1 (index 1) values - same as table
                  const netRevenue = series.netRevenue?.[1] || 0;
                  const direct = series.direct?.[1] || 0;
                  let opex = series.opex?.[1] || 0;
                  
                  // Apply adjustments to opex when in adjusted mode
                  if (ebitdaViewMode === 'adjusted' && hasAdjustments && adjustmentImpact !== 0) {
                    const totalOpExBase = Object.values(dcfSeriesData).reduce((s, sLine) => s + (sLine.opex?.[1] || 0), 0);
                    if (totalOpExBase > 0) {
                      const lineShare = opex / totalOpExBase;
                      opex = opex - (adjustmentImpact * lineShare);
                    }
                  }
                  
                  const ebitda = netRevenue - direct - opex;
                  
                  acc.revenue += netRevenue;
                  acc.direct += direct;
                  acc.opex += opex;
                  acc.ebitda += ebitda;
                  return acc;
                }, { revenue: 0, direct: 0, opex: 0, ebitda: 0 });
                
                const displayEBITDAForCards = totalsForCards.ebitda;
                const displayOpexForCards = totalsForCards.opex;
                const margin = totalsForCards.revenue > 0 ? (displayEBITDAForCards / totalsForCards.revenue) * 100 : 0;
                
                return (
                  <div className="mb-3 space-y-4">
                    {/* Page Header */}
                    <div className="mb-6">
                      <h1 className="text-2xl font-bold text-succession-text-primary mb-2">
                        {intl.formatMessage({ id: 'succession.multi_line_valuation.ebitda_multiple_title' })}
                      </h1>
                      <p className="text-succession-text-secondary">
                        {intl.formatMessage({ id: 'succession.multi_line_valuation.ebitda_multiple_subtitle' })}
                      </p>
                    </div>
                    
                    {/* Toggle and Badge */}
                    {hasAdjustments && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 bg-succession-bg-secondary border border-succession-border rounded-lg p-1">
                            <button
                              type="button"
                              onClick={() => setEbitdaViewMode('reported')}
                              className={`px-3 py-1.5 text-xs rounded transition-colors font-medium ${
                                ebitdaViewMode === 'reported'
                                  ? 'bg-succession-primary text-white'
                                  : 'bg-transparent text-succession-text-primary border border-succession-border hover:bg-succession-bg-tertiary'
                              }`}
                            >
                              {intl.formatMessage({ id: 'succession.multi_line_valuation.reported' })}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEbitdaViewMode('adjusted')}
                              className={`px-3 py-1.5 text-xs rounded transition-colors font-medium ${
                                ebitdaViewMode === 'adjusted'
                                  ? 'bg-succession-success text-white'
                                  : 'bg-transparent text-succession-text-primary border border-succession-border hover:bg-succession-bg-tertiary'
                              }`}
                            >
                              {intl.formatMessage({ id: 'succession.multi_line_valuation.adjusted' })}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bridge Card - Removed per request */}

                    <div className="bg-succession-bg-primary border border-succession-border rounded-xl p-8 shadow-sm">
                      <ValuationGauge title={intl.formatMessage({ id: 'succession.multi_line_valuation.ebitda_indication' })} lowValue={displayTotals.low} currentValue={displayTotals.mid} highValue={displayTotals.high} />
                    </div>
                    <EBITDASummaryCards
                      totalRevenue={totalsForCards.revenue}
                      totalOperatingExpenses={displayOpexForCards}
                      totalEBITDA={displayEBITDAForCards}
                      ebitdaMarginPct={margin}
                      className="mt-3"
                    />
                  </div>
                );
              })()}
              <CanonTable>
                <CanonThead>
                  <CanonTh sticky>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_line' })}</CanonTh>
                  {tab === 'ebitda' && (
                    <>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_projected_revenue' })}</CanonTh>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_direct_expenses' })}</CanonTh>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_operating_expenses' })}</CanonTh>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_ebitda' })}</CanonTh>
                    </>
                  )}
                  {tab === 'mor' || tab === 'ebitda' ? (
                    <>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_low' })}</CanonTh>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_mid' })}</CanonTh>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_high' })}</CanonTh>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_weighted' })}</CanonTh>
                    </>
                  ) : (
                    <>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_mid' })}</CanonTh>
                      <CanonTh>{intl.formatMessage({ id: 'succession.multi_line_valuation.table_weighted' })}</CanonTh>
                    </>
                  )}
                </CanonThead>
                <tbody>
                  {(() => {
                    const linesArr = result?.lines || [];
                    if (tab === 'ebitda') {
                      // Calculate adjustment impact for operating expenses normalization
                      const backendImpact = Number(result?.adjustment_impact?.ebitda || 0);
                      const localImpact = (() => {
                        let impact = 0;
                        const cats = Array.isArray(expensesInputs.operatingExpenseCategories) ? expensesInputs.operatingExpenseCategories : [];
                        cats.forEach((cat) => {
                          const adjs = Array.isArray(cat?.adjustments) ? cat.adjustments : [];
                          adjs.forEach((adj) => {
                            const expenseDelta = Number(adj?.amount || 0);
                            impact += -expenseDelta;
                          });
                        });
                        const nonOp = Number(expensesInputs?.otherAdjustments?.nonOperatingIncome || 0);
                        if (Number.isFinite(nonOp) && nonOp !== 0) impact += -nonOp;
                        (expensesInputs.normalizingAdjustments || []).forEach((adj) => {
                          const amount = Number(adj?.amount || 0);
                          const treatment = adj?.treatment || (adj?.type === 'nonOperatingIncome' ? 'subtract' : 'add');
                          impact += (treatment === 'add') ? amount : -amount;
                        });
                        return impact;
                      })();
                      const adjustmentImpact = backendImpact !== 0 ? backendImpact : localImpact;
                      
                      const rows = linesArr.map((l) => {
                        const row = (l.methods || {})[tab] || {};
                        const dcf = (l.methods || {}).dcf || {};
                        const p0 = Array.isArray(dcf.projections) ? (dcf.projections[0] || {}) : {};
                        const w = Number(weights[tab] || 0);
                        
                        // Use optimistic data if available (aligns with DCF tab logic)
                        const series = dcfSeriesData[l.line];
                        const hasSeries = !!series;
                        
                        // Projected Year 1 Revenue (index 1) - forward-looking for valuation planning
                        // Net Revenue already has payout applied for wealth line
                        const netRevenue = hasSeries ? (series.netRevenue[1] || 0) : Number(p0.revenue || 0);
                        
                        // Direct Expenses (Owner Comp only - payout is already reflected in net revenue)
                        const ownerCompAmount = hasSeries ? (series.direct[1] || 0) : 0;
                        
                        // OpEx - Projected Year 1 (index 1)
                        let opex = hasSeries ? (series.opex[1] || 0) : Number(p0.operatingExpenses || 0);
                        
                        // Apply adjustments to operating expenses when in adjusted mode (normalized OpEx)
                        if (ebitdaViewMode === 'adjusted' && hasAdjustments && adjustmentImpact !== 0) {
                          const totalOpExBase = Object.values(dcfSeriesData).reduce((s, sLine) => s + (sLine.opex[1] || 0), 0);
                          const totalReportedOpEx = totalOpExBase > 0 ? totalOpExBase : linesArr.reduce((sum, line) => {
                            const lineDcf = line?.methods?.dcf || {};
                            const lineP0 = Array.isArray(lineDcf.projections) ? (lineDcf.projections[0] || {}) : {};
                            return sum + Number(lineP0.operatingExpenses || 0);
                          }, 0);
                          
                          if (totalReportedOpEx > 0) {
                            const lineShare = opex / totalReportedOpEx;
                            const lineAdjustment = adjustmentImpact * lineShare;
                            opex = opex - lineAdjustment;
                          } else {
                            opex = opex - (adjustmentImpact / linesArr.length);
                          }
                        }
                        
                        // Recalculate EBITDA using net revenue (consistent with DCF)
                        const ebitda = netRevenue - ownerCompAmount - opex;
                        
                        // Derive multiples from backend and apply to local EBITDA
                        const beEbitda = Number(row.ebitda || 0);
                        const lowM = beEbitda > 0 ? Number(row.low || 0) / beEbitda : 0;
                        const midM = beEbitda > 0 ? Number(row.mid || 0) / beEbitda : 0;
                        const highM = beEbitda > 0 ? Number(row.high || 0) / beEbitda : 0;

                        return {
                          id: l.line,
                          revenue: netRevenue,
                          direct: ownerCompAmount,
                          opex,
                          ebitda,
                          low: Math.max(0, ebitda * lowM),
                          mid: Math.max(0, ebitda * midM),
                          high: Math.max(0, ebitda * highM),
                          weighted: Math.max(0, ebitda * midM) * w,
                        };
                      });
                      const totals = rows.reduce((acc, r) => ({
                        revenue: acc.revenue + r.revenue,
                        direct: acc.direct + (r.direct || 0),
                        opex: acc.opex + r.opex,
                        ebitda: acc.ebitda + r.ebitda,
                        low: acc.low + r.low,
                        mid: acc.mid + r.mid,
                        high: acc.high + r.high,
                        weighted: acc.weighted + r.weighted,
                      }), { revenue: 0, direct: 0, opex: 0, ebitda: 0, low: 0, mid: 0, high: 0, weighted: 0 });
                      return (
                        <>
                          {rows.map((r) => (
                            <CanonTr key={`${tab}-${r.id}`}>
                              <CanonTd sticky align="left">{formatLineLabel(r.id)}</CanonTd>
                              <CanonTd>{fmt(r.revenue)}</CanonTd>
                              <CanonTd>{fmt(r.direct)}</CanonTd>
                              <CanonTd>{fmt(r.opex)}</CanonTd>
                              <CanonTd>{fmt(r.ebitda)}</CanonTd>
                              <CanonTd>{fmt(r.low)}</CanonTd>
                              <CanonTd>{fmt(r.mid)}</CanonTd>
                              <CanonTd>{fmt(r.high)}</CanonTd>
                              <CanonTd>{fmt(r.weighted)}</CanonTd>
                            </CanonTr>
                          ))}
                          <CanonTr>
                            <CanonTd sticky align="left" className="font-semibold">
                              Total
                              {ebitdaViewMode === 'adjusted' && hasAdjustments && (
                                <span className="ml-2 px-2 py-0.5 rounded text-xs font-normal bg-amber-500 text-white">
                                  Firm-level adjustments applied
                                </span>
                              )}
                            </CanonTd>
                            <CanonTd className="font-semibold">{fmt(totals.revenue)}</CanonTd>
                            <CanonTd className="font-semibold">{fmt(totals.direct)}</CanonTd>
                            <CanonTd className="font-semibold">{fmt(totals.opex)}</CanonTd>
                            <CanonTd className="font-semibold">{fmt(totals.ebitda)}</CanonTd>
                            <CanonTd className="font-semibold">{fmt(totals.low)}</CanonTd>
                            <CanonTd className="font-semibold">{fmt(totals.mid)}</CanonTd>
                            <CanonTd className="font-semibold">{fmt(totals.high)}</CanonTd>
                            <CanonTd className="font-semibold text-emerald-600">{fmt(totals.weighted)}</CanonTd>
                          </CanonTr>
                        </>
                      );
                    }
                    // Default (non-ebitda) rows
                    return (result?.lines || []).map((l) => {
                      const m = l.methods || {};
                      const row = m[tab] || {};
                      const w = (weights[tab] || 0);
                      return (
                        <CanonTr key={`${tab}-${l.line}`}>
                          <CanonTd sticky align="left">{formatLineLabel(l.line)}</CanonTd>
                          <CanonTd>{fmt(row.mid)}</CanonTd>
                          <CanonTd>{fmt((row.mid || 0) * w)}</CanonTd>
                        </CanonTr>
                      );
                    });
                  })()}
                </tbody>
              </CanonTable>
            </>
          )
        ) : (
          (() => {
            // For category-based OpEx normalizations, all adjustments affect DCF.
            // We build a display list as "EBITDA impacts" (positive = improves cash flow, negative = reduces).
            const dcfAdjustments = (() => {
              const items = [];
              const cats = Array.isArray(expensesInputs.operatingExpenseCategories) ? expensesInputs.operatingExpenseCategories : [];
              cats.forEach((cat) => {
                const catLabel = cat?.label || cat?.key;
                const adjs = Array.isArray(cat?.adjustments) ? cat.adjustments : [];
                adjs.forEach((adj) => {
                  const expenseDelta = Number(adj?.amount || 0); // positive increases expenses
                  const impact = -expenseDelta; // EBITDA/cashflow impact
                  const label = String(adj?.label || '').trim() || `${catLabel} adjustment`;
                  if (impact !== 0 || label) items.push({ id: adj?.id, label, amount: impact });
                });
              });
              const nonOp = Number(expensesInputs?.otherAdjustments?.nonOperatingIncome || 0);
              if (Number.isFinite(nonOp) && nonOp !== 0) {
                items.push({ id: 'nonOperatingIncome', label: 'Remove Non-Operating Income', amount: -nonOp });
              }

              // Legacy global adjustments (if present)
              const legacy = (expensesInputs.normalizingAdjustments || []).filter((adj) => adj.affectsDCF !== false);
              legacy.forEach((adj) => {
                const amount = Number(adj?.amount || 0);
                const treatment = adj?.treatment || (adj?.type === 'nonOperatingIncome' ? 'subtract' : 'add');
                const impact = (treatment === 'add') ? amount : -amount;
                items.push({ id: adj?.id, label: getAdjustmentLabel(adj?.type), amount: impact });
              });

              return items.filter((x) => x && (Number(x.amount || 0) !== 0 || String(x.label || '').trim() !== ''));
            })();
            const hasDcfAdjustments = dcfAdjustments.length > 0;

            
            /* Duplicate block removed
            // const unused_dcfSeriesData_duplicate = useMemo(() => {
              const empty = { series: {}, years: [] };
              if (!result?.lines) return empty;
              
              const dcfLines = result.lines.map((l) => ({ id: l.line, dcf: l?.methods?.dcf || {} })).filter((x) => Array.isArray(x.dcf.projections));
              if (dcfLines.length === 0) return empty;

              const maxYears = Math.max(...dcfLines.map((x) => (x.dcf.projections?.length || 0)));
              const years = Array.from({ length: maxYears + 1 }, (_v, i) => i);

              const series = dcfLines.reduce((acc, { id, dcf }) => {
                const proj = dcf.projections || [];
                const rev1 = proj[0]?.revenue || 0;
                const revGrowth = Number.isFinite(dcf.growthRate) ? dcf.growthRate : (proj[1] && proj[0] && proj[0].revenue ? (proj[1].revenue / proj[0].revenue) - 1 : 0);
                const opex1 = proj[0]?.operatingExpenses || 0;
                const opexGrowth = (proj[1] && proj[0] && proj[0].operatingExpenses) ? ((proj[1].operatingExpenses / proj[0].operatingExpenses) - 1) : 0.02;
                
                // Determine effective tax rate
                const effectiveRate = (() => {
                  const preferredRate = (Number.isFinite(dcf.taxRate) ? dcf.taxRate : null);
                  const fallbackRateFromY1 = (() => {
                    const pe = Number(proj[0]?.ebit);
                    const pt = Number(proj[0]?.tax);
                    if (Number.isFinite(pe) && pe > 0 && Number.isFinite(pt)) return Math.max(0, pt / pe);
                    return null;
                  })();
                  return (preferredRate != null ? preferredRate : (fallbackRateFromY1 != null ? fallbackRateFromY1 : 0.25));
                })();

                const payoutLocal = (() => {
                  if (id === 'wealth' && lineInputs?.wealth?.faRetentionPercent != null) {
                    const p = Number(lineInputs.wealth.faRetentionPercent);
                    return Number.isFinite(p) ? (p > 1 ? p / 100 : p) : 0;
                  }
                  const n = Number(currentUser?.branch_net_payout);
                  return Number.isFinite(n) ? (n > 1 ? n / 100 : n) : 0;
                })();

                const ocpLocal = (bnr) => {
                  if (bnr < 250000.0) return 0.401;
                  if (bnr < 500000.0) return 0.44;
                  if (bnr < 1000000.0) return 0.264;
                  if (bnr < 2000000.0) return 0.202;
                  if (bnr < 3000000.0) return 0.194;
                  if (bnr < 5000000.0) return 0.189;
                  if (bnr < 7000000.0) return 0.132;
                  if (bnr < 10000000.0) return 0.193;
                  if (bnr < 15000000.0) return 0.183;
                  return 0.141;
                };
                const ownersCompEnabled = (viewerUser?.company?.rj_use_owners_comp_direct_expenses != null) ? !!viewerUser.company.rj_use_owners_comp_direct_expenses : true;

                // Calculate Year 0 (Current)
                const rev0 = rev1 / (1 + (revGrowth || 0));
                const nr0 = (id === 'wealth' ? (rev0 * payoutLocal) : rev0);
                const opex0 = opex1 / (1 + (opexGrowth || 0));
                const direct0 = (id === 'wealth' && ownersCompEnabled) ? (nr0 * ocpLocal(nr0)) : 0;
                const ebit0 = nr0 - opex0 - direct0;
                const t0Backend = Number.isFinite(Number(dcf.year0Tax)) ? Number(dcf.year0Tax) : null;
                const tax0 = (t0Backend != null) ? t0Backend : (ebit0 > 0 ? ebit0 * effectiveRate : 0);
                const nopat0 = ebit0 - tax0;
                const fcf0 = nopat0; 
                const discount0 = 1;
                const pv0 = fcf0 * discount0;

                // Calculate Projections (Years 1+)
                const calculatedProjections = proj.map((p) => {
                  const r = Number(p.revenue || 0);
                  const nr = (id === 'wealth' ? (r * payoutLocal) : r);
                  const opex = Number(p.operatingExpenses || 0);
                  const direct = (id === 'wealth' && ownersCompEnabled) ? (nr * ocpLocal(nr)) : 0;
                  const ebit = nr - opex - direct;
                  const tax = (ebit > 0) ? ebit * effectiveRate : 0;
                  const nopat = ebit - tax;
                  const fcf = nopat;
                  const df = Number(p.discountFactor || 0);
                  const pv = fcf * df;
                  return { revenue: r, netRevenue: nr, operatingExpenses: opex, direct, ebit, tax, nopat, fcf, discountFactor: df, pv };
                });

                // Construct Arrays
                const revenue = [rev0, ...calculatedProjections.map(p => p.revenue)].slice(0, years.length);
                const netRevenue = [nr0, ...calculatedProjections.map(p => p.netRevenue)].slice(0, years.length);
                const opexArray = [opex0, ...calculatedProjections.map(p => p.operatingExpenses)].slice(0, years.length);
                const directArray = [direct0, ...calculatedProjections.map(p => p.direct)].slice(0, years.length);
                const ebit = [ebit0, ...calculatedProjections.map(p => p.ebit)].slice(0, years.length);
                const tax = [tax0, ...calculatedProjections.map(p => p.tax)].slice(0, years.length);
                const nopat = [nopat0, ...calculatedProjections.map(p => p.nopat)].slice(0, years.length);
                const fcf = [fcf0, ...calculatedProjections.map(p => p.fcf)].slice(0, years.length);
                const discount = [discount0, ...calculatedProjections.map(p => p.discountFactor)].slice(0, years.length);
                const pv = [pv0, ...calculatedProjections.map(p => p.pv)].slice(0, years.length);

                // Calculate Terminal Value & Enterprise Value
                const wacc = Number(dcf.wacc || 0);
                const termG = Number.isFinite(dcf.terminalGrowthRate) ? dcf.terminalGrowthRate : 0.02;
                const lastFcf = fcf[fcf.length - 1] || 0;
                const lastDf = discount[discount.length - 1] || 0;
                const terminalValue = (wacc > termG) ? (lastFcf * (1 + termG)) / (wacc - termG) : 0;
                const pvTerminal = terminalValue * lastDf;
                const pvSum = pv.slice(1).reduce((s, v) => s + v, 0); // Exclude Year 0
                const enterpriseValue = pvSum + pvTerminal;

                acc[id] = {
                  revenue,
                  netRevenue,
                  opex: opexArray,
                  direct: directArray,
                  ebit,
                  tax,
                  nopat,
                  fcf,
                  discount,
                  pv,
                  growthRate: (Number.isFinite(dcf.growthRate) ? dcf.growthRate : revGrowth),
                  terminalGrowthRate: termG,
                  wacc: wacc,
                  waccComponents: dcf.waccComponents || null,
                  terminalValue,
                  pvTerminal,
                  enterpriseValue,
                  pvSum
                };
                return acc;
              }, {});

              return { series, years };
            }, [result, lineInputs, currentUser, viewerUser]);
            */
            
            // Use backend-provided adjustment_impact when available; otherwise fall back to summing
            // the client-side category adjustments (so Adjusted CF can reflect normalized OpEx immediately).
            const backendImpact = Number(result?.adjustment_impact?.dcf || 0);
            const localImpact = dcfAdjustments.reduce((s, a) => s + Number(a?.amount || 0), 0);
            const adjustmentImpact = backendImpact !== 0 ? backendImpact : localImpact;
            
            const sums = Object.values(dcfSeriesData).reduce((acc, line) => {
              acc.sumPv += (line.pvSum || 0);
              acc.pvTerminal += (line.pvTerminal || 0);
              acc.enterprise += (line.enterpriseValue || 0);
              return acc;
            }, { sumPv: 0, pvTerminal: 0, enterprise: 0 });
            
            // Apply adjustment ratio if in adjusted mode
            const adjustmentRatio = dcfViewMode === 'adjusted' && hasDcfAdjustments && sums.enterprise > 0
              ? 1 + (adjustmentImpact / sums.enterprise)
              : 1;
            
            const displaySums = {
              sumPv: sums.sumPv * adjustmentRatio,
              pvTerminal: sums.pvTerminal * adjustmentRatio,
              enterprise: sums.enterprise * adjustmentRatio,
            };
            
            return (
              <>
                {/* Page Header */}
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-succession-text-primary mb-2">
                    {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_title' })}
                  </h1>
                  <p className="text-succession-text-secondary">
                    {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_subtitle' })}
                  </p>
                </div>
                
                {/* Toggle and Badge */}
                {hasDcfAdjustments && (
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 bg-succession-bg-secondary border border-succession-border rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => setDcfViewMode('reported')}
                          className={`px-3 py-1.5 text-xs rounded transition-colors font-medium ${
                            dcfViewMode === 'reported'
                              ? 'bg-succession-primary text-white'
                              : 'bg-transparent text-succession-text-primary border border-succession-border hover:bg-succession-bg-tertiary'
                          }`}
                        >
                          {intl.formatMessage({ id: 'succession.multi_line_valuation.reported_cf' })}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDcfViewMode('adjusted')}
                          className={`px-3 py-1.5 text-xs rounded transition-colors font-medium ${
                            dcfViewMode === 'adjusted'
                              ? 'bg-succession-success text-white'
                              : 'bg-transparent text-succession-text-primary border border-succession-border hover:bg-succession-bg-tertiary'
                          }`}
                        >
                          {intl.formatMessage({ id: 'succession.multi_line_valuation.adjusted' })}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="bg-succession-bg-primary border border-succession-border rounded-xl p-8 shadow-sm">
                  {(() => {
                    const dcfVal = displaySums.enterprise;
                    const useRange = hasDcfRangeFeature
                      && grossProductionFloor > 0
                      && dcfVal > 0
                      && grossProductionFloor !== dcfVal;
                    if (useRange) {
                      // Order the ends smaller → bigger; label each by what it is
                      // (1× revenue floor vs. DCF ceiling), not by its position.
                      const low = Math.min(grossProductionFloor, dcfVal);
                      const high = Math.max(grossProductionFloor, dcfVal);
                      const floorIsLow = low === grossProductionFloor;
                      const revenueLabel = intl.formatMessage({ id: 'succession.multi_line_valuation.gauge_revenue_floor' });
                      const dcfLabel = intl.formatMessage({ id: 'succession.multi_line_valuation.gauge_dcf_ceiling' });
                      return (
                        <>
                          <ValuationGauge
                            title={intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_indication' })}
                            rangeMode
                            lowValue={low}
                            highValue={high}
                            lowSublabel={floorIsLow ? revenueLabel : dcfLabel}
                            highSublabel={floorIsLow ? dcfLabel : revenueLabel}
                          />
                          <p className="text-center text-sm text-succession-text-secondary mt-4" style={{ margin: '1rem 0 0' }}>
                            {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_range_caption' })}
                          </p>
                        </>
                      );
                    }
                    return (
                      <ValuationGauge
                        title={intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_indication' })}
                        lowValue={dcfVal * 0.9}
                        highValue={dcfVal * 1.1}
                        currentValue={dcfVal}
                      />
                    );
                  })()}
                  {t12YearEndNote && (
                    <p className="text-center text-xs text-succession-text-secondary mt-3" style={{ margin: '0.75rem 0 0' }}>
                      {t12YearEndNote}
                    </p>
                  )}
                </div>
                <DCFSummaryCards
                  sumOfPV={displaySums.sumPv}
                  terminalValue={displaySums.pvTerminal}
                  enterpriseValue={displaySums.enterprise}
                  className="mt-2"
                />
              </>
            );
          })()
        )}

        {tab === 'dcf' && (
          <section className="space-y-4">
            {(() => {
              if (Object.keys(dcfSeriesData).length === 0) return null;
              const years = dcfYears;
              
              const seriesByLine = dcfSeriesData;
              
              const unused_dcfLines = (result?.lines || []).map((l) => ({ id: l.line, dcf: l?.methods?.dcf || {} })).filter((x) => Array.isArray(x.dcf.projections) && x.dcf.projections.length > 0);
              const unused_reducer = unused_dcfLines.reduce((acc, { id, dcf }) => {
                const proj = dcf.projections || [];
                const rev1 = proj[0]?.revenue || 0;
                const revGrowth = Number.isFinite(dcf.growthRate) ? dcf.growthRate : (proj[1] && proj[0] && proj[0].revenue ? (proj[1].revenue / proj[0].revenue) - 1 : 0);
                const opex1 = proj[0]?.operatingExpenses || 0;
                const opexGrowth = (proj[1] && proj[0] && proj[0].operatingExpenses) ? ((proj[1].operatingExpenses / proj[0].operatingExpenses) - 1) : 0.02;

                const payoutLocal = (() => {
                  if (id === 'wealth' && lineInputs?.wealth?.faRetentionPercent != null) {
                    const p = Number(lineInputs.wealth.faRetentionPercent);
                    return Number.isFinite(p) ? (p > 1 ? p / 100 : p) : 0;
                  }
                  const n = Number(currentUser?.branch_net_payout);
                  return Number.isFinite(n) ? (n > 1 ? n / 100 : n) : 0;
                })();

                const ocpLocal = (bnr) => {
                  if (bnr < 250000.0) return 0.401;
                  if (bnr < 500000.0) return 0.44;
                  if (bnr < 1000000.0) return 0.264;
                  if (bnr < 2000000.0) return 0.202;
                  if (bnr < 3000000.0) return 0.194;
                  if (bnr < 5000000.0) return 0.189;
                  if (bnr < 7000000.0) return 0.132;
                  if (bnr < 10000000.0) return 0.193;
                  if (bnr < 15000000.0) return 0.183;
                  return 0.141;
                };
                const ownersCompEnabled = (viewerUser?.company?.rj_use_owners_comp_direct_expenses != null) ? !!viewerUser.company.rj_use_owners_comp_direct_expenses : true;

                // Determine effective tax rate
                const effectiveRate = (() => {
                  const preferredRate = (Number.isFinite(dcf.taxRate) ? dcf.taxRate : null);
                  const fallbackRateFromY1 = (() => {
                    const pe = Number(proj[0]?.ebit);
                    const pt = Number(proj[0]?.tax);
                    if (Number.isFinite(pe) && pe > 0 && Number.isFinite(pt)) return Math.max(0, pt / pe);
                    return null;
                  })();
                  return (preferredRate != null ? preferredRate : (fallbackRateFromY1 != null ? fallbackRateFromY1 : 0.25));
                })();

                // Calculate Year 0 (Current)
                const rev0 = rev1 / (1 + (revGrowth || 0));
                const nr0 = (id === 'wealth' ? (rev0 * payoutLocal) : rev0);
                const opex0 = opex1 / (1 + (opexGrowth || 0));
                const direct0 = (id === 'wealth' && ownersCompEnabled) ? (nr0 * ocpLocal(nr0)) : 0;
                const ebit0 = nr0 - opex0 - direct0;
                const t0Backend = Number.isFinite(Number(dcf.year0Tax)) ? Number(dcf.year0Tax) : null;
                const tax0 = (t0Backend != null) ? t0Backend : (ebit0 > 0 ? ebit0 * effectiveRate : 0);
                const nopat0 = ebit0 - tax0; // Year 0 NOPAT/FCF usually same
                const fcf0 = nopat0; 
                const discount0 = 1;
                const pv0 = fcf0 * discount0;

                // Calculate Projections (Years 1+)
                const calculatedProjections = proj.map((p) => {
                  const r = Number(p.revenue || 0);
                  const nr = (id === 'wealth' ? (r * payoutLocal) : r);
                  const opex = Number(p.operatingExpenses || 0);
                  const direct = (id === 'wealth' && ownersCompEnabled) ? (nr * ocpLocal(nr)) : 0;
                  const ebit = nr - opex - direct;
                  const tax = (ebit > 0) ? ebit * effectiveRate : 0;
                  const nopat = ebit - tax;
                  const fcf = nopat; // Simplified FCF = NOPAT
                  const df = Number(p.discountFactor || 0);
                  const pv = fcf * df;

                  return { revenue: r, netRevenue: nr, operatingExpenses: opex, ebit, tax, nopat, fcf, discountFactor: df, pv };
                });

                // Construct Arrays
                const revenue = [rev0, ...calculatedProjections.map(p => p.revenue)].slice(0, years.length);
                const netRevenue = [nr0, ...calculatedProjections.map(p => p.netRevenue)].slice(0, years.length);
                const opex = [opex0, ...calculatedProjections.map(p => p.operatingExpenses)].slice(0, years.length);
                const ebit = [ebit0, ...calculatedProjections.map(p => p.ebit)].slice(0, years.length);
                const tax = [tax0, ...calculatedProjections.map(p => p.tax)].slice(0, years.length);
                const nopat = [nopat0, ...calculatedProjections.map(p => p.nopat)].slice(0, years.length);
                const fcf = [fcf0, ...calculatedProjections.map(p => p.fcf)].slice(0, years.length);
                const discount = [discount0, ...calculatedProjections.map(p => p.discountFactor)].slice(0, years.length);
                const pv = [pv0, ...calculatedProjections.map(p => p.pv)].slice(0, years.length);

                acc[id] = {
                  revenue,
                  netRevenue,
                  opex,
                  ebit,
                  tax,
                  nopat,
                  fcf,
                  discount,
                  pv,
                  growthRate: (Number.isFinite(dcf.growthRate) ? dcf.growthRate : revGrowth),
                  terminalGrowthRate: (Number.isFinite(dcf.terminalGrowthRate) ? dcf.terminalGrowthRate : 0.02),
                  wacc: (Number.isFinite(dcf.wacc) ? dcf.wacc : 0),
                  waccComponents: dcf.waccComponents || null
                };
                return acc;
              }, {});

              const sumSeries = (key) => years.map((_, i) => Object.values(seriesByLine).reduce((s, v) => s + (v[key][i] || 0), 0));
              const avgSeries = (key) => years.map((_, i) => {
                const vals = Object.values(seriesByLine).map((v) => v[key][i] || 0);
                return vals.length ? (vals.reduce((s, x) => s + x, 0) / vals.length) : 0;
              });

              const totalEbit = sumSeries('ebit');
              const totalTax = sumSeries('tax');
              const totalNopat = sumSeries('nopat');
              const totalFcf = sumSeries('fcf');
              const totalPv = sumSeries('pv');
              const avgDiscount = avgSeries('discount');
              const avgWacc = (() => {
                const items = Object.entries(seriesByLine).map(([k, v]) => ({ key: k, wacc: Number(v.wacc || 0), rev1: Number((v.revenue && v.revenue[1]) || 0) }));
                const valid = items.filter((it) => Number.isFinite(it.wacc) && it.wacc > 0);
                const totalRev = valid.reduce((s, it) => s + (Number.isFinite(it.rev1) ? it.rev1 : 0), 0);
                if (totalRev > 0) {
                  return valid.reduce((s, it) => s + (it.wacc * ((it.rev1 || 0) / totalRev)), 0);
                }
                return valid.length ? (valid.reduce((s, it) => s + it.wacc, 0) / valid.length) : 0;
              })();

              const headerCols = (
                <tr>
                  <th className="px-3 py-2 text-left text-succession-text-secondary font-medium"></th>
                  {years.map((y) => (
                    <th key={`y-${y}`} className="px-3 py-2 text-left text-succession-text-secondary font-medium">{y === 0 ? intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_current_year' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_year_n' }, { n: y })}</th>
                  ))}
                </tr>
              );

              // Adapt projections to the new DCFProjectionsTable shape
              // Derive unified Direct/Operating expenses series
              const expenseGrowthRate = Number.isFinite(expensesInputs.expenseGrowthRate) 
                ? expensesInputs.expenseGrowthRate 
                : (Number.isFinite(currentUser?.expense_growth_rate) 
                  ? (currentUser.expense_growth_rate > 1 ? currentUser.expense_growth_rate / 100 : currentUser.expense_growth_rate)
                  : 0.02);
              const payout = (() => { const n = Number(currentUser?.branch_net_payout); return Number.isFinite(n) ? (n > 1 ? n / 100 : n) : 0; })();
              const ocp = (bnr) => {
                if (bnr < 250000.0) return 0.401;
                if (bnr < 500000.0) return 0.44;
                if (bnr < 1000000.0) return 0.264;
                if (bnr < 2000000.0) return 0.202;
                if (bnr < 3000000.0) return 0.194;
                if (bnr < 5000000.0) return 0.189;
                if (bnr < 7000000.0) return 0.132;
                if (bnr < 10000000.0) return 0.193;
                if (bnr < 15000000.0) return 0.183;
                return 0.141;
              };
              // Use the memoized ownersCompEnabled flag for consistency
              const ownersCompEnabled = ownersCompEnabledTable;
              const directSeries = years.map((_y, idx) => {
                if (!ownersCompEnabled) return 0;
                // Prefer backend-provided direct expenses (includes any override value)
                if (seriesByLine.wealth?.direct?.[idx] != null) {
                  return seriesByLine.wealth.direct[idx];
                }
                const wealthRev = seriesByLine.wealth ? Number(seriesByLine.wealth.revenue[idx] || 0) : 0;
                const branchNetRevenue = wealthRev * payout;
                return branchNetRevenue * ocp(branchNetRevenue);
              });
              
              // Calculate per-line operating expenses from allocations
              const totalOperatingExpenses = Number(expensesInputs.operatingExpenses || 0);
              const allocations = expensesInputs.allocations || { wealth: 0, life: 0, pc: 0, group: 0 };
              const allocationsSum = (allocations.wealth || 0) + (allocations.life || 0) + (allocations.pc || 0) + (allocations.group || 0);
              
              // Use user-entered allocations if they sum to > 0 or if operating expenses are set
              // Otherwise fall back to backend projections
              const useUserAllocations = allocationsSum > 0 || totalOperatingExpenses > 0;
              
              const overheadSeriesByLine = {
                wealth: years.map((_y, idx) => {
                  if (useUserAllocations) {
                    return (totalOperatingExpenses * (allocations.wealth || 0) / 100) * Math.pow(1 + expenseGrowthRate, idx);
                  }
                  // Fall back to backend projection operating expenses
                  const backendOpEx = seriesByLine.wealth ? (seriesByLine.wealth.opex[idx] || 0) : 0;
                  return backendOpEx;
                }),
                life: years.map((_y, idx) => {
                  if (useUserAllocations) {
                    return (totalOperatingExpenses * (allocations.life || 0) / 100) * Math.pow(1 + expenseGrowthRate, idx);
                  }
                  const backendOpEx = seriesByLine.life ? (seriesByLine.life.opex[idx] || 0) : 0;
                  return backendOpEx;
                }),
                pc: years.map((_y, idx) => {
                  if (useUserAllocations) {
                    return (totalOperatingExpenses * (allocations.pc || 0) / 100) * Math.pow(1 + expenseGrowthRate, idx);
                  }
                  const backendOpEx = seriesByLine.pc ? (seriesByLine.pc.opex[idx] || 0) : 0;
                  return backendOpEx;
                }),
                group: years.map((_y, idx) => {
                  if (useUserAllocations) {
                    return (totalOperatingExpenses * (allocations.group || 0) / 100) * Math.pow(1 + expenseGrowthRate, idx);
                  }
                  const backendOpEx = seriesByLine.group ? (seriesByLine.group.opex[idx] || 0) : 0;
                  return backendOpEx;
                }),
              };
              // Total overhead series (sum of all lines) for backwards compatibility
              const overheadSeries = years.map((_y, idx) => 
                overheadSeriesByLine.wealth[idx] + 
                overheadSeriesByLine.life[idx] + 
                overheadSeriesByLine.pc[idx] + 
                overheadSeriesByLine.group[idx]
              );

              // Use the memoized ownersCompEnabled flag (already calculated at component level)
              // This ensures consistent evaluation on refresh

              // For category-based OpEx normalizations, all adjustments affect DCF.
              // (Legacy: used affectsDCF filter on global adjustments.)
              const hasDcfAdjustments = hasAdjustments;
              const backendImpact = Number(result?.adjustment_impact?.dcf || 0);
              // Mirror the adjustment list computation used by the banner above
              const localImpact = (() => {
                const cats = Array.isArray(expensesInputs.operatingExpenseCategories) ? expensesInputs.operatingExpenseCategories : [];
                let impact = 0;
                cats.forEach((cat) => {
                  const adjs = Array.isArray(cat?.adjustments) ? cat.adjustments : [];
                  adjs.forEach((adj) => {
                    const expenseDelta = Number(adj?.amount || 0); // positive increases expenses
                    impact += -expenseDelta; // cash flow impact
                  });
                });

                const nonOp = Number(expensesInputs?.otherAdjustments?.nonOperatingIncome || 0);
                if (Number.isFinite(nonOp) && nonOp !== 0) {
                  impact += -nonOp;
                }

                const legacy = (expensesInputs.normalizingAdjustments || []).filter((adj) => adj?.affectsDCF !== false);
                legacy.forEach((adj) => {
                  const amount = Number(adj?.amount || 0);
                  const treatment = adj?.treatment || (adj?.type === 'nonOperatingIncome' ? 'subtract' : 'add');
                  impact += (treatment === 'add') ? amount : -amount;
                });

                return impact;
              })();
              const adjustmentImpact = backendImpact !== 0 ? backendImpact : localImpact;
              
              // Determine if we should use adjusted values
              const useAdjustedValues = dcfViewMode === 'adjusted' && hasDcfAdjustments;
              
              // Calculate effective tax rate - ALWAYS infer from projections which reflect actual backend calculation
              // We previously tried using dcf.taxRate, but it could be stale from saved scenarios
              const effectiveTaxRate = (() => {
                // Primary: infer from Year 1 projections (these come directly from backend calculation)
                const y1Ebit = totalEbit[1] || 0;
                const y1Tax = totalTax[1] || 0;
                
                if (y1Ebit > 0 && y1Tax > 0) {
                  const inferred = y1Tax / y1Ebit;
                  if (Number.isFinite(inferred) && inferred > 0 && inferred < 1) {
                    return inferred;
                  }
                }
                
                // Fallback: try backend's explicit taxRate
                const firstLine = result?.lines?.[0];
                const dcf = firstLine?.methods?.dcf;
                if (dcf && Number.isFinite(dcf.taxRate) && dcf.taxRate > 0) {
                  return Math.max(0, Math.min(1, dcf.taxRate));
                }
                
                return 0.25; // Default tax rate
              })();

              const tableData = years.map((y, i) => {
                // Recalculate EBIT using adjusted operating expenses from allocations
                const netRev = (
                  (seriesByLine.wealth ? (seriesByLine.wealth.netRevenue[i] || 0) : 0) +
                  (seriesByLine.life ? (seriesByLine.life.netRevenue[i] || 0) : 0) +
                  (seriesByLine.pc ? (seriesByLine.pc.netRevenue[i] || 0) : 0) +
                  (seriesByLine.group ? (seriesByLine.group.netRevenue[i] || 0) : 0)
                );
                const directExp = directSeries[i] || 0;
                const operatingExp = overheadSeries[i] || 0;
                
                // Apply normalizing adjustments to operating expenses if in adjusted mode
                // Adjustments reduce operating expenses (add-backs) or increase them (subtracts)
                let adjustedOperatingExp = operatingExp;
                if (useAdjustedValues && adjustmentImpact !== 0) {
                  // Adjustments impact: positive = add-back (reduces expenses), negative = subtract (increases expenses)
                  // Apply adjustment to operating expenses so it's visible in the table
                  adjustedOperatingExp = operatingExp - adjustmentImpact;
                }
                
                // Calculate EBIT with adjusted operating expenses
                let ebit = netRev - directExp - adjustedOperatingExp;
                
                // Recalculate Tax, NOPAT, FCF based on EBIT
                const tax = ebit > 0 ? ebit * effectiveTaxRate : 0;
                const nopat = ebit - tax;
                const fcf = nopat; // FCF = NOPAT (ignoring DA and WC changes)
                
                return {
                  year: y === 0 ? intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_current_year' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_year_n' }, { n: y }),
                  wealthRevenue: seriesByLine.wealth ? (seriesByLine.wealth.revenue[i] || 0) : 0,
                  lifeRevenue: seriesByLine.life ? (seriesByLine.life.revenue[i] || 0) : 0,
                  pcRevenue: seriesByLine.pc ? (seriesByLine.pc.revenue[i] || 0) : 0,
                  groupRevenue: seriesByLine.group ? (seriesByLine.group.revenue[i] || 0) : 0,
                  netRevenue: netRev,
                  // Unified expenses for display (show adjusted expenses when in adjusted mode)
                  directExpenses: directExp,
                  operatingExpenses: adjustedOperatingExp,
                  // Per-line operating expenses for breakdown (apply same adjustment proportionally)
                  wealthOperatingExpenses: useAdjustedValues && adjustmentImpact !== 0 && operatingExp > 0
                    ? overheadSeriesByLine.wealth[i] - (adjustmentImpact * (overheadSeriesByLine.wealth[i] / operatingExp))
                    : overheadSeriesByLine.wealth[i] || 0,
                  lifeOperatingExpenses: useAdjustedValues && adjustmentImpact !== 0 && operatingExp > 0
                    ? overheadSeriesByLine.life[i] - (adjustmentImpact * (overheadSeriesByLine.life[i] / operatingExp))
                    : overheadSeriesByLine.life[i] || 0,
                  pcOperatingExpenses: useAdjustedValues && adjustmentImpact !== 0 && operatingExp > 0
                    ? overheadSeriesByLine.pc[i] - (adjustmentImpact * (overheadSeriesByLine.pc[i] / operatingExp))
                    : overheadSeriesByLine.pc[i] || 0,
                  groupOperatingExpenses: useAdjustedValues && adjustmentImpact !== 0 && operatingExp > 0
                    ? overheadSeriesByLine.group[i] - (adjustmentImpact * (overheadSeriesByLine.group[i] / operatingExp))
                    : overheadSeriesByLine.group[i] || 0,
                  // Always use recalculated values with adjusted expenses
                  ebit: ebit,
                  tax: tax,
                  nopat: nopat,
                  fcf: fcf,
                  discountFactor: (totalFcf[i] && Math.abs(totalFcf[i]) > 1) ? totalPv[i] / totalFcf[i] : (avgDiscount[i] || 0),
                  discountRate: avgWacc || 0,
                  presentValue: totalPv[i] || 0,
                };
              });
              return (
                <>
                  <div className="rounded-md border border-succession-border overflow-hidden">
                    <DCFProjectionsTable
                      data={tableData}
                      wealthPayoutPercent={lineInputs?.wealth?.faRetentionPercent ?? currentUser?.branch_net_payout}
                      showDirectExpenses={ownersCompEnabledTable}
                      showDiscountFactor={!!(features && features.multi_line_discount_factor)}
                      directExpensesOverride={expensesInputs.directExpenses ?? null}
                      enabledLines={enabledLinesMap}
                    />
                  </div>
                  {(() => {
                    const enabledGrowthLines = enabledLineTabs
                      .filter((tab) => tab.id !== 'expenses')
                      .map((tab) => tab.id);
                    const assumptions = enabledGrowthLines.map((lineId) => ({
                      line: formatLineLabel(lineId),
                      growthRate: Math.round(((seriesByLine[lineId]?.growthRate || 0) * 100) * 10) / 10,
                      growthBasis: seriesByLine[lineId]?.growthBasis || null,
                    }));
                    const dcfSummary = {
                      discountRate: Math.round(((avgWacc || 0) * 100) * 10) / 10,
                      terminalGrowthRate: Math.round(((Object.values(seriesByLine)[0]?.terminalGrowthRate || 0) * 100) * 10) / 10,
                      projectionYears: years.length - 1,
                    };
                    // Prepare breakdown data (per-line rate, weight by Year 1 revenue, contribution)
                    const bd = (() => {
                      const entries = Object.entries(seriesByLine).map(([id, v]) => ({ id, wacc: Number(v.wacc || 0), rev1: Number((v.revenue && v.revenue[1]) || 0) }));
                      const valid = entries.filter((x) => x.rev1 > 0 && x.wacc > 0);
                      const totalRev = valid.reduce((s, it) => s + it.rev1, 0);
                      if (!totalRev) return [];
                      const isRjas = !!(viewerUser && viewerUser.inventory_user && viewerUser.inventory_user.is_rjas);
                      return valid.map((it) => {
                        const comps = seriesByLine[it.id]?.waccComponents || {};
                        const base = Number(comps.base || 0);
                        const companyRisk = Number(comps.companyRisk || 0);
                        const ind = Number(comps.industryPremium || 0);
                        const adders = Number(comps.rjRiskAdders || Math.max(0, it.wacc - (base + companyRisk + ind)));
                        const weight = it.rev1 / totalRev;
                        return {
                          line: it.id,
                          rate: it.wacc,
                          weight,
                          contribution: weight * it.wacc,
                          breakdown: { base, companyRisk, industryPremium: ind, rjRiskAdders: adders },
                          source: {
                            baseField: it.id === 'wealth' ? 'wm_cost_of_equity_mid' : (it.id === 'life' ? 'li_cost_of_equity_mid' : (it.id === 'pc' ? 'pc_cost_of_equity_mid' : 'group_cost_of_equity_mid')),
                            companyRiskField: isRjas ? 'rj_company_specific_risk_secondary' : 'rj_company_specific_risk',
                          },
                        };
                      });
                    })();
                    return (
                      <>
                        {assumptions.length > 0 && (
                          <DCFGrowthAssumptions assumptions={assumptions} className="mt-4" />
                        )}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.6 }}
                          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4"
                        >
                          <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6">
                            <div className="text-sm text-succession-text-secondary mb-2 flex items-center gap-2">
                              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_discount_rate_label' })}
                              <InfoTooltip content="The Weighted Average Cost of Capital (WACC) used to discount future cash flows to present value. A higher discount rate reflects greater risk and reduces the present value of future cash flows." />
                            </div>
                            <div className="text-3xl font-bold text-succession-text-primary mb-1">{dcfSummary.discountRate}%</div>
                            <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_wacc_subtitle' })}{' '}
                              {canViewDiscountRateBreakdown && (
                                <button type="button" className="underline text-succession-primary hover:opacity-90 ml-1" onClick={()=> { setRateBreakdown(bd); setRateModalOpen(true); }}>
                                  {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_how_calculated' })}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6">
                            <div className="text-sm text-succession-text-secondary mb-2 flex items-center gap-2">
                              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_terminal_growth_label' })}
                              <InfoTooltip content="The assumed perpetual growth rate of cash flows beyond the projection period. This rate is used to calculate the terminal value, which represents the business value at the end of the forecast horizon." />
                            </div>
                            <div className="text-3xl font-bold text-succession-text-primary mb-1">{dcfSummary.terminalGrowthRate}%</div>
                            <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_long_term_subtitle' })}</div>
                          </div>
                          <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6">
                            <div className="text-sm text-succession-text-secondary mb-2 flex items-center gap-2">
                              {intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_projection_period_label' })}
                              <InfoTooltip content="The number of years for which detailed cash flow projections are made. After this period, a terminal value is calculated to capture the remaining business value in perpetuity." />
                            </div>
                            <div className="text-3xl font-bold text-succession-text-primary mb-1">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_years' }, { count: dcfSummary.projectionYears })}</div>
                            <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_plus_terminal' })}</div>
                          </div>
                        </motion.div>
                      </>
                    );
                  })()}
                </>
              );
            })()}
          </section>
        )}

      </section>)}

      {/* Graphs Tab */}
      {tab === 'graphs' && hasGraphsFeature && (
        <section className="space-y-6">
          {/* Historical Revenue Charts by Line */}
          <HistoricalRevenueChart
            lineInputs={lineInputs}
            enabledLines={enabledLineTabs.filter(t => t.id !== 'expenses').map(t => t.id)}
          />

          {/* Industry Benchmark Comparison Chart */}
          {hasIndustryBenchmarkFeature && industryBenchmark ? (
            <OperatingExpensesBenchmarkChart
              userExpenses={(() => {
                // When benchmark mode is ON, the DCF uses benchmark expenses, not user's categories
                // So we should show the benchmark total as "Your Practice" (what DCF is actually using)
                if (expensesInputs?.useBenchmark) {
                  const wealthLine = (result?.lines || []).find((l) => l.line === 'wealth');
                  const proj0 = wealthLine?.methods?.dcf?.projections?.[0];
                  const benchmarkOpEx = proj0?.operatingExpenses || proj0?.operating_expenses || 
                                        Number(expensesInputs?.operatingExpenses || 0);
                  // Return as "other" since benchmark mode uses a single total, not categories
                  return benchmarkOpEx > 0 ? [{ key: 'other', label: 'Other Expenses', amount: benchmarkOpEx }] : [];
                }
                
                // Pass full category objects to the chart so it can use labels for mapping
                const cats = Array.isArray(expensesInputs?.operatingExpenseCategories)
                  ? expensesInputs.operatingExpenseCategories
                  : [];
                
                // Check if we have meaningful category data (any non-zero amounts)
                const hasCategoryData = cats.some(c => Number(c?.amount || 0) > 0);
                
                if (hasCategoryData) {
                  // Return full category objects (chart can use labels for mapping)
                  return cats;
                }
                
                // Fallback: If no category breakdown but user has operating expenses, use total
                const wealthLine = (result?.lines || []).find((l) => l.line === 'wealth');
                const proj0 = wealthLine?.methods?.dcf?.projections?.[0];
                const totalOpEx = proj0?.operatingExpenses || proj0?.operating_expenses || 
                                  Number(expensesInputs?.operatingExpenses || 0);
                
                if (totalOpEx > 0) {
                  // Return as array with 'other' label so chart can map it correctly
                  return [{ key: 'other', label: 'Other Expenses', amount: totalOpEx }];
                }
                
                return [];
              })()}
              benchmarkData={industryBenchmark.incomeStatementData || industryBenchmark.income_statement_data || {}}
              branchNetRevenue={(() => {
                const wealthLine = (result?.lines || []).find((l) => l.line === 'wealth');
                const wealthRev = wealthLine?.methods?.dcf?.projections?.[0]?.revenue || 0;
                const payoutLocal = (() => {
                  const n = Number(currentUser?.branch_net_payout);
                  return Number.isFinite(n) ? (n > 1 ? n / 100 : n) : 0;
                })();
                return wealthRev * payoutLocal;
              })()}
              tierName={industryBenchmark.tierName || industryBenchmark.tier_name}
              useBenchmarkMode={!!expensesInputs?.useBenchmark}
            />
          ) : (
            <div className="p-8 text-center text-succession-text-secondary">
              <p className="text-lg font-medium mb-2">{intl.formatMessage({ id: 'succession.multi_line_valuation.no_graphs_available' })}</p>
              <p className="text-sm">
                {!hasIndustryBenchmarkFeature
                  ? intl.formatMessage({ id: 'succession.multi_line_valuation.benchmark_not_enabled' })
                  : intl.formatMessage({ id: 'succession.multi_line_valuation.run_valuation_for_graphs' })}
              </p>
            </div>
          )}
        </section>
      )}

      {/* Valuation Disclaimer */}
      {(() => {
        const company = viewerUser?.company || currentUser?.company;
        const isCanadian = company?.country === 'CA' || company?.country === 'Canada';
        const valuatorKey = isCanadian
          ? 'succession.multi_line_valuation.disclaimer_valuator_ca'
          : 'succession.multi_line_valuation.disclaimer_valuator_us';
        return (
          <div className="mt-12 pt-6 border-t border-succession-border">
            <p className="text-xs text-succession-text-secondary leading-relaxed">
              <strong>{intl.formatMessage({ id: 'succession.multi_line_valuation.disclaimer_label' })}</strong>{' '}
              {intl.formatMessage(
                { id: 'succession.multi_line_valuation.disclaimer_body' },
                { valuator: intl.formatMessage({ id: valuatorKey }) }
              )}
            </p>
          </div>
        );
      })()}

    </SingleColumnTemplate>
    
    {/* Discount Rate Breakdown Modal */}
    <Modal open={rateModalOpen} onClose={()=> setRateModalOpen(false)} className="bg-succession-bg-primary text-succession-text-primary rounded-lg shadow-succession-lg border border-succession-border w-full max-w-5xl animate-modal-scale-in">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_title' })}</h3>
          <button onClick={()=> setRateModalOpen(false)} className="text-succession-text-secondary hover:text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_close' })}</button>
        </div>
        <p className="text-sm text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_desc' })}</p>
        <div className="overflow-x-auto mt-3 rounded border border-succession-border">
          <table className="min-w-full text-sm">
            <thead className="bg-succession-bg-tertiary">
              <tr>
                <th className="px-3 py-2 text-left">{intl.formatMessage({ id: 'succession.multi_line_valuation.table_line' })}</th>
                <th className="px-3 py-2 text-right">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_col_weight' })}</th>
                <th className="px-3 py-2 text-right">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_col_rate' })}</th>
                <th className="px-3 py-2 text-right">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_col_base' })}</th>
                <th className="px-3 py-2 text-right">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_col_company_risk' })}</th>
                <th className="px-3 py-2 text-right">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_col_industry_premium' })}</th>
                <th className="px-3 py-2 text-right">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_col_rj_risk' })}</th>
                <th className="px-3 py-2 text-right">{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_col_contribution' })}</th>
              </tr>
            </thead>
            <tbody>
              {(rateBreakdown || []).map((r)=> (
                <tr key={r.line} className="border-t border-succession-border">
                  <td className="px-3 py-2">{formatLineLabel(r.line)}</td>
                  <td className="px-3 py-2 text-right">{(r.weight * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right">{(r.rate * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right">{((r.breakdown?.base || 0) * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right">{((r.breakdown?.companyRisk || 0) * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right">{((r.breakdown?.industryPremium || 0) * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right">{((r.breakdown?.rjRiskAdders || 0) * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right font-medium">{(r.contribution * 100).toFixed(1)}%</td>
                </tr>
              ))}
              {rateBreakdown && rateBreakdown.length > 0 && (
                <tr className="border-t-2 border-succession-border">
                  <td className="px-3 py-2 font-semibold" colSpan={7}>{intl.formatMessage({ id: 'succession.multi_line_valuation.dcf_modal_blended_rate' })}</td>
                  <td className="px-3 py-2 text-right font-semibold">{(rateBreakdown.reduce((s,x)=> s + x.contribution, 0) * 100).toFixed(1)}%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-xs text-succession-text-secondary">
          <div className="font-semibold mb-1">Sources</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="rounded border border-succession-border p-2">
              <div className="font-medium mb-1">Base field by line</div>
              <ul className="list-disc ml-4 space-y-1">
                <li>Wealth: <code>wm_cost_of_equity_mid</code></li>
                <li>Life: <code>li_cost_of_equity_mid</code></li>
                <li>P&amp;C: <code>pc_cost_of_equity_mid</code></li>
                <li>Group: <code>group_cost_of_equity_mid</code></li>
              </ul>
            </div>
            <div className="rounded border border-succession-border p-2">
              <div className="font-medium mb-1">Company-specific risk</div>
              <ul className="list-disc ml-4 space-y-1">
                <li><code>rj_company_specific_risk</code></li>
                <li>RJAS: <code>rj_company_specific_risk_secondary</code></li>
              </ul>
            </div>
            <div className="rounded border border-succession-border p-2">
              <div className="font-medium mb-1">Industry risk premium (constants)</div>
              <ul className="list-disc ml-4 space-y-1">
                <li>Wealth: 0.0%</li>
                <li>Life: 0.5%</li>
                <li>P&amp;C: 1.0%</li>
                <li>Group: 0.75%</li>
              </ul>
            </div>
            <div className="rounded border border-succession-border p-2">
              <div className="font-medium mb-1">RJ risk adders</div>
              <ul className="list-disc ml-4 space-y-1">
                <li>Client age 65%+ concentration</li>
                <li>Negative NNA</li>
                <li>Low recurring revenue</li>
                <li>Top‑20 concentration</li>
              </ul>
              <div className="mt-1">Shown as the residual after base + company risk + industry premium.</div>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    {/* Floating Inputs button (accessible on all tabs) */}
    <div
      data-tutorial-id="valuation-inputs"
      className="fixed z-50"
      style={{
        transform: 'rotate(-90deg)',
        transformOrigin: 'right center',
        top: 'calc(50% - 100px)',
        backgroundColor: 'var(--succession-primary)',
        borderColor: 'var(--succession-primary)',
        borderRadius: '2px',
        boxShadow: '-2px 2px 0px rgba(0, 0, 0, 0.33)',
        right: drawerOpen ? 'calc(min(100vw, 1024px) + 20px)' : '20px',
        transition: 'right 0.3s ease',
      }}
      onClick={() => {
        if (drawerOpen) {
          if (!readOnly && hasChanges && !window.confirm(intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_unsaved_confirm' }))) return;
          setDrawerOpen(false);
        } else {
          setDrawerTab('wealth');
          setDrawerOpen(true);
        }
      }}
    >
      <button
        type="button"
        className="bg-transparent border-0 text-white p-2.5 text-lg rounded-sm font-light w-40 cursor-pointer"
        style={{
          backgroundColor: 'var(--succession-primary)',
          borderColor: 'var(--succession-primary)',
          color: '#fff',
        }}
        aria-label={drawerOpen ? intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_close_inputs_label' }) : (readOnly ? intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_view_inputs_label' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_edit_inputs_label' }))}
      >
        <span>
          {drawerOpen ? intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_close' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_title' })}
        </span>
      </button>
    </div>

    {/* Valuation Inputs Slide-out Drawer */}
    <Drawer anchor="right" open={drawerOpen} onClose={() => {
      if (!readOnly && hasChanges && !window.confirm(intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_unsaved_confirm' }))) return;
      setDrawerOpen(false);
    }}
      PaperProps={{ style: { width: 'min(100vw, 1024px)', maxWidth: '100%', overflowY: 'auto', scrollBehavior: 'auto', overflowAnchor: 'none' } }}
    >
      <div ref={drawerScrollRef} className="w-full h-full overflow-y-auto p-6 bg-succession-bg-primary text-succession-text-primary" style={{ scrollBehavior: 'auto', overflowAnchor: 'none' }}>
        <div className="space-y-1 pb-4 border-b border-succession-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-semibold">
                {intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_title' })} {readOnly && <span className="text-base font-normal text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_read_only_badge' })}</span>}
              </h2>
              <p className="text-base text-succession-text-secondary">
                {readOnly
                  ? intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_viewing_inputs' })
                  : intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_configure_inputs' })
                }
              </p>
            </div>
            <button
              type="button"
              className="inline-flex items-center rounded border border-succession-border px-2 py-1 text-sm hover:bg-succession-bg-tertiary"
              onClick={() => {
                if (!readOnly && hasChanges && !window.confirm(intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_unsaved_confirm' }))) return;
                setDrawerOpen(false);
              }}
              aria-label={intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_close' })}
            >
              <XIcon className="w-4 h-4 mr-1" /> {intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_close' })}
            </button>
          </div>
        </div>

        {quickbooksConnectorEnabled && (
          <div className={`mt-4 flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border ${quickbooksConnected ? 'border-green-200 bg-green-50' : 'border-succession-border bg-succession-bg-secondary'}`}>
            <div className="flex items-center gap-2">
              <img
                src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
                alt="QuickBooks"
                className="w-5 h-5 object-contain flex-shrink-0"
              />
              <div>
                <p className="text-sm font-medium text-succession-text-primary leading-tight">QuickBooks Online</p>
                <p className="text-xs text-succession-text-muted">
                  {quickbooksConnected ? 'Revenue data imported from QuickBooks' : 'Connect QuickBooks to auto-import revenue data'}
                </p>
              </div>
            </div>
            {quickbooksConnected ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Connected
              </span>
            ) : (
              <a
                href={quickbooksConnectUrl}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium bg-succession-accent text-white hover:opacity-90 transition-opacity flex-shrink-0"
              >
                Connect
              </a>
            )}
          </div>
        )}

        <div className="mt-6">
          <Tabs
            tabs={enabledLineTabs}
            value={drawerTab}
            onChange={setDrawerTab}
          />
        <div className="mt-4">
          <AnimatePresence mode="wait">
            {drawerTab === 'wealth' && (
              <motion.section
                key="wealth"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <WealthInputsForm
                  values={(drawerTab === 'wealth' ? ((lineInputs.wealth && Object.keys(lineInputs.wealth).length > 0) ? lineInputs.wealth : (initialInputs.wealth || {})) : (initialInputs.wealth || {}))}
                  terminology={terminology}
                  showAum={wealthExtraFields.showAum}
                  showClientCount={wealthExtraFields.showClientCount}
                  t12Note={t12YearEndNote}
                  onChange={() => setHasChanges(true)}
                  onFieldChange={(k, v) => {
                    setHasChanges(true);
                    const setByPath = (obj, path, val) => {
                      const parts = path.split('.');
                      const head = parts[0];
                      const tail = parts.slice(1);
                      if (tail.length === 0) { obj[head] = val; return obj; }
                      const next = { ...(obj[head] || {}) };
                      obj[head] = setByPath(next, tail.join('.'), val);
                      return obj;
                    };
                    if (drawerTab === 'wealth') {
                      rememberDrawerScroll();
                      setLineInputs((prev) => {
                        const current = { ...(prev.wealth || {}) };
                        const next = setByPath(current, k, v);
                        const keyLower = String(k || '').toLowerCase();
                        if (!allowManualGrowth && keyLower.startsWith('history.')) {
                          const h = next.history || {};
                          const arr = [h.y1, h.y2, h.y3, h.y4, h.y5]
                            .map((x) => Number(x || 0))
                            .filter((x) => x);
                          if (arr.length >= 2) {
                            const growths = [];
                            for (let i = 0; i < arr.length - 1; i++) {
                              const c = arr[i];
                              const n2 = arr[i + 1];
                              if (c) growths.push((c - n2) / c);
                            }
                            if (growths.length) {
                              // Divide by arr.length (# of data points) to match backend rj_gross_revenue_detail.rb
                              next.growth = growths.reduce((s, x) => s + x, 0) / arr.length;
                            }
                          }
                        }
                        return { ...prev, wealth: next };
                      });
                    }
                  }}
                />
              </motion.section>
            )}
            {drawerTab === 'life' && (
              <motion.section
                key="life"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <LifeInputsForm
                  values={(drawerTab === 'life' ? ((lineInputs.life && Object.keys(lineInputs.life).length > 0) ? lineInputs.life : (initialInputs.life || {})) : (initialInputs.life || {}))}
                  terminology={terminology}
                  onChange={() => setHasChanges(true)}
                  onFieldChange={(k, v) => {
                    setHasChanges(true);
                    const setByPath = (obj, path, val) => {
                      const parts = path.split('.');
                      const head = parts[0];
                      const tail = parts.slice(1);
                      if (tail.length === 0) { obj[head] = val; return obj; }
                      const next = { ...(obj[head] || {}) };
                      obj[head] = setByPath(next, tail.join('.'), val);
                      return obj;
                    };
                    if (drawerTab === 'life') {
                      setLineInputs((prev) => {
                        const current = { ...(prev.life || {}) };
                        const next = setByPath(current, k, v);
                        const keyLower = String(k || '').toLowerCase();
                        if (!allowManualGrowth && keyLower.startsWith('history.')) {
                          const h = next.history || {};
                          const arr = [h.y1, h.y2, h.y3, h.y4, h.y5]
                            .map((x) => Number(x || 0))
                            .filter((x) => x);
                          if (arr.length >= 2) {
                            const growths = [];
                            for (let i = 0; i < arr.length - 1; i++) {
                              const c = arr[i];
                              const n2 = arr[i + 1];
                              if (c) growths.push((c - n2) / c);
                            }
                            if (growths.length) {
                              // Divide by arr.length (# of data points) to match backend rj_gross_revenue_detail.rb
                              next.growth = growths.reduce((s, x) => s + x, 0) / arr.length;
                            }
                          }
                        }
                        return { ...prev, life: next };
                      });
                    }
                  }}
                />
              </motion.section>
            )}
            {drawerTab === 'pc' && (
              <motion.section
                key="pc"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <PCInputsForm
                  values={(drawerTab === 'pc' ? ((lineInputs.pc && Object.keys(lineInputs.pc).length > 0) ? lineInputs.pc : (initialInputs.pc || {})) : (initialInputs.pc || {}))}
                  terminology={terminology}
                  onChange={() => setHasChanges(true)}
                  onFieldChange={(k, v) => {
                    setHasChanges(true);
                    const setByPath = (obj, path, val) => {
                      const parts = path.split('.');
                      const head = parts[0];
                      const tail = parts.slice(1);
                      if (tail.length === 0) { obj[head] = val; return obj; }
                      const next = { ...(obj[head] || {}) };
                      obj[head] = setByPath(next, tail.join('.'), val);
                      return obj;
                    };
                    if (drawerTab === 'pc') {
                      setLineInputs((prev) => {
                        const current = { ...(prev.pc || {}) };
                        const next = setByPath(current, k, v);
                        const keyLower = String(k || '').toLowerCase();
                        if (!allowManualGrowth && keyLower.startsWith('history.')) {
                          const h = next.history || {};
                          const arr = [h.y1, h.y2, h.y3, h.y4, h.y5]
                            .map((x) => Number(x || 0))
                            .filter((x) => x);
                          if (arr.length >= 2) {
                            const growths = [];
                            for (let i = 0; i < arr.length - 1; i++) {
                              const c = arr[i];
                              const n2 = arr[i + 1];
                              if (c) growths.push((c - n2) / c);
                            }
                            if (growths.length) {
                              // Divide by arr.length (# of data points) to match backend rj_gross_revenue_detail.rb
                              next.growth = growths.reduce((s, x) => s + x, 0) / arr.length;
                            }
                          }
                        }
                        return { ...prev, pc: next };
                      });
                    }
                  }}
                />
              </motion.section>
            )}
            {drawerTab === 'group' && (
              <motion.section
                key="group"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <GroupInputsForm
                  values={(drawerTab === 'group' ? ((lineInputs.group && Object.keys(lineInputs.group).length > 0) ? lineInputs.group : (initialInputs.group || {})) : (initialInputs.group || {}))}
                  terminology={terminology}
                  onChange={() => setHasChanges(true)}
                  onFieldChange={(k, v) => {
                    setHasChanges(true);
                    const setByPath = (obj, path, val) => {
                      const parts = path.split('.');
                      const head = parts[0];
                      const tail = parts.slice(1);
                      if (tail.length === 0) { obj[head] = val; return obj; }
                      const next = { ...(obj[head] || {}) };
                      obj[head] = setByPath(next, tail.join('.'), val);
                      return obj;
                    };
                    if (drawerTab === 'group') {
                      setLineInputs((prev) => {
                        const current = { ...(prev.group || {}) };
                        const next = setByPath(current, k, v);
                        const keyLower = String(k || '').toLowerCase();
                        if (!allowManualGrowth && keyLower.startsWith('history.')) {
                          const h = next.history || {};
                          const arr = [h.y1, h.y2, h.y3, h.y4, h.y5]
                            .map((x) => Number(x || 0))
                            .filter((x) => x);
                          if (arr.length >= 2) {
                            const growths = [];
                            for (let i = 0; i < arr.length - 1; i++) {
                              const c = arr[i];
                              const n2 = arr[i + 1];
                              if (c) growths.push((c - n2) / c);
                            }
                            if (growths.length) {
                              // Divide by arr.length (# of data points) to match backend rj_gross_revenue_detail.rb
                              next.growth = growths.reduce((s, x) => s + x, 0) / arr.length;
                            }
                          }
                        }
                        return { ...prev, group: next };
                      });
                    }
                  }}
                />
              </motion.section>
            )}
            {drawerTab === 'expenses' && (
              <motion.section
                key="expenses"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <ExpensesForm
                  values={expensesInputs}
                  onChange={() => setHasChanges(true)}
                  onFieldChange={(k, v) => {
                    setExpensesField(k, v);
                  }}
                  result={result}
                  currentUser={currentUser}
                  viewerUser={viewerUser}
                  terminology={terminology}
                />
              </motion.section>
            )}
          </AnimatePresence>
        </div>
        </div>

        <div className="sticky bottom-0 left-0 right-0 bg-succession-bg-primary border-t border-succession-border pt-4 mt-6 flex items-center justify-between">
          <button
            type="button"
            className="px-3 py-2 rounded border border-succession-border text-succession-text-primary hover:bg-succession-bg-tertiary inline-flex items-center"
            onClick={() => {
              if (!readOnly && hasChanges && !window.confirm(intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_discard_confirm' }))) return;
              setDrawerOpen(false);
            }}
          >
            <XIcon className="w-4 h-4 mr-2" />
            {readOnly ? intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_close' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_cancel' })}
          </button>
          {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`px-3 py-2 rounded text-white inline-flex items-center ${hasChanges ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-succession-text-secondary cursor-not-allowed'}`}
              onClick={() => {
                // Recalculate should preserve current drawer inputs; do not hydrate from incoming initialInputs
                hydrateFromInitialRef.current = false;
                onRecalculate && onRecalculate(buildFullPayload());
                setHasChanges(false);
                setDrawerOpen(false);
              }}
              disabled={!hasChanges}
            >
              <SaveIcon className="w-4 h-4 mr-2" />
              {intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_save_changes' })}
            </button>
            <button
              type="button"
              className={`px-3 py-2 rounded text-white inline-flex items-center ${hasChanges ? 'bg-succession-primary hover:opacity-90' : 'bg-succession-text-secondary cursor-not-allowed'}`}
              onClick={() => {
                // Only recalculate - don't auto-save scenarios (use top "Save" button for that)
                hydrateFromInitialRef.current = false;
                onRecalculate && onRecalculate(buildFullPayload());
                setHasChanges(false);
                setDrawerOpen(false);
              }}
              disabled={!hasChanges}
            >
              <RefreshCwIcon className="w-4 h-4 mr-2" />
              {intl.formatMessage({ id: 'succession.multi_line_valuation.drawer_recalculate' })}
            </button>
          </div>
          )}
        </div>
      </div>
    </Drawer>

    {/* Chat Window - only shown if feature is enabled and chat is open */}
    {hasChatFeature && showChat && (
      <MultiLineValuationChatWindow
        user={currentUser}
        clientState={chatClientState}
        onStateUpdate={handleChatStateUpdate}
        onClose={() => setShowChat(false)}
        conversationId={chatConversationId}
        onConversationIdChange={setChatConversationId}
      />
    )}
    </>
  );
};

// Valuation Empty State - shown when user hasn't accessed valuation yet
const ValuationEmptyState = ({ onSetupProfile, isReadOnly = false, companyName = 'your firm', className = '', quickbooksConnectorEnabled = false, quickbooksConnected = false, quickbooksConnectUrl = null }) => {
  const intl = useIntl();
  const { code: _emptyCurrencyCode } = useCurrency();
  const CurrencyIcon = _emptyCurrencyCode === 'GBP' ? PoundSterling : DollarSign;
  const features = [
    {
      icon: CurrencyIcon,
      title: intl.formatMessage({ id: 'succession.multi_line_valuation.empty_feature_multi_method_title' }),
      description: intl.formatMessage({ id: 'succession.multi_line_valuation.empty_feature_multi_method_desc' }),
    },
    {
      icon: TrendingUpIcon,
      title: intl.formatMessage({ id: 'succession.multi_line_valuation.empty_feature_business_line_title' }),
      description: intl.formatMessage({ id: 'succession.multi_line_valuation.empty_feature_business_line_desc' }),
    },
    {
      icon: BarChart3,
      title: intl.formatMessage({ id: 'succession.multi_line_valuation.empty_feature_confidence_title' }),
      description: intl.formatMessage({ id: 'succession.multi_line_valuation.empty_feature_confidence_desc' }),
    },
  ];

  return (
    <div className={`flex flex-col items-center py-6 px-4 ${className}`}>
      {/* Main bordered card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-4xl rounded-2xl border border-succession-border bg-succession-bg-primary p-8 text-center shadow-succession transition-colors duration-300 md:p-10"
      >
        {/* Icon */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full border border-succession-border bg-succession-primary/10"
        >
          <BarChart3 className="h-10 w-10 text-succession-primary" />
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-2xl md:text-3xl font-bold mb-4 text-succession-text-primary"
        >
          {intl.formatMessage({ id: 'succession.multi_line_valuation.empty_title' })}
        </motion.h2>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mb-6 text-lg leading-relaxed text-succession-text-secondary"
        >
          {isReadOnly
            ? intl.formatMessage({ id: 'succession.multi_line_valuation.empty_readonly_desc' }, { companyName })
            : intl.formatMessage({ id: 'succession.multi_line_valuation.empty_desc' })}
        </motion.p>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mb-8 grid grid-cols-1 gap-4 text-left md:grid-cols-3"
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className="flex items-start gap-3 rounded-xl border border-succession-border bg-succession-bg-secondary p-4 transition-colors duration-300"
            >
              <feature.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-succession-primary" />
              <div>
                <p className="font-medium text-succession-text-primary text-sm">{feature.title}</p>
                <p className="text-xs text-succession-text-secondary">{feature.description}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <button
            onClick={onSetupProfile}
            className="inline-flex items-center gap-2 rounded-lg bg-succession-primary px-8 py-3 font-medium text-white shadow-succession transition-colors duration-300 hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-succession-primary focus:ring-offset-2"
          >
            <CurrencyIcon className="w-5 h-5" />
            {isReadOnly ? intl.formatMessage({ id: 'succession.multi_line_valuation.empty_cta_readonly' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.empty_cta' })}
            <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>

          <p className="text-sm text-succession-text-secondary mt-4">
            {isReadOnly
              ? intl.formatMessage({ id: 'succession.multi_line_valuation.empty_managed_by' }, { companyName })
              : intl.formatMessage({ id: 'succession.multi_line_valuation.empty_time_hint' })}
          </p>

          {quickbooksConnectorEnabled && !isReadOnly && (
            <div className="mt-5 pt-5 border-t border-succession-border/50">
              {quickbooksConnected ? (
                <div className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm">
                  <img
                    src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
                    alt="QuickBooks"
                    className="w-4 h-4 flex-shrink-0 object-contain"
                  />
                  <span className="font-medium text-green-700">QuickBooks Connected</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                </div>
              ) : (
                <>
                  <p className="text-sm text-succession-text-muted mb-3">Or import automatically</p>
                  <a
                    href={quickbooksConnectUrl}
                    className="inline-flex items-center gap-2 rounded-lg border border-succession-border px-5 py-2.5 text-sm font-medium text-succession-text-primary hover:bg-succession-bg-secondary transition-colors"
                  >
                    <img
                      src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
                      alt="QuickBooks"
                      className="w-5 h-5 flex-shrink-0 rounded object-contain"
                    />
                    Connect QuickBooks
                  </a>
                </>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Info Banner - Outside the bordered card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.5 }}
        className="mt-6 w-full max-w-4xl rounded-xl border border-succession-border bg-succession-bg-primary p-4 shadow-succession transition-colors duration-300"
      >
        <div className="flex items-start gap-3">
          <InfoIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-succession-info" />
          <div className="text-sm">
            <p className="font-medium text-succession-text-primary mb-1">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.empty_why_title' })}
            </p>
            <p className="text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.multi_line_valuation.empty_why_desc' })}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Calculating Valuation Interstitial - shown while processing
const ValuationCalculatingInterstitial = ({ onComplete }) => {
  const intl = useIntl();
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onComplete?.();
    }, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-succession-bg-primary">
      <div className="text-center space-y-8 max-w-2xl px-8">
        <style>{`@keyframes fb-spin{to{transform:rotate(360deg)}}`}</style>
        <div className="w-24 h-24 bg-succession-primary rounded-full flex items-center justify-center mx-auto">
          <div className="w-12 h-12" style={{ animation: 'fb-spin 1s linear infinite' }} aria-hidden="true">
            <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z" />
            </svg>
          </div>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.multi_line_valuation.calculating_title' })}</h1>
        <p className="text-xl text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.calculating_desc' })}</p>
        <div className="flex justify-center space-x-2">
          <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
};

// RevenueInformationModal - wrapper for the profile edit modal with "Show Valuation" CTA
import RevenueInformationEditModal from './ProfileEdit/modals/RevenueInformationEditModal';

const RevenueInformationModal = ({ user, onClose, onShowValuation, onError, isReadOnly = false, companyName = 'your firm', quickbooksConnected = false }) => {
  const intl = useIntl();
  const handleSave = (message) => {
    // After save completes, trigger the show valuation flow
    onShowValuation?.();
  };

  return (
    <RevenueInformationEditModal
      user={user}
      onClose={onClose}
      onSave={handleSave}
      onError={onError}
      title={isReadOnly ? intl.formatMessage({ id: 'succession.multi_line_valuation.revenue_info_modal_title_readonly' }) : intl.formatMessage({ id: 'succession.multi_line_valuation.revenue_info_modal_title' })}
      submitButtonText={intl.formatMessage({ id: 'succession.multi_line_valuation.show_valuation' })}
      submitButtonIcon={<BarChart3 className="w-4 h-4" />}
      isReadOnly={isReadOnly}
      companyName={companyName}
      quickbooksConnected={quickbooksConnected}
    />
  );
};

export default MultiLineValuation;
export { ValuationEmptyState, ValuationCalculatingInterstitial, RevenueInformationModal };

